import React, { useState, useEffect, useCallback, Component } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

/* ─── Color maps ─────────────────────────────────────────────────────────── */
const ACCT_STATUS = {
  pending:      { label: "Pending",      color: "#B45309", bg: "rgba(180,83,9,0.1)",    border: "rgba(180,83,9,0.25)"   },
  under_review: { label: "Under Review", color: "#1D4ED8", bg: "rgba(29,78,216,0.1)",   border: "rgba(29,78,216,0.25)"  },
  approved:     { label: "Approved",     color: "#16A34A", bg: "rgba(22,163,74,0.1)",   border: "rgba(22,163,74,0.25)"  },
  paused:       { label: "Paused",       color: "#B45309", bg: "rgba(180,83,9,0.1)",    border: "rgba(180,83,9,0.25)"   },
  suspended:    { label: "Suspended",    color: "#DC2626", bg: "rgba(220,38,38,0.1)",   border: "rgba(220,38,38,0.25)"  },
  rejected:     { label: "Rejected",     color: "#DC2626", bg: "rgba(220,38,38,0.1)",   border: "rgba(220,38,38,0.25)"  },
  offboarded:   { label: "Offboarded",   color: "#6B7280", bg: "rgba(107,114,128,0.1)", border: "rgba(107,114,128,0.2)" },
  live:         { label: "Live",         color: "#16A34A", bg: "rgba(22,163,74,0.12)",  border: "rgba(22,163,74,0.3)"   },
};
const VERIFY_STATUS = {
  pending:  { label: "Pending",  color: "#5A90A8", bg: "rgba(90,144,168,0.08)", border: "rgba(90,144,168,0.2)"   },
  verified: { label: "Verified", color: "#16A34A", bg: "rgba(22,163,74,0.1)",   border: "rgba(22,163,74,0.25)"   },
  rejected: { label: "Rejected", color: "#DC2626", bg: "rgba(220,38,38,0.1)",   border: "rgba(220,38,38,0.25)"   },
  expired:  { label: "Expired",  color: "#B45309", bg: "rgba(180,83,9,0.08)",   border: "rgba(180,83,9,0.2)"     },
};
const ROLE_COLORS = {
  admin:   { color: "#7C3AED", bg: "rgba(124,58,237,0.1)",  border: "rgba(124,58,237,0.25)" },
  doctor:  { color: "#1D4ED8", bg: "rgba(29,78,216,0.1)",   border: "rgba(29,78,216,0.25)"  },
  patient: { color: "#16A34A", bg: "rgba(22,163,74,0.1)",   border: "rgba(22,163,74,0.25)"  },
};

/* ─── Shared styles ──────────────────────────────────────────────────────── */
const card = {
  background: "#FFFFFF",
  border: "1px solid rgba(90,144,168,0.18)", borderRadius: 16,
  boxShadow: "0 2px 16px rgba(90,144,168,0.1)",
};
const sectionHeading = {
  fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
  color: "#5A90A8", fontFamily: "Inter, sans-serif", marginBottom: 16,
  paddingBottom: 12, borderBottom: "1px solid rgba(90,144,168,0.15)",
};
const labelTxt  = { fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" };
const valueTxt  = { fontSize: 13, color: "#1C3545", fontFamily: "Inter, sans-serif" };
const inputBase = {
  padding: "9px 12px", border: "1px solid rgba(90,144,168,0.25)", borderRadius: 8,
  fontSize: 13, fontFamily: "Inter, sans-serif", color: "#1C3545",
  background: "#F5FAFC", outline: "none", boxSizing: "border-box",
};

/* ─── Shared UI atoms ────────────────────────────────────────────────────── */
function Badge({ map, value }) {
  const s = map[value] || { label: value, color: "rgba(255,255,255,0.5)", bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.1)" };
  return (
    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", color: s.color, background: s.bg, border: `1px solid ${s.border}`, whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
}

function Btn({ label, color = "#5A90A8", bg = "rgba(90,144,168,0.1)", border = "rgba(90,144,168,0.3)", onClick, disabled, small }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      style={{ padding: small ? "4px 12px" : "7px 16px", borderRadius: 7, border: `1px solid ${disabled ? "rgba(90,144,168,0.15)" : border}`, background: disabled ? "rgba(90,144,168,0.04)" : bg, color: disabled ? "#A8C8D8" : color, fontSize: small ? 11 : 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: disabled ? "not-allowed" : "pointer", transition: "all 150ms", whiteSpace: "nowrap" }}>
      {label}
    </button>
  );
}

function FieldRow({ label, val }) {
  if (val === null || val === undefined || val === "") return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: "7px 0", borderBottom: "1px solid rgba(90,144,168,0.08)" }}>
      <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", flexShrink: 0, minWidth: 120 }}>{label}</span>
      <span style={{ fontSize: 12, color: "#1C3545", fontFamily: "Inter, sans-serif", textAlign: "right" }}>{val}</span>
    </div>
  );
}

function InfoBlock({ label, text, mb = 0 }) {
  return (
    <div style={{ background: "rgba(90,144,168,0.04)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 12, padding: "14px 18px", marginBottom: mb }}>
      <p style={{ margin: "0 0 6px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>{label}</p>
      <p style={{ margin: 0, fontSize: 13, color: "#2D5A72", fontFamily: "Inter, sans-serif", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{text}</p>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 48 }}>
      <div style={{ width: 32, height: 32, border: "2px solid rgba(90,144,168,0.2)", borderTopColor: "#8AAEBB", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function RejectModal({ open, title = "Reject", subtitle = "Please provide a reason for rejection.", onConfirm, onCancel }) {
  const [reason, setReason] = useState("");
  if (!open) return null;
  const handleConfirm = () => { onConfirm(reason); setReason(""); };
  const handleCancel  = () => { setReason(""); onCancel(); };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={handleCancel}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 440, maxWidth: "92vw", boxShadow: "0 12px 48px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column", gap: 14 }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{title}</p>
            <p style={{ margin: "3px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{subtitle}</p>
          </div>
        </div>
        <textarea
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          placeholder="Enter rejection reason…"
          style={{ ...inputBase, width: "100%", resize: "vertical", boxSizing: "border-box" }}
        />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn label="Cancel" color="#9BBCCC" bg="rgba(90,144,168,0.06)" border="rgba(90,144,168,0.2)" onClick={handleCancel} />
          <Btn label="Reject" color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)" onClick={handleConfirm} />
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ open, title, subtitle, body, confirmLabel = "Confirm", confirmColor = "#5A90A8", confirmBg = "rgba(90,144,168,0.1)", confirmBorder = "rgba(90,144,168,0.25)", onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onCancel}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 440, maxWidth: "92vw", boxShadow: "0 12px 48px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column", gap: 16 }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `${confirmBg}`, border: `1px solid ${confirmBorder}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke={confirmColor} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
              <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z" /><path d="M12 8v4" /><path d="M12 16h.01" />
            </svg>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{title}</p>
            {subtitle && <p style={{ margin: "3px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{subtitle}</p>}
          </div>
        </div>
        {body && (
          <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(90,144,168,0.05)", border: "1px solid rgba(90,144,168,0.12)", fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>
            {body}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn label="Cancel" color="#9BBCCC" bg="rgba(90,144,168,0.06)" border="rgba(90,144,168,0.2)" onClick={onCancel} />
          <Btn label={confirmLabel} color={confirmColor} bg={confirmBg} border={confirmBorder} onClick={onConfirm} />
        </div>
      </div>
    </div>
  );
}

/* Inject once — fixes native <option> rendering in light theme + mobile layout */
const ADMIN_SELECT_STYLE = `
  .admin-dash select option {
    background: #F5FAFC;
    color: #1C3545;
  }
  .admin-dash select option:checked {
    background: #EDF4F7;
    color: #5A90A8;
  }
  /* Mobile: show hamburger, hide sidebar by default */
  @media (max-width: 768px) {
    .admin-hamburger {
      display: flex !important;
    }
    .admin-sidebar {
      position: fixed !important;
      top: 54px !important;
      left: 0 !important;
      bottom: 0 !important;
      width: 210px !important;
      z-index: 300 !important;
      transform: translateX(-100%) !important;
      transition: transform 250ms ease !important;
      box-shadow: 4px 0 24px rgba(0,0,0,0.18) !important;
      pointer-events: auto !important;
    }
    .admin-sidebar--open {
      transform: translateX(0) !important;
    }
    .admin-dash header {
      padding: 0 12px 0 14px !important;
    }
    .admin-dash header span:first-child {
      font-size: 15px !important;
    }
  }
  /* Table scroll on mobile */
  @media (max-width: 900px) {
    .admin-dash .admin-table-wrap {
      overflow-x: auto !important;
      -webkit-overflow-scrolling: touch !important;
    }
    .admin-dash .admin-table-wrap table {
      min-width: 520px !important;
    }
    .admin-dash main {
      padding-left: 0 !important;
    }
  }

  /* ClustersSection: responsive form grids */
  @media (max-width: 768px) {
    .admin-dash .bio-form-row {
      grid-template-columns: 1fr 1fr !important;
    }
    .admin-dash .cluster-edit-grid {
      grid-template-columns: 1fr 1fr !important;
    }
    /* Cluster info grid in Doctor Detail Panel */
    .admin-dash .cluster-info-grid {
      grid-template-columns: 1fr 1fr !important;
    }
  }
  @media (max-width: 560px) {
    .admin-dash .bio-form-row {
      grid-template-columns: 1fr !important;
    }
    .admin-dash .cluster-edit-grid {
      grid-template-columns: 1fr !important;
    }
    .admin-dash .cluster-info-grid {
      grid-template-columns: 1fr !important;
    }
    /* Section padding adjustments */
    .admin-dash .cluster-card-header {
      flex-direction: column !important;
      align-items: flex-start !important;
    }
  }
  @media (max-width: 480px) {
    .admin-dash header {
      padding: 0 10px !important;
    }
    .admin-user-name {
      display: none !important;
    }
  }
  /* BiomarkersSection library form grid */
  @media (max-width: 900px) {
    .admin-dash .bio-lib-form-row {
      grid-template-columns: 1fr 1fr 1fr !important;
    }
  }
  @media (max-width: 640px) {
    .admin-dash .bio-lib-form-row {
      grid-template-columns: 1fr 1fr !important;
    }
  }
  @media (max-width: 420px) {
    .admin-dash .bio-lib-form-row {
      grid-template-columns: 1fr !important;
    }
  }
  /* Alternate Units table — more columns than the standard admin table, needs a wider scroll floor */
  @media (max-width: 900px) {
    .admin-dash .admin-table-wrap .alt-units-table {
      min-width: 980px !important;
    }
  }
  /* Aliases table — has an Actions column with two buttons, needs a bit more room than the default */
  @media (max-width: 900px) {
    .admin-dash .admin-table-wrap .aliases-table {
      min-width: 720px !important;
    }
  }
  /* Alternate Units add/edit form grid */
  @media (max-width: 900px) {
    .admin-dash .alt-unit-form-row {
      grid-template-columns: 1fr 1fr !important;
    }
  }
  @media (max-width: 560px) {
    .admin-dash .alt-unit-form-row {
      grid-template-columns: 1fr !important;
    }
  }
  /* Sidebar overlay — covers content area ONLY (right of sidebar) */
  .admin-sidebar-overlay {
    display: none;
  }
  @media (max-width: 768px) {
    .admin-sidebar-overlay {
      display: block;
      position: fixed;
      top: 54px;
      left: 210px;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.45);
      z-index: 200;
      cursor: pointer;
    }
  }
`;

/* ── Tag-style multi-value input for string[] biomarker fields ── */
function BioTagInput({ tags, onChange, placeholder, suggestions = [], restricted = false }) {
  const [input, setInput] = React.useState("");
  const add = (val) => {
    const v = val.trim();
    if (!v) return;
    if (restricted && !suggestions.includes(v)) return;
    if (!tags.includes(v)) onChange([...tags, v]);
    setInput("");
  };
  const remove = (tag) => onChange(tags.filter((t) => t !== tag));
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(input); }
    if (e.key === "Backspace" && !input && tags.length > 0) onChange(tags.slice(0, -1));
  };
  return (
    <div>
      {tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {tags.map((tag) => (
            <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px 3px 10px", borderRadius: 999, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.3)", color: "#5A90A8", fontSize: 12, fontFamily: "Inter, sans-serif" }}>
              {tag}
              <button type="button" onClick={() => remove(tag)} style={{ background: "none", border: "none", cursor: "pointer", color: "#7AAABB", padding: 0, lineHeight: 1, fontSize: 13 }}>✕</button>
            </span>
          ))}
        </div>
      )}
      {!restricted && (
        <input
          type="text" value={input} placeholder={tags.length ? "Add another…" : placeholder}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => { if (input.trim()) add(input); }}
          style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid rgba(90,144,168,0.25)", background: "#FAFCFD", fontSize: 12, fontFamily: "Inter, sans-serif", color: "#1C3545", outline: "none", boxSizing: "border-box" }}
        />
      )}
      {suggestions.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: restricted ? 0 : 6 }}>
          {suggestions.filter((s) => !tags.includes(s)).map((s) => (
            <button key={s} type="button" onClick={() => add(s)}
              style={{ padding: "3px 9px", borderRadius: 999, border: "1px solid rgba(90,144,168,0.2)", background: "transparent", color: "#7AAABB", fontSize: 11, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoricalMapEditor({ rows, onChange }) {
  const update = (idx, field, value) => {
    const next = rows.map((r, i) => i === idx ? { ...r, [field]: value } : r);
    onChange(next);
  };
  const addRow = () => onChange([...rows, { label: "", score: "" }]);
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));
  return (
    <div>
      {rows.length > 0 && (
        <div style={{ display: "grid", gap: 6, marginBottom: 8 }}>
          {rows.map((row, idx) => (
            <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 120px 28px", gap: 6, alignItems: "center" }}>
              <input
                value={row.label} placeholder="Category label (e.g. Normal)"
                onChange={(e) => update(idx, "label", e.target.value)}
                style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid rgba(90,144,168,0.25)", background: "#FAFCFD", fontSize: 12, fontFamily: "Inter, sans-serif", color: "#1C3545", outline: "none" }}
              />
              <input
                type="number" step="any" value={row.score} placeholder="Score"
                onChange={(e) => update(idx, "score", e.target.value)}
                style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid rgba(90,144,168,0.25)", background: "#FAFCFD", fontSize: 12, fontFamily: "Inter, sans-serif", color: "#1C3545", outline: "none", textAlign: "right" }}
              />
              <button type="button" onClick={() => removeRow(idx)}
                style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid rgba(220,38,38,0.25)", background: "rgba(220,38,38,0.06)", color: "#DC2626", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <button type="button" onClick={addRow}
        style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, border: "1px dashed rgba(90,144,168,0.35)", background: "transparent", color: "#5A90A8", fontSize: 12, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
        + Add Category
      </button>
    </div>
  );
}

/* Parse score_bands from the DB (JSON string) back to an array of band objects.
   Falls back gracefully if the stored value is the old newline-separated format. */
const parseBands = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    // old format: "80–100 Optimal\n60–79 Adequate" → migrate on load
    return raw.split("\n").filter(Boolean).map((s) => ({ label: s, min: "", max: "" }));
  }
  return [];
};

function ScoreBandEditor({ bands, onChange }) {
  const update = (idx, field, val) =>
    onChange(bands.map((b, i) => (i === idx ? { ...b, [field]: val } : b)));
  const addBand  = () => onChange([...bands, { label: "", min: "", max: "" }]);
  const removeBand = (idx) => onChange(bands.filter((_, i) => i !== idx));

  const cell = {
    padding: "6px 10px", borderRadius: 7,
    border: "1px solid rgba(90,144,168,0.25)", background: "#FAFCFD",
    fontSize: 12, fontFamily: "Inter, sans-serif", color: "#1C3545", outline: "none",
  };

  return (
    <div>
      {bands.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {/* column headers */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 100px 28px", gap: 6, marginBottom: 4 }}>
            {["Label", "Min", "Max", ""].map((h, i) => (
              <p key={i} style={{ margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{h}</p>
            ))}
          </div>
          {bands.map((b, idx) => (
            <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 100px 100px 28px", gap: 6, marginBottom: 6, alignItems: "center" }}>
              <input value={b.label} placeholder="e.g. Optimal" onChange={(e) => update(idx, "label", e.target.value)} style={{ ...cell, width: "100%" }} />
              <input type="number" step="any" value={b.min ?? ""} placeholder="Min" onChange={(e) => update(idx, "min", e.target.value)} style={{ ...cell, width: "100%", textAlign: "right" }} />
              <input type="number" step="any" value={b.max ?? ""} placeholder="Max" onChange={(e) => update(idx, "max", e.target.value)} style={{ ...cell, width: "100%", textAlign: "right" }} />
              <button type="button" onClick={() => removeBand(idx)}
                style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid rgba(220,38,38,0.25)", background: "rgba(220,38,38,0.06)", color: "#DC2626", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <button type="button" onClick={addBand}
        style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, border: "1px dashed rgba(90,144,168,0.35)", background: "transparent", color: "#5A90A8", fontSize: 12, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
        + Add Band
      </button>
    </div>
  );
}

function ErrBanner({ msg, onDismiss }) {
  if (!msg) return null;
  return (
    <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 13, fontFamily: "Inter, sans-serif", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span>{msg}</span>
      {onDismiss && <button onClick={onDismiss} style={{ background: "none", border: "none", color: "#DC2626", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0, marginLeft: 12 }}>×</button>}
    </div>
  );
}

function StatCard({ label, value, sub, color = "#5A90A8" }) {
  return (
    <div style={{ ...card, padding: "20px 22px", flex: "1 1 140px" }}>
      <p style={{ margin: "0 0 4px", fontSize: 28, fontWeight: 700, color, fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{value ?? "—"}</p>
      <p style={{ margin: "0 0 2px", fontSize: 12, fontWeight: 600, color: "#4D7A8E", fontFamily: "Inter, sans-serif" }}>{label}</p>
      {sub && <p style={{ margin: 0, fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{sub}</p>}
    </div>
  );
}

function TableRow({ children, onClick, selected }) {
  return (
    <tr onClick={onClick}
      style={{ borderBottom: "1px solid rgba(90,144,168,0.1)", background: selected ? "rgba(90,144,168,0.06)" : "transparent", cursor: onClick ? "pointer" : "default", transition: "background 120ms" }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = "rgba(90,144,168,0.04)"; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "transparent"; }}
    >{children}</tr>
  );
}
const Th = ({ children, w }) => <th style={{ padding: "10px 14px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif", textAlign: "left", width: w, whiteSpace: "nowrap" }}>{children}</th>;
const Td = ({ children, style: s }) => <td style={{ padding: "11px 14px", fontSize: 13, color: "#2D5A72", fontFamily: "Inter, sans-serif", verticalAlign: "middle", ...s }}>{children}</td>;

/* ═══════════════════════════════════════════════════════════════════════════
   1. OVERVIEW SECTION
═══════════════════════════════════════════════════════════════════════════ */
function OverviewSection({ onGoToDoctors, onGoToUsers }) {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      api.get("/admin/dashboard/stats"),
      api.get("/admin/dashboard/activity"),
    ]).then(([s, a]) => {
      if (s.status === "fulfilled") setStats(s.value.data);
      if (a.status === "fulfilled") setActivity(a.value.data || []);
      setLoading(false);
    });
  }, []);

  if (loading) return <Spinner />;

  const d = stats?.doctors || {};
  const u = stats?.users || {};

  return (
    <div style={{ padding: "32px 28px 60px" }}>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>Admin Overview</h2>
        <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Platform health and pending actions</p>
      </div>

      {/* User stats */}
      <p style={sectionHeading}>Users</p>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 28 }}>
        <StatCard label="Total Users"   value={u.total}          sub={`+${u.new_this_week} this week`} color="#DAFF6B" />
        <StatCard label="Patients"      value={u.patients}       color="#4ADE80" />
        <StatCard label="Doctors"       value={u.doctors}        color="#60A5FA" />
        <StatCard label="Admins"        value={u.admins}         color="#C4B5FD" />
      </div>

      {/* Doctor pipeline */}
      <p style={sectionHeading}>Doctor Pipeline</p>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 28 }}>
        <StatCard label="Pending"       value={d.pending}        sub="awaiting review"   color="#FACC15" />
        <StatCard label="Under Review"  value={d.under_review}   sub="being reviewed"    color="#60A5FA" />
        <StatCard label="Approved"      value={d.approved}       color="#4ADE80" />
        <StatCard label="Live"          value={d.live}           sub="on platform"       color="#86EFAC" />
        <StatCard label="Suspended"     value={d.suspended}      color="#FCA5A5" />
        <StatCard label="This Month"    value={d.new_this_month} sub="new signups"       color="#E5E7EB" />
      </div>

      {/* Pending actions */}
      <p style={sectionHeading}>Pending Actions</p>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 32 }}>
        <div style={{ ...card, padding: "18px 22px", flex: "1 1 220px", cursor: "pointer" }} onClick={onGoToDoctors}>
          <p style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 700, color: "#FACC15", fontFamily: "Inter, sans-serif" }}>{stats?.documents?.pending_verification ?? 0}</p>
          <p style={{ margin: 0, fontSize: 13, color: "rgba(255,255,255,0.55)", fontFamily: "Inter, sans-serif" }}>Documents awaiting verification</p>
        </div>
        <div style={{ ...card, padding: "18px 22px", flex: "1 1 220px", cursor: "pointer" }} onClick={onGoToDoctors}>
          <p style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 700, color: "#60A5FA", fontFamily: "Inter, sans-serif" }}>{stats?.programs?.pending_review ?? 0}</p>
          <p style={{ margin: 0, fontSize: 13, color: "rgba(255,255,255,0.55)", fontFamily: "Inter, sans-serif" }}>Programs pending review</p>
        </div>
        <div style={{ ...card, padding: "18px 22px", flex: "1 1 220px", cursor: "pointer" }} onClick={onGoToDoctors}>
          <p style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 700, color: "#FACC15", fontFamily: "Inter, sans-serif" }}>{(d.pending || 0) + (d.under_review || 0)}</p>
          <p style={{ margin: 0, fontSize: 13, color: "rgba(255,255,255,0.55)", fontFamily: "Inter, sans-serif" }}>Doctors needing attention</p>
        </div>
      </div>

      {/* Recent activity */}
      {activity.length > 0 && (
        <>
          <p style={sectionHeading}>Recent Activity</p>
          <div style={{ ...card, overflow: "hidden" }}>
            {activity.slice(0, 10).map((ev) => (
              <div key={ev.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 18px", borderBottom: "1px solid rgba(90,144,168,0.1)" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: ev.event_type === "went_live" ? "#16A34A" : ev.event_type === "rejected" ? "#DC2626" : ev.event_type === "suspended" ? "#B45309" : "#9BBCCC" }} />
                <span style={{ flex: 1, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif" }}>
                  <strong style={{ color: "#1C3545" }}>{ev.doctor_name}</strong> — {(ev.event_type || "").replace(/_/g, " ")}
                  {ev.reason && <span style={{ color: "#9BBCCC" }}> · {ev.reason}</span>}
                </span>
                <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>
                  {ev.actioned_at ? new Date(ev.actioned_at).toLocaleDateString() : ""}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   2. USERS SECTION
═══════════════════════════════════════════════════════════════════════════ */
function UsersSection() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [err, setErr] = useState("");
  const [actionLoading, setActionLoading] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: 0, limit: 100 });
      if (search)       params.set("search", search);
      if (roleFilter)   params.set("role", roleFilter);
      if (statusFilter !== "") params.set("is_active", statusFilter);
      const { data } = await api.get(`/admin/users?${params}`);
      setUsers(data.items || []);
      setTotal(data.total || 0);
    } catch { setErr("Failed to load users."); }
    setLoading(false);
  }, [search, roleFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (user, field) => {
    const key = `${user.id}-${field}`;
    setActionLoading((p) => ({ ...p, [key]: true }));
    try {
      await api.patch(`/admin/users/${user.id}/status`, { [field]: !user[field] });
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, [field]: !u[field] } : u));
    } catch { setErr("Action failed."); }
    setActionLoading((p) => ({ ...p, [key]: false }));
  };

  const changeRole = async (user, newRole) => {
    if (!window.confirm(`Change ${user.name}'s role to "${newRole}"?`)) return;
    try {
      await api.patch(`/admin/users/${user.id}/role`, { role: newRole });
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, role: newRole } : u));
    } catch { setErr("Role change failed."); }
  };

  return (
    <div style={{ padding: "32px 28px 60px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: "0 0 2px", fontSize: 20, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>User Management</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{total} total users</p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input placeholder="Search name or email…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ ...inputBase, width: 220 }} />
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} style={{ ...inputBase, cursor: "pointer" }}>
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="doctor">Doctor</option>
            <option value="patient">Patient</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ ...inputBase, cursor: "pointer" }}>
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      <ErrBanner msg={err} onDismiss={() => setErr("")} />

      {loading ? <Spinner /> : (
        <div className="admin-table-wrap" style={{ ...card, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "rgba(255,255,255,0.03)" }}>
              <tr>
                <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Provider</Th>
                <Th>Active</Th><Th>Verified</Th><Th>Joined</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <Td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {u.profile_image
                        ? <img src={u.profile_image} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                        : <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(90,144,168,0.12)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>{(u.name || "?")[0].toUpperCase()}</div>
                      }
                      <span style={{ fontWeight: 600, color: "#1C3545" }}>{u.name}</span>
                    </div>
                  </Td>
                  <Td style={{ color: "#7AAABB" }}>{u.email}</Td>
                  <Td><Badge map={ROLE_COLORS} value={u.role} /></Td>
                  <Td style={{ color: "#9BBCCC", textTransform: "capitalize" }}>{u.provider}</Td>
                  <Td>
                    <span style={{ fontSize: 12, fontWeight: 600, color: u.is_active ? "#16A34A" : "#DC2626" }}>
                      {u.is_active ? "Active" : "Inactive"}
                    </span>
                  </Td>
                  <Td>
                    <span style={{ fontSize: 12, fontWeight: 600, color: u.is_verified ? "#16A34A" : "#9BBCCC" }}>
                      {u.is_verified ? "Verified" : "Unverified"}
                    </span>
                  </Td>
                  <Td style={{ color: "#9BBCCC", fontSize: 12 }}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </Td>
                  <Td>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Btn small label={u.is_active ? "Deactivate" : "Activate"}
                        color={u.is_active ? "#FCA5A5" : "#4ADE80"}
                        bg={u.is_active ? "rgba(252,165,165,0.08)" : "rgba(74,222,128,0.08)"}
                        border={u.is_active ? "rgba(252,165,165,0.25)" : "rgba(74,222,128,0.25)"}
                        disabled={!!actionLoading[`${u.id}-is_active`]}
                        onClick={() => toggleStatus(u, "is_active")} />
                      {!u.is_verified && (
                        <Btn small label="Verify" color="#4ADE80" bg="rgba(74,222,128,0.08)" border="rgba(74,222,128,0.25)"
                          disabled={!!actionLoading[`${u.id}-is_verified`]}
                          onClick={() => toggleStatus(u, "is_verified")} />
                      )}
                      {u.role !== "admin" && (
                        <select
                          value={u.role}
                          onChange={(e) => changeRole(u, e.target.value)}
                          style={{ ...inputBase, padding: "3px 8px", fontSize: 11, cursor: "pointer" }}
                        >
                          <option value="patient">patient</option>
                          <option value="doctor">doctor</option>
                          <option value="admin">admin</option>
                        </select>
                      )}
                    </div>
                  </Td>
                </TableRow>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 32, textAlign: "center", color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13 }}>No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   3. DOCTOR DETAIL PANEL — tabs: Profile | Documents | Clusters | Programs
                                  Checklist | Go-Live | Notes
═══════════════════════════════════════════════════════════════════════════ */
function DoctorDetailPanel({ doctorId, onBack }) {
  const [tab, setTab] = useState("profile");
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Tab data
  const [docs, setDocs]           = useState([]);
  const [clusters, setClusters]   = useState([]);
  const [allClusters, setAllClusters] = useState([]);
  const [programs, setPrograms]   = useState([]);
  const [expandedProgId,   setExpandedProgId]   = useState(null);
  const [progInclusionsMap, setProgInclusionsMap] = useState({});
  const [inclLoadingSet,   setInclLoadingSet]   = useState(new Set());
  const [checklist, setChecklist] = useState([]);
  const [goLiveLog, setGoLiveLog] = useState([]);
  const [notes, setNotes]         = useState([]);
  const [newNote, setNewNote]     = useState("");
  const [noteType, setNoteType]   = useState("general");
  const [noteLoading, setNoteLoading] = useState(false);

  // Notification dispatch state
  const [notifications, setNotifications] = useState([]);
  const [notifTitle, setNotifTitle]       = useState("");
  const [notifBody, setNotifBody]         = useState("");
  const [notifSending, setNotifSending]   = useState(false);
  const [goLiveReason, setGoLiveReason] = useState("");
  const [goLiveLoading, setGoLiveLoading] = useState(false);
  const [goLiveConfirm, setGoLiveConfirm] = useState({ open: false, event: null, label: "", color: "#5A90A8", bg: "rgba(90,144,168,0.1)", border: "rgba(90,144,168,0.25)" });
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignForm, setAssignForm] = useState({ cluster_id: "", expertise_level: "", years_in_cluster: "", notes: "" });
  const [assigning, setAssigning] = useState(false);
  const [approvingClusterId, setApprovingClusterId] = useState(null);
  const [approveExpertise, setApproveExpertise] = useState("proficient");

  // Cluster expand + biomarkers state
  const [expandedClusterIds, setExpandedClusterIds] = useState(new Set()); // Set of assignment IDs expanded
  const [clusterBioMap, setClusterBioMap]           = useState({});         // { cluster_id: BiomarkerMasterResponse[] }
  const [clusterBioLoading, setClusterBioLoading]   = useState(new Set());  // Set of cluster_ids currently loading

  // Custom questions state
  const [customQuestions, setCustomQuestions] = useState([]);
  const [qReviewId, setQReviewId] = useState(null);
  const [qReviewStatus, setQReviewStatus] = useState("approved");
  const [qReviewNotes, setQReviewNotes] = useState("");
  const [qReviewSaving, setQReviewSaving] = useState(false);

  // Checklist notes state
  const [checklistNotes, setChecklistNotes] = useState({});

  // Credential checks state
  const [credChecks, setCredChecks]     = useState([]);
  const [newCheckType, setNewCheckType] = useState("license_verification");
  const [newCheckNotes, setNewCheckNotes] = useState("");
  const [checkCreating, setCheckCreating] = useState(false);

  // Payment state
  const [payment, setPayment]           = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [feeAgreements, setFeeAgreements]   = useState([]);
  const [payouts, setPayouts]               = useState([]);
  const [feeFormOpen, setFeeFormOpen]       = useState(false);
  const [payoutFormOpen, setPayoutFormOpen] = useState(false);
  const [feeForm, setFeeForm] = useState({ agreement_version: "", fee_model: "flat_monthly", monthly_platform_fee_inr: "", per_patient_share_pct: "", minimum_monthly_fee: "", payment_terms: "Net 30", effective_from: "", expires_at: "" });
  const [payoutForm, setPayoutForm] = useState({ period_start: "", period_end: "", gross_amount: "", tds_rate: "10", gst_amount: "", reference_no: "", notes: "" });
  const [feeSaving, setFeeSaving]     = useState(false);
  const [payoutSaving, setPayoutSaving] = useState(false);

  // Biomarker customisation review state
  const [customisations, setCustomisations] = useState([]);
  const [biomarkerMap, setBiomarkerMap]     = useState({});
  const [bmLoading, setBmLoading]           = useState(false);
  const [expandedIds, setExpandedIds]       = useState(new Set());
  const [reviewingId, setReviewingId]       = useState(null);
  const [reviewNotes, setReviewNotes]       = useState("");
  const [reviewSaving, setReviewSaving]     = useState(false);
  const [rejectModal, setRejectModal]       = useState({ open: false, title: "", onConfirm: null });

  // Custom biomarker proposals review state
  const [customBiomarkers,   setCustomBiomarkers]   = useState([]);
  const [cbReviewingId,      setCbReviewingId]      = useState(null);
  const [cbReviewNotes,      setCbReviewNotes]      = useState("");
  const [cbReviewSaving,     setCbReviewSaving]     = useState(false);
  const [cbExpandedIds,      setCbExpandedIds]      = useState(new Set());

  const openRejectModal = (title, onConfirm) =>
    setRejectModal({ open: true, title, onConfirm });
  const closeRejectModal = () =>
    setRejectModal({ open: false, title: "", onConfirm: null });

  const toggleExpanded = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleCbExpanded = (id) =>
    setCbExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/doctors/${doctorId}`);
        setDoctor(data);
      } catch { setErr("Failed to load doctor."); }
      setLoading(false);
    };
    load();
  }, [doctorId]);

  const loadTab = useCallback(async (t) => {
    setErr("");
    try {
      if (t === "documents") {
        const { data } = await api.get(`/admin/doctors/${doctorId}/documents`);
        setDocs(Array.isArray(data) ? data : []);
      } else if (t === "clusters") {
        const [assignRes, masterRes] = await Promise.allSettled([
          api.get(`/admin/doctors/${doctorId}/clusters`),
          api.get("/admin/clusters?active_only=true"),
        ]);
        const clusterData = assignRes.status === "fulfilled" ? (Array.isArray(assignRes.value.data) ? assignRes.value.data : []) : [];
        if (assignRes.status === "fulfilled") setClusters(clusterData);
        if (masterRes.status === "fulfilled") setAllClusters(Array.isArray(masterRes.value.data) ? masterRes.value.data : []);
      } else if (t === "programs") {
        const { data } = await api.get(`/admin/doctors/${doctorId}/programs`);
        setPrograms(Array.isArray(data) ? data : []);
      } else if (t === "checklist") {
        const { data } = await api.get(`/admin/doctors/${doctorId}/checklist`);
        setChecklist(Array.isArray(data) ? data : []);
      } else if (t === "golive") {
        const { data } = await api.get(`/admin/doctors/${doctorId}/go-live/log`);
        setGoLiveLog(Array.isArray(data) ? data : []);
      } else if (t === "biomarkers") {
        setBmLoading(true);
        try {
          const { data } = await api.get(`/admin/doctors/${doctorId}/biomarkers/customisations`);
          const custs = Array.isArray(data) ? data : [];
          setCustomisations(custs);
          const clusterIds = [...new Set(custs.map((c) => c.cluster_id))];
          const bmap = {};
          await Promise.allSettled(
            clusterIds.map(async (cid) => {
              const { data: bms } = await api.get(`/admin/clusters/${cid}/biomarkers`);
              if (Array.isArray(bms)) bms.forEach((bm) => { bmap[bm.id] = bm; });
            })
          );
          setBiomarkerMap(bmap);
          const cbRes = await api.get(`/admin/doctors/${doctorId}/custom-biomarkers`);
          setCustomBiomarkers(Array.isArray(cbRes.data) ? cbRes.data : []);
        } catch (e2) { setErr(e2.response?.data?.detail || "Failed to load biomarker requests."); }
        setBmLoading(false);
      } else if (t === "questions") {
        const { data } = await api.get(`/admin/doctors/${doctorId}/custom-questions`);
        setCustomQuestions(Array.isArray(data) ? data : []);
      } else if (t === "credentials") {
        const { data } = await api.get(`/admin/doctors/${doctorId}/credential-checks`);
        setCredChecks(Array.isArray(data) ? data : []);
      } else if (t === "payment") {
        setPaymentLoading(true);
        try {
          const [payRes, feeRes, payoutRes] = await Promise.allSettled([
            api.get(`/admin/doctors/${doctorId}/payment`),
            api.get(`/admin/doctors/${doctorId}/fee-agreements`),
            api.get(`/admin/doctors/${doctorId}/payouts`),
          ]);
          if (payRes.status === "fulfilled") setPayment(payRes.value.data);
          else if (payRes.reason?.response?.status === 404) setPayment(null);
          if (feeRes.status === "fulfilled") setFeeAgreements(Array.isArray(feeRes.value.data) ? feeRes.value.data : []);
          if (payoutRes.status === "fulfilled") setPayouts(Array.isArray(payoutRes.value.data) ? payoutRes.value.data : []);
        } catch (e2) {
          setErr(e2.response?.data?.detail || "Failed to load payment details.");
        }
        setPaymentLoading(false);
      } else if (t === "notes") {
        const [notesRes, notifsRes] = await Promise.allSettled([
          api.get(`/admin/doctors/${doctorId}/notes`),
          api.get(`/admin/doctors/${doctorId}/notifications`),
        ]);
        if (notesRes.status === "fulfilled") setNotes(Array.isArray(notesRes.value.data) ? notesRes.value.data : []);
        if (notifsRes.status === "fulfilled") setNotifications(Array.isArray(notifsRes.value.data) ? notifsRes.value.data : []);
      }
    } catch (e) { setErr(e.response?.data?.detail || "Failed to load data."); }
  }, [doctorId]);

  useEffect(() => {
    // Reset cluster expansion state when switching away from clusters tab
    if (tab !== "clusters") {
      setExpandedClusterIds(new Set());
      setClusterBioMap({});
      setClusterBioLoading(new Set());
    }
    loadTab(tab);
  }, [tab, loadTab]);

  const verifyDoc = async (docId, verificationStatus, rejectionReason = null) => {
    try {
      await api.put(`/admin/doctors/${doctorId}/documents/${docId}/verify`, { verification_status: verificationStatus, rejection_reason: rejectionReason });
      setDocs((prev) => prev.map((d) => d.id === docId ? { ...d, verification_status: verificationStatus } : d));
    } catch (e) { setErr(e.response?.data?.detail || "Action failed."); }
  };

  const reviewCluster = async (assignmentId, approvalStatus, rejectionReason = null, expertiseLevel = null) => {
    try {
      const payload = { approval_status: approvalStatus };
      if (rejectionReason) payload.rejection_reason = rejectionReason;
      if (expertiseLevel) payload.expertise_level = expertiseLevel;
      await api.put(`/admin/doctors/${doctorId}/clusters/${assignmentId}/review`, payload);
      setClusters((prev) => prev.map((c) => c.id === assignmentId ? { ...c, approval_status: approvalStatus, ...(expertiseLevel && { expertise_level: expertiseLevel }) } : c));
      setApprovingClusterId(null);
    } catch (e) { setErr(e.response?.data?.detail || "Action failed."); }
  };

  const assignCluster = async () => {
    if (!assignForm.cluster_id) { setErr("Please select a cluster."); return; }
    setAssigning(true);
    try {
      const payload = {
        cluster_id: assignForm.cluster_id,
        ...(assignForm.expertise_level && { expertise_level: assignForm.expertise_level }),
        ...(assignForm.years_in_cluster !== "" && { years_in_cluster: Number(assignForm.years_in_cluster) }),
        ...(assignForm.notes && { notes: assignForm.notes }),
      };
      const { data } = await api.post(`/admin/doctors/${doctorId}/clusters`, payload);
      setClusters((prev) => [data, ...prev]);
      setAssignForm({ cluster_id: "", expertise_level: "", years_in_cluster: "", notes: "" });
      setShowAssignForm(false);
    } catch (e) { setErr(e.response?.data?.detail || "Assignment failed."); }
    setAssigning(false);
  };

  const loadClusterBiomarkers = async (clusterId) => {
    if (clusterBioMap[clusterId] !== undefined) return; // already fetched
    setClusterBioLoading((prev) => new Set(prev).add(clusterId));
    try {
      const { data } = await api.get(`/admin/clusters/${clusterId}/biomarkers`);
      setClusterBioMap((prev) => ({ ...prev, [clusterId]: Array.isArray(data) ? data : [] }));
    } catch (_) {
      setClusterBioMap((prev) => ({ ...prev, [clusterId]: [] }));
    }
    setClusterBioLoading((prev) => { const s = new Set(prev); s.delete(clusterId); return s; });
  };

  const toggleClusterExpand = (assignmentId, clusterId) => {
    setExpandedClusterIds((prev) => {
      const next = new Set(prev);
      if (next.has(assignmentId)) { next.delete(assignmentId); } else { next.add(assignmentId); }
      return next;
    });
    loadClusterBiomarkers(clusterId);
  };

  const reviewCustomisation = async (custId, status) => {
    setReviewSaving(true);
    try {
      const { data } = await api.put(
        `/admin/doctors/${doctorId}/biomarkers/${custId}/review`,
        { status, admin_notes: reviewNotes.trim() || null }
      );
      setCustomisations((prev) => prev.map((c) => c.id === custId ? data : c));
      setReviewingId(null);
      setReviewNotes("");
    } catch (e) { setErr(e.response?.data?.detail || "Review failed."); }
    setReviewSaving(false);
  };

  const reviewCustomBiomarker = async (cbId, status) => {
    setCbReviewSaving(true);
    try {
      const { data } = await api.put(
        `/admin/doctors/${doctorId}/custom-biomarkers/${cbId}/review`,
        { approval_status: status, admin_notes: cbReviewNotes.trim() || null }
      );
      setCustomBiomarkers((prev) => prev.map((b) => b.id === cbId ? data : b));
      setCbReviewingId(null); setCbReviewNotes("");
    } catch (e) { setErr(e.response?.data?.detail || "Review failed."); }
    setCbReviewSaving(false);
  };

  const toggleProgExpand = async (progId) => {
    if (expandedProgId === progId) { setExpandedProgId(null); return; }
    setExpandedProgId(progId);
    if (!progInclusionsMap[progId]) {
      setInclLoadingSet((prev) => new Set(prev).add(progId));
      try {
        const { data } = await api.get(`/admin/doctors/${doctorId}/programs/${progId}/inclusions`);
        setProgInclusionsMap((prev) => ({ ...prev, [progId]: Array.isArray(data) ? data : [] }));
      } catch (_) {
        setProgInclusionsMap((prev) => ({ ...prev, [progId]: [] }));
      }
      setInclLoadingSet((prev) => { const s = new Set(prev); s.delete(progId); return s; });
    }
  };

  const createCredCheck = async () => {
    setCheckCreating(true);
    try {
      const { data } = await api.post(`/admin/doctors/${doctorId}/credential-checks`, {
        check_type: newCheckType,
        ...(newCheckNotes.trim() && { notes: newCheckNotes.trim() }),
      });
      setCredChecks((prev) => [...prev, data]);
      setNewCheckNotes("");
    } catch (e) { setErr(e.response?.data?.detail || "Failed to create check."); }
    setCheckCreating(false);
  };

  const updateCredCheck = async (checkId, newStatus) => {
    try {
      const { data } = await api.patch(`/admin/doctors/${doctorId}/credential-checks/${checkId}`, { status: newStatus });
      setCredChecks((prev) => prev.map((c) => c.id === checkId ? data : c));
    } catch (e) { setErr(e.response?.data?.detail || "Update failed."); }
  };

  const verifyPayment = async (isVerified) => {
    setPaymentLoading(true);
    try {
      const { data } = await api.put(`/admin/doctors/${doctorId}/payment/verify`, { is_verified: isVerified });
      setPayment(data);
    } catch (e) { setErr(e.response?.data?.detail || "Verification failed."); }
    setPaymentLoading(false);
  };

  const submitFeeAgreement = async () => {
    setFeeSaving(true);
    try {
      const payload = {
        agreement_version: feeForm.agreement_version,
        fee_model: feeForm.fee_model,
        monthly_platform_fee_inr: feeForm.monthly_platform_fee_inr ? Number(feeForm.monthly_platform_fee_inr) : null,
        per_patient_share_pct: feeForm.per_patient_share_pct ? Number(feeForm.per_patient_share_pct) : null,
        minimum_monthly_fee: feeForm.minimum_monthly_fee ? Number(feeForm.minimum_monthly_fee) : null,
        payment_terms: feeForm.payment_terms || null,
        effective_from: feeForm.effective_from || null,
        expires_at: feeForm.expires_at || null,
      };
      const { data } = await api.post(`/admin/doctors/${doctorId}/fee-agreements`, payload);
      setFeeAgreements((prev) => [data, ...prev.map((a) => ({ ...a, is_active: false }))]);
      setFeeFormOpen(false);
      setFeeForm({ agreement_version: "", fee_model: "flat_monthly", monthly_platform_fee_inr: "", per_patient_share_pct: "", minimum_monthly_fee: "", payment_terms: "Net 30", effective_from: "", expires_at: "" });
    } catch (e) { setErr(e.response?.data?.detail || "Failed to create fee agreement."); }
    setFeeSaving(false);
  };

  const submitPayout = async () => {
    setPayoutSaving(true);
    try {
      const payload = {
        period_start: payoutForm.period_start,
        period_end: payoutForm.period_end,
        gross_amount: Number(payoutForm.gross_amount),
        tds_rate: payoutForm.tds_rate !== "" ? Number(payoutForm.tds_rate) : 10,
        gst_amount: payoutForm.gst_amount ? Number(payoutForm.gst_amount) : null,
        reference_no: payoutForm.reference_no || null,
        notes: payoutForm.notes || null,
      };
      const { data } = await api.post(`/admin/doctors/${doctorId}/payouts`, payload);
      setPayouts((prev) => [data, ...prev]);
      setPayoutFormOpen(false);
      setPayoutForm({ period_start: "", period_end: "", gross_amount: "", tds_rate: "10", gst_amount: "", reference_no: "", notes: "" });
    } catch (e) { setErr(e.response?.data?.detail || "Failed to create payout."); }
    setPayoutSaving(false);
  };

  const updatePayoutStatus = async (payoutId, newStatus) => {
    try {
      const { data } = await api.patch(`/admin/doctors/${doctorId}/payouts/${payoutId}`, { status: newStatus });
      setPayouts((prev) => prev.map((p) => p.id === payoutId ? data : p));
    } catch (e) { setErr(e.response?.data?.detail || "Failed to update payout."); }
  };

  const reviewProgram = async (programId, reviewStatus, adminNotes = null) => {
    try {
      const { data } = await api.put(`/admin/doctors/${doctorId}/programs/${programId}/review`, {
        status: reviewStatus,
        ...(adminNotes && { admin_notes: adminNotes }),
      });
      setPrograms((prev) => prev.map((p) => p.id === programId ? data : p));
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : (typeof detail === "string" ? detail : "Action failed.");
      setErr(msg);
    }
  };

  const updateChecklist = async (item, newStatus, notes = null) => {
    try {
      const payload = { status: newStatus };
      if (notes) payload.notes = notes;
      await api.put(`/admin/doctors/${doctorId}/checklist/${item}`, payload);
      setChecklist((prev) => prev.map((c) => c.checklist_item === item ? { ...c, status: newStatus, notes: notes || c.notes } : c));
      setChecklistNotes((prev) => ({ ...prev, [item]: "" }));
    } catch (e) { setErr(e.response?.data?.detail || "Action failed."); }
  };

  const reviewCustomQuestion = async () => {
    if (!qReviewId) return;
    setQReviewSaving(true);
    try {
      const { data } = await api.put(`/admin/doctors/${doctorId}/custom-questions/${qReviewId}/review`, {
        approval_status: qReviewStatus,
        admin_notes: qReviewNotes || null,
      });
      setCustomQuestions((prev) => prev.map((q) => q.id === qReviewId ? data : q));
      setQReviewId(null); setQReviewNotes("");
    } catch (e) { setErr(e.response?.data?.detail || "Action failed."); }
    setQReviewSaving(false);
  };

  const GO_LIVE_META = {
    went_live:   { label: "Set Live",   color: "#16A34A", bg: "rgba(22,163,74,0.1)",    border: "rgba(22,163,74,0.25)"   },
    paused:      { label: "Pause",      color: "#B45309", bg: "rgba(180,83,9,0.08)",    border: "rgba(180,83,9,0.25)"    },
    suspended:   { label: "Suspend",    color: "#DC2626", bg: "rgba(220,38,38,0.08)",   border: "rgba(220,38,38,0.25)"   },
    reactivated: { label: "Reactivate", color: "#1D4ED8", bg: "rgba(29,78,216,0.08)",   border: "rgba(29,78,216,0.25)"   },
    rejected:    { label: "Reject",     color: "#DC2626", bg: "rgba(220,38,38,0.08)",   border: "rgba(220,38,38,0.25)"   },
    offboarded:  { label: "Offboard",   color: "#6B7280", bg: "rgba(107,114,128,0.08)", border: "rgba(107,114,128,0.25)" },
  };

  const sendGoLive = (eventType) => {
    if (!goLiveReason.trim()) { setErr("Please enter a reason before selecting an action."); return; }
    const meta = GO_LIVE_META[eventType] || { label: eventType, color: "#5A90A8", bg: "rgba(90,144,168,0.1)", border: "rgba(90,144,168,0.25)" };
    setGoLiveConfirm({ open: true, event: eventType, ...meta });
  };

  const executeGoLive = async () => {
    const eventType = goLiveConfirm.event;
    setGoLiveConfirm((s) => ({ ...s, open: false }));
    setGoLiveLoading(true);
    try {
      await api.post(`/admin/doctors/${doctorId}/go-live`, { event_type: eventType, reason: goLiveReason });
      setGoLiveReason("");
      await loadTab("golive");
      const { data } = await api.get(`/admin/doctors/${doctorId}`);
      setDoctor(data);
    } catch (e) { setErr(e.response?.data?.detail || "Go-live action failed."); }
    setGoLiveLoading(false);
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setNoteLoading(true);
    try {
      await api.post(`/admin/doctors/${doctorId}/notes`, { note: newNote, note_type: noteType });
      setNewNote("");
      await loadTab("notes");
    } catch (e) { setErr(e.response?.data?.detail || "Failed to add note."); }
    setNoteLoading(false);
  };

  const sendNotification = async () => {
    if (!notifTitle.trim()) return;
    setNotifSending(true);
    try {
      await api.post(`/admin/doctors/${doctorId}/notifications`, { title: notifTitle, body: notifBody });
      setNotifTitle("");
      setNotifBody("");
      await loadTab("notes");
    } catch (e) { setErr(e.response?.data?.detail || "Failed to send notification."); }
    setNotifSending(false);
  };

  const TABS = [
    { key: "profile",      label: "Profile"      },
    { key: "documents",    label: "Documents"    },
    { key: "clusters",     label: "Clusters"     },
    { key: "biomarkers",   label: "Biomarkers"   },
    { key: "questions",    label: "Questions"    },
    { key: "programs",     label: "Programs"     },
    { key: "payment",      label: "Payment"      },
    { key: "checklist",    label: "Checklist"    },
    { key: "credentials",  label: "Credentials"  },
    { key: "golive",       label: "Go-Live"      },
    { key: "notes",        label: "Notes"        },
  ];

  if (loading) return <div style={{ padding: 32 }}><Spinner /></div>;

  const prof = doctor?.professional_profile || {};

  return (
    <div style={{ padding: "24px 28px 60px" }}>
      {/* Back + header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <button onClick={onBack} style={{ ...inputBase, padding: "6px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>← Back</button>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {doctor?.profile_photo_url && (
            <img src={`${UPLOADS_BASE}${doctor.profile_photo_url}`} alt="" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(90,144,168,0.4)" }} />
          )}
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{doctor?.display_name || "Doctor"}</h2>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{prof.primary_specialty || "—"} · {doctor?.city || ""}</p>
          </div>
          {doctor?.account_status && <Badge map={ACCT_STATUS} value={doctor.account_status} />}
        </div>
      </div>

      <ErrBanner msg={err} onDismiss={() => setErr("")} />

      <RejectModal
        open={rejectModal.open}
        title={rejectModal.title}
        onConfirm={(reason) => { rejectModal.onConfirm?.(reason); closeRejectModal(); }}
        onCancel={closeRejectModal}
      />

      <ConfirmModal
        open={goLiveConfirm.open}
        title={`Confirm: ${goLiveConfirm.label}`}
        subtitle="This will update the doctor's account status and be logged in the audit trail."
        body={goLiveReason ? `Reason: "${goLiveReason}"` : undefined}
        confirmLabel={goLiveConfirm.label}
        confirmColor={goLiveConfirm.color}
        confirmBg={goLiveConfirm.bg}
        confirmBorder={goLiveConfirm.border}
        onConfirm={executeGoLive}
        onCancel={() => setGoLiveConfirm((s) => ({ ...s, open: false }))}
      />

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid rgba(90,144,168,0.2)", paddingBottom: 0, flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding: "8px 16px", border: "none", borderBottom: tab === t.key ? "2px solid #5A90A8" : "2px solid transparent", background: "none", color: tab === t.key ? "#1C3545" : "#7AAABB", fontSize: 13, fontWeight: tab === t.key ? 600 : 400, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms", borderRadius: "4px 4px 0 0" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Profile tab */}
      {tab === "profile" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {[
            ["Phone", doctor?.phone], ["Gender", doctor?.gender], ["DOB", doctor?.date_of_birth],
            ["City", doctor?.city], ["Country", doctor?.country], ["Timezone", doctor?.timezone],
            ["Languages", doctor?.languages_spoken?.join(", ")],
            ["Onboarding Step", doctor?.onboarding_step],
          ].map(([l, v]) => v ? (
            <div key={l} style={{ ...card, padding: "14px 18px" }}>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>{l}</p>
              <p style={{ ...valueTxt, margin: 0, fontWeight: 500 }}>{v}</p>
            </div>
          ) : null)}
          {prof.medical_degree && (
            <div style={{ ...card, padding: "14px 18px" }}>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Medical Degree</p>
              <p style={{ ...valueTxt, margin: 0, fontWeight: 500 }}>{prof.medical_degree}</p>
            </div>
          )}
          {prof.years_of_experience != null && (
            <div style={{ ...card, padding: "14px 18px" }}>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Years Experience</p>
              <p style={{ ...valueTxt, margin: 0, fontWeight: 500 }}>{prof.years_of_experience}</p>
            </div>
          )}
          {prof.registration_number && (
            <div style={{ ...card, padding: "14px 18px", gridColumn: "span 2" }}>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Registration · {prof.registration_body} · {prof.registration_country}</p>
              <p style={{ ...valueTxt, margin: 0, fontWeight: 500 }}>{prof.registration_number}</p>
            </div>
          )}
          {prof.additional_qualifications?.length > 0 && (
            <div style={{ ...card, padding: "14px 18px", gridColumn: "span 2" }}>
              <p style={{ ...labelTxt, margin: "0 0 6px" }}>Additional Qualifications</p>
              <p style={{ ...valueTxt, margin: 0, fontWeight: 500 }}>{prof.additional_qualifications.join(", ")}</p>
            </div>
          )}
          {prof.secondary_specialties?.length > 0 && (
            <div style={{ ...card, padding: "14px 18px", gridColumn: "span 2" }}>
              <p style={{ ...labelTxt, margin: "0 0 6px" }}>Secondary Specialties</p>
              <p style={{ ...valueTxt, margin: 0, fontWeight: 500 }}>{prof.secondary_specialties.join(", ")}</p>
            </div>
          )}
          {prof.practice_type && (
            <div style={{ ...card, padding: "14px 18px" }}>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Practice Type</p>
              <p style={{ ...valueTxt, margin: 0, fontWeight: 500 }}>{prof.practice_type.replace(/_/g, " ")}</p>
            </div>
          )}
          {prof.clinic_name && (
            <div style={{ ...card, padding: "14px 18px" }}>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Clinic</p>
              <p style={{ ...valueTxt, margin: 0, fontWeight: 500 }}>{prof.clinic_name}</p>
            </div>
          )}
          {prof.clinic_address && (
            <div style={{ ...card, padding: "14px 18px", gridColumn: "span 2" }}>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Clinic Address</p>
              <p style={{ ...valueTxt, margin: 0, fontWeight: 500 }}>{prof.clinic_address}</p>
            </div>
          )}
          {prof.consultation_mode?.length > 0 && (
            <div style={{ ...card, padding: "14px 18px" }}>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Consultation Mode</p>
              <p style={{ ...valueTxt, margin: 0, fontWeight: 500 }}>{prof.consultation_mode.join(", ")}</p>
            </div>
          )}
          <div style={{ ...card, padding: "14px 18px" }}>
            <p style={{ ...labelTxt, margin: "0 0 4px" }}>Telehealth</p>
            <p style={{ ...valueTxt, margin: 0, fontWeight: 500 }}>{prof.telehealth_enabled ? "Enabled" : "Disabled"}</p>
          </div>
          {prof.bio_short && (
            <div style={{ ...card, padding: "14px 18px", gridColumn: "span 2" }}>
              <p style={{ ...labelTxt, margin: "0 0 6px" }}>Short Bio</p>
              <p style={{ ...valueTxt, margin: 0, lineHeight: 1.7 }}>{prof.bio_short}</p>
            </div>
          )}
          {prof.bio_long && (
            <div style={{ ...card, padding: "14px 18px", gridColumn: "span 2" }}>
              <p style={{ ...labelTxt, margin: "0 0 6px" }}>Full Bio</p>
              <p style={{ ...valueTxt, margin: 0, lineHeight: 1.7 }}>{prof.bio_long}</p>
            </div>
          )}
          {prof.philosophy_statement && (
            <div style={{ ...card, padding: "14px 18px", gridColumn: "span 2" }}>
              <p style={{ ...labelTxt, margin: "0 0 6px" }}>Philosophy Statement</p>
              <p style={{ ...valueTxt, margin: 0, lineHeight: 1.7, fontStyle: "italic" }}>{prof.philosophy_statement}</p>
            </div>
          )}
          {prof.video_intro_url && (
            <div style={{ ...card, padding: "14px 18px", gridColumn: "span 2" }}>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Video Introduction</p>
              <a href={prof.video_intro_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>Watch video →</a>
            </div>
          )}
        </div>
      )}

      {/* Documents tab */}
      {tab === "documents" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {docs.length === 0 && <p style={{ ...labelTxt, textAlign: "center", padding: 32 }}>No documents uploaded.</p>}
          {docs.map((doc) => (
            <div key={doc.id} style={{ ...card, padding: "16px 20px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{doc.document_label || doc.document_type}</p>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {doc.document_type && <span style={labelTxt}>{doc.document_type.replace(/_/g, " ")}</span>}
                  {doc.issued_by && <span style={labelTxt}>Issued by {doc.issued_by}</span>}
                  {doc.expiry_date && <span style={{ ...labelTxt, color: doc.is_expiring_soon ? "#B45309" : undefined }}>Expires {doc.expiry_date}</span>}
                  {doc.file_url && <a href={doc.file_url.startsWith("http") ? doc.file_url : `${UPLOADS_BASE}${doc.file_url}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>View file →</a>}
                </div>
              </div>
              <Badge map={VERIFY_STATUS} value={doc.verification_status || "pending"} />
              <div style={{ display: "flex", gap: 8 }}>
                {doc.verification_status !== "verified" && (
                  <Btn small label="Verify" color="#16A34A" bg="rgba(22,163,74,0.1)" border="rgba(22,163,74,0.25)" onClick={() => verifyDoc(doc.id, "verified")} />
                )}
                {doc.verification_status !== "rejected" && (
                  <Btn small label="Reject" color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)"
                    onClick={() => openRejectModal("Reject Document", (r) => verifyDoc(doc.id, "rejected", r))} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Credentials tab */}
      {tab === "credentials" && (() => {
        const CHECK_STATUS_MAP = {
          pending:    { label: "Pending",    color: "#B45309", bg: "rgba(180,83,9,0.1)",    border: "rgba(180,83,9,0.25)"    },
          passed:     { label: "Passed",     color: "#16A34A", bg: "rgba(22,163,74,0.1)",   border: "rgba(22,163,74,0.25)"   },
          failed:     { label: "Failed",     color: "#DC2626", bg: "rgba(220,38,38,0.1)",   border: "rgba(220,38,38,0.25)"   },
          needs_info: { label: "Needs Info", color: "#7C3AED", bg: "rgba(124,58,237,0.1)",  border: "rgba(124,58,237,0.25)"  },
        };
        const CHECK_TYPES = [
          { value: "license_verification",    label: "License Verification"    },
          { value: "sanctions_check",         label: "Sanctions Check"         },
          { value: "identity_check",          label: "Identity Check"          },
          { value: "qualification_check",     label: "Qualification Check"     },
          { value: "cluster_expertise_review",label: "Cluster Expertise Review"},
          { value: "protocol_review",         label: "Protocol Review"         },
        ];
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Create check form */}
            <div style={{ ...card, padding: "18px 20px" }}>
              <p style={sectionHeading}>Run New Credential Check</p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ flex: "0 0 220px" }}>
                  <p style={{ ...labelTxt, margin: "0 0 5px" }}>Check Type</p>
                  <select value={newCheckType} onChange={(e) => setNewCheckType(e.target.value)}
                    style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
                    {CHECK_TYPES.map((ct) => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <p style={{ ...labelTxt, margin: "0 0 5px" }}>Internal Notes (optional)</p>
                  <input value={newCheckNotes} onChange={(e) => setNewCheckNotes(e.target.value)}
                    placeholder="Notes visible only to admin…"
                    style={{ ...inputBase, width: "100%", boxSizing: "border-box" }} />
                </div>
                <Btn label={checkCreating ? "Creating…" : "Add Check"} disabled={checkCreating}
                  color="#1D4ED8" bg="rgba(29,78,216,0.08)" border="rgba(29,78,216,0.25)"
                  onClick={createCredCheck} />
              </div>
            </div>

            {/* Check list */}
            <div style={{ ...card, overflow: "hidden" }}>
              {credChecks.length === 0 && (
                <p style={{ padding: 32, textAlign: "center", color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13 }}>No credential checks yet. Add one above.</p>
              )}
              {credChecks.map((chk, idx) => {
                const st = CHECK_STATUS_MAP[chk.status] || CHECK_STATUS_MAP.pending;
                const typeLabel = CHECK_TYPES.find((t) => t.value === chk.check_type)?.label || chk.check_type;
                return (
                  <div key={chk.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", borderBottom: idx < credChecks.length - 1 ? "1px solid rgba(90,144,168,0.1)" : "none", flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{typeLabel}</p>
                      {chk.notes && <p style={{ margin: "3px 0 0", fontSize: 12, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>{chk.notes}</p>}
                      {chk.checked_at && <p style={{ margin: "2px 0 0", fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Updated {new Date(chk.checked_at).toLocaleDateString()}</p>}
                    </div>
                    <Badge map={CHECK_STATUS_MAP} value={chk.status || "pending"} />
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      {chk.status !== "passed"     && <Btn small label="Pass"       color="#16A34A" bg="rgba(22,163,74,0.08)"    border="rgba(22,163,74,0.25)"    onClick={() => updateCredCheck(chk.id, "passed")}     />}
                      {chk.status !== "failed"     && <Btn small label="Fail"       color="#DC2626" bg="rgba(220,38,38,0.08)"   border="rgba(220,38,38,0.25)"   onClick={() => updateCredCheck(chk.id, "failed")}     />}
                      {chk.status !== "needs_info" && <Btn small label="Needs Info" color="#7C3AED" bg="rgba(124,58,237,0.08)" border="rgba(124,58,237,0.25)" onClick={() => updateCredCheck(chk.id, "needs_info")} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Clusters tab */}
      {tab === "clusters" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Header row with Assign button */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{clusters.length} cluster{clusters.length !== 1 ? "s" : ""}</span>
            <Btn small label={showAssignForm ? "Cancel" : "+ Assign Cluster"} onClick={() => { setShowAssignForm((p) => !p); setErr(""); }} />
          </div>

          {/* Assign form */}
          {showAssignForm && (
            <div style={{ ...card, padding: "18px 20px" }}>
              <p style={{ ...sectionHeading, marginBottom: 14 }}>Assign Cluster to Doctor</p>
              <div className="ob-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <p style={{ ...labelTxt, margin: "0 0 5px" }}>Cluster *</p>
                  <select
                    value={assignForm.cluster_id}
                    onChange={(e) => setAssignForm((p) => ({ ...p, cluster_id: e.target.value }))}
                    style={{ ...inputBase, width: "100%", cursor: "pointer" }}
                  >
                    <option value="">— select cluster —</option>
                    {allClusters
                      .filter((ac) => !clusters.some((c) => c.cluster_id === ac.id))
                      .map((ac) => (
                        <option key={ac.id} value={ac.id}>{ac.cluster_name} ({ac.cluster_code})</option>
                      ))
                    }
                  </select>
                </div>
                <div>
                  <p style={{ ...labelTxt, margin: "0 0 5px" }}>Expertise Level</p>
                  <select
                    value={assignForm.expertise_level}
                    onChange={(e) => setAssignForm((p) => ({ ...p, expertise_level: e.target.value }))}
                    style={{ ...inputBase, width: "100%", cursor: "pointer" }}
                  >
                    <option value="">— optional —</option>
                    <option value="familiar">Familiar</option>
                    <option value="proficient">Proficient</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
                <div>
                  <p style={{ ...labelTxt, margin: "0 0 5px" }}>Years in Cluster</p>
                  <input
                    type="number" min={0} max={60}
                    value={assignForm.years_in_cluster}
                    onChange={(e) => setAssignForm((p) => ({ ...p, years_in_cluster: e.target.value }))}
                    placeholder="Optional"
                    style={{ ...inputBase, width: "100%" }}
                  />
                </div>
                <div>
                  <p style={{ ...labelTxt, margin: "0 0 5px" }}>Notes</p>
                  <input
                    value={assignForm.notes}
                    onChange={(e) => setAssignForm((p) => ({ ...p, notes: e.target.value }))}
                    placeholder="Optional admin note"
                    style={{ ...inputBase, width: "100%" }}
                  />
                </div>
              </div>
              <Btn label={assigning ? "Assigning…" : "Assign Cluster"} disabled={!assignForm.cluster_id || assigning} onClick={assignCluster} />
            </div>
          )}

          {clusters.length === 0 && !showAssignForm && (
            <p style={{ ...labelTxt, textAlign: "center", padding: 32 }}>No clusters assigned yet.</p>
          )}

          {clusters.map((c) => {
            const isExpanded = expandedClusterIds.has(c.id);
            const bms        = clusterBioMap[c.cluster_id] || [];
            const bioLoading = clusterBioLoading.has(c.cluster_id);
            const cl         = c.cluster;

            return (
              <div key={c.id} style={{ ...card, overflow: "hidden" }}>

                {/* ── Card header row ── */}
                <div
                  style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", cursor: "pointer", transition: "background 150ms" }}
                  onClick={() => toggleClusterExpand(c.id, c.cluster_id)}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(90,144,168,0.04)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  {/* Icon */}
                  <div style={{ width: 38, height: 38, borderRadius: 9, overflow: "hidden", flexShrink: 0, border: "1px solid rgba(90,144,168,0.2)", background: "rgba(90,144,168,0.06)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {cl?.icon_url
                      ? <img src={cl.icon_url.startsWith("http") ? cl.icon_url : `${UPLOADS_BASE}${cl.icon_url}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span style={{ fontSize: 18 }}>🧬</span>
                    }
                  </div>

                  {/* Name + meta */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 2 }}>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {cl?.cluster_name || c.cluster_id}
                      </p>
                      {cl?.cluster_code && (
                        <code style={{ fontSize: 10, color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "1px 5px", borderRadius: 3, fontFamily: "monospace" }}>{cl.cluster_code}</code>
                      )}
                      {!c.claimed_by_doctor && (
                        <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 999, background: "rgba(124,58,237,0.1)", color: "#7C3AED", border: "1px solid rgba(124,58,237,0.25)", fontFamily: "Inter, sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                          Admin Assigned
                        </span>
                      )}
                    </div>
                    <p style={{ ...labelTxt, margin: 0, fontSize: 11 }}>
                      {c.expertise_level ? c.expertise_level.charAt(0).toUpperCase() + c.expertise_level.slice(1) : "No level set"}
                      {c.years_in_cluster ? ` · ${c.years_in_cluster} yrs` : ""}
                      {cl?.cluster_group ? ` · ${cl.cluster_group.replace(/_/g, " ")}` : ""}
                    </p>
                  </div>

                  {/* Status badge + actions + expand toggle */}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", flexShrink: 0 }}>
                    <Badge map={{
                      pending:  { label: "Pending",  color: "#B45309", bg: "rgba(180,83,9,0.1)",   border: "rgba(180,83,9,0.25)"   },
                      approved: { label: "Approved", color: "#16A34A", bg: "rgba(22,163,74,0.1)",  border: "rgba(22,163,74,0.25)"  },
                      rejected: { label: "Rejected", color: "#DC2626", bg: "rgba(220,38,38,0.1)",  border: "rgba(220,38,38,0.25)"  },
                    }} value={c.approval_status || "pending"} />

                    {c.approval_status !== "approved" && approvingClusterId !== c.id && (
                      <Btn small label="Approve" color="#16A34A" bg="rgba(22,163,74,0.1)" border="rgba(22,163,74,0.25)"
                        onClick={(e) => { e.stopPropagation(); setApprovingClusterId(c.id); setApproveExpertise("proficient"); }} />
                    )}
                    {approvingClusterId === c.id && (
                      <>
                        <select value={approveExpertise} onChange={(e) => setApproveExpertise(e.target.value)}
                          style={{ ...inputBase, padding: "4px 8px", fontSize: 12, cursor: "pointer" }}
                          onClick={(e) => e.stopPropagation()}>
                          <option value="familiar">Familiar</option>
                          <option value="proficient">Proficient</option>
                          <option value="expert">Expert</option>
                        </select>
                        <Btn small label="Confirm" color="#16A34A" bg="rgba(22,163,74,0.1)" border="rgba(22,163,74,0.25)"
                          onClick={(e) => { e.stopPropagation(); reviewCluster(c.id, "approved", null, approveExpertise); }} />
                        <Btn small label="Cancel" color="#7AAABB" bg="transparent" border="rgba(90,144,168,0.2)"
                          onClick={(e) => { e.stopPropagation(); setApprovingClusterId(null); }} />
                      </>
                    )}
                    {c.approval_status !== "rejected" && approvingClusterId !== c.id && (
                      <Btn small label="Reject" color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)"
                        onClick={(e) => { e.stopPropagation(); openRejectModal("Reject Cluster", (r) => reviewCluster(c.id, "rejected", r)); }} />
                    )}

                    {/* Expand chevron */}
                    <div style={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9BBCCC" strokeWidth="2.2" strokeLinecap="round" style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0)", transition: "transform 220ms ease" }}>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </div>
                  </div>
                </div>

                {/* ── Expanded detail panel ── */}
                {isExpanded && (
                  <div style={{ borderTop: "1px solid rgba(90,144,168,0.15)", background: "rgba(90,144,168,0.02)" }}>

                    {/* ── Cluster Details ── */}
                    <div style={{ padding: "16px 18px 0" }}>

                      {/* Sub-header: user-facing name + subtitle */}
                      {(cl?.user_facing_name || cl?.user_subtitle || cl?.score_full_name) && (
                        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
                          {cl.user_facing_name && <span style={{ fontSize: 14, fontWeight: 700, color: "#2D5A72", fontFamily: "Inter, sans-serif" }}>{cl.user_facing_name}</span>}
                          {cl.score_full_name && <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>({cl.score_full_name})</span>}
                          {cl.user_subtitle && <span style={{ fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>— {cl.user_subtitle}</span>}
                        </div>
                      )}

                      {/* Top grid: Cluster Info + Assignment Info */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12, marginBottom: 14 }}>

                        {/* Cluster Info card */}
                        <div style={{ background: "#FFFFFF", borderRadius: 10, padding: "14px 16px", border: "1px solid rgba(90,144,168,0.15)" }}>
                          <p style={{ margin: "0 0 10px", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Cluster Info</p>
                          {[
                            { label: "Cluster ID",    val: cl?.cluster_id },
                            { label: "Code",          val: cl?.cluster_code },
                            { label: "Group",         val: cl?.cluster_group?.replace(/_/g, " ") },
                            { label: "Score Code",    val: cl?.score_code },
                            { label: "Tile Name",     val: cl?.tile_name },
                            { label: "Status",        val: cl?.status },
                            { label: "Display Order", val: cl?.display_order != null ? String(cl.display_order) : null },
                            { label: "Active",        val: cl?.is_active != null ? (cl.is_active ? "Yes" : "No") : null, color: cl?.is_active ? "#16A34A" : "#DC2626" },
                            { label: "Oversight",     val: cl?.physician_oversight || null },
                            { label: "Score Intent",  val: cl?.score_intent },
                            { label: "Biomarkers",    val: cl?.biomarkers_in_cluster != null ? `${cl.biomarkers_in_cluster} tracked` : null },
                            { label: "Lab Markers",   val: cl?.lab_or_vital_markers != null ? String(cl.lab_or_vital_markers) : null },
                            { label: "Required Lab",  val: cl?.required_lab_markers != null ? String(cl.required_lab_markers) : null },
                            { label: "Subj. Slot",    val: cl?.subjective_slot },
                          ].filter(({ val }) => val != null && val !== "").map(({ label, val, color }) => (
                            <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, padding: "6px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                              <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", flexShrink: 0, minWidth: 100 }}>{label}</span>
                              <span style={{ fontSize: 11, color: color || "#1C3545", fontFamily: "Inter, sans-serif", textAlign: "right", fontWeight: color ? 600 : 500, wordBreak: "break-word" }}>{val}</span>
                            </div>
                          ))}
                        </div>

                        {/* Assignment Info card */}
                        <div style={{ background: "#FFFFFF", borderRadius: 10, padding: "14px 16px", border: "1px solid rgba(90,144,168,0.15)" }}>
                          <p style={{ margin: "0 0 10px", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Assignment</p>
                          {[
                            { label: "Type",           val: c.claimed_by_doctor ? "Doctor Self-Selected" : "Admin Assigned", color: c.claimed_by_doctor ? "#5A90A8" : "#7C3AED" },
                            { label: "Status",         val: c.approval_status ? c.approval_status.charAt(0).toUpperCase() + c.approval_status.slice(1) : "Pending",
                              color: c.approval_status === "approved" ? "#16A34A" : c.approval_status === "rejected" ? "#DC2626" : "#B45309" },
                            { label: "Expertise",      val: c.expertise_level ? c.expertise_level.charAt(0).toUpperCase() + c.expertise_level.slice(1) : null },
                            { label: "Years",          val: c.years_in_cluster != null ? `${c.years_in_cluster} yr${c.years_in_cluster !== 1 ? "s" : ""}` : null },
                            { label: "Admin Approved", val: c.approved_by_longevityos ? "Yes" : "No", color: c.approved_by_longevityos ? "#16A34A" : "#9BBCCC" },
                            { label: "Approved On",    val: c.approved_at ? new Date(c.approved_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null },
                          ].filter(({ val }) => val != null).map(({ label, val, color }) => (
                            <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                              <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", flexShrink: 0 }}>{label}</span>
                              <span style={{ fontSize: 11, color: color || "#1C3545", fontFamily: "Inter, sans-serif", fontWeight: color ? 600 : 500 }}>{val}</span>
                            </div>
                          ))}
                          {c.cluster_notes_by_doctor && (
                            <div style={{ marginTop: 10 }}>
                              <p style={{ margin: "0 0 4px", fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Doctor's Statement</p>
                              <p style={{ margin: 0, fontSize: 11, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.6, fontStyle: "italic" }}>{c.cluster_notes_by_doctor}</p>
                            </div>
                          )}
                          {c.rejection_reason && (
                            <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 7, background: "rgba(220,38,38,0.05)", border: "1px solid rgba(220,38,38,0.2)" }}>
                              <p style={{ margin: "0 0 3px", fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#DC2626", fontFamily: "Inter, sans-serif" }}>Rejection</p>
                              <p style={{ margin: 0, fontSize: 11, color: "#DC2626", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>{c.rejection_reason}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      {cl?.description && (
                        <div style={{ background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
                          <p style={{ margin: "0 0 6px", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Description</p>
                          <p style={{ margin: 0, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.75 }}>{cl.description}</p>
                        </div>
                      )}

                      {/* Long text / string[] fields (score_bands handled separately below) */}
                      {[
                        { label: "Clinical Scope",       val: cl?.clinical_scope },
                        { label: "What Moves The Score", val: cl?.what_moves_the_score },
                        { label: "Specialist Type",      val: cl?.specialist_type },
                        { label: "User Persona",         val: cl?.user_persona },
                        { label: "Composite Formula",    val: cl?.composite_formula },
                      ].filter(({ val }) => val != null && val !== "").length > 0 && (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))", gap: 12, marginBottom: 14 }}>
                          {[
                            { label: "Clinical Scope",       val: cl?.clinical_scope },
                            { label: "What Moves The Score", val: cl?.what_moves_the_score },
                            { label: "Specialist Type",      val: cl?.specialist_type },
                            { label: "User Persona",         val: cl?.user_persona },
                            { label: "Composite Formula",    val: cl?.composite_formula },
                          ].filter(({ val }) => val != null && val !== "").map(({ label, val }) => {
                            const strVal = String(val);
                            const items = strVal.includes("\n") ? strVal.split("\n").filter(Boolean) : null;
                            return (
                              <div key={label} style={{ background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 10, padding: "14px 16px" }}>
                                <p style={{ margin: "0 0 8px", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{label}</p>
                                {items ? (
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {items.map((item) => (
                                      <span key={item} style={{ display: "inline-block", padding: "4px 11px", borderRadius: 999, background: "rgba(90,144,168,0.08)", border: "1px solid rgba(90,144,168,0.22)", color: "#5A90A8", fontSize: 12, fontFamily: "Inter, sans-serif", fontWeight: 500 }}>{item}</span>
                                    ))}
                                  </div>
                                ) : (
                                  <p style={{ margin: 0, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.75, whiteSpace: "pre-wrap" }}>{strVal}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Score Bands — structured display */}
                      {cl?.score_bands && (() => {
                        const bands = parseBands(cl.score_bands);
                        if (bands.length === 0) return null;
                        return (
                          <div style={{ background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
                            <p style={{ margin: "0 0 10px", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Score Bands</p>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                              {bands.map((b, i) => (
                                <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 8, background: "rgba(90,144,168,0.06)", border: "1px solid rgba(90,144,168,0.2)", fontFamily: "Inter, sans-serif" }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1C3545" }}>{b.label}</span>
                                  {(b.min !== "" && b.min != null) || (b.max !== "" && b.max != null) ? (
                                    <span style={{ fontSize: 11, color: "#7AAABB" }}>
                                      {b.min != null && b.min !== "" ? b.min : "—"}
                                      {" – "}
                                      {b.max != null && b.max !== "" ? b.max : "—"}
                                    </span>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Biomarkers section */}
                    <div style={{ padding: "0 18px 18px" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                        <p style={{ ...sectionHeading, marginBottom: 0, fontSize: 9 }}>
                          Biomarkers {!bioLoading && clusterBioMap[c.cluster_id] !== undefined ? `(${bms.length})` : bioLoading ? "(loading…)" : ""}
                        </p>
                      </div>

                      {bioLoading && (
                        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 0", color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontSize: 13 }}>
                          <div style={{ width: 18, height: 18, border: "2px solid rgba(90,144,168,0.2)", borderTopColor: "#8AAEBB", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                          Loading biomarkers…
                        </div>
                      )}

                      {!bioLoading && bms.length === 0 && clusterBioMap[c.cluster_id] !== undefined && (
                        <p style={{ margin: 0, fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic", padding: "8px 0" }}>No biomarkers defined for this cluster yet.</p>
                      )}

                      {!bioLoading && bms.length > 0 && (
                        <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid rgba(90,144,168,0.15)" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720, background: "#FFFFFF" }}>
                            <thead>
                              <tr style={{ background: "rgba(90,144,168,0.06)" }}>
                                {["Name", "Code", "Category", "Unit", "Ref Range", "Optimal Range", "Weight", "Mandatory", "Status"].map((h) => (
                                  <th key={h} style={{ padding: "9px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif", textAlign: "left", whiteSpace: "nowrap", borderBottom: "1px solid rgba(90,144,168,0.12)" }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {bms.map((b, idx) => (
                                <tr key={b.id} style={{ borderBottom: idx < bms.length - 1 ? "1px solid rgba(90,144,168,0.08)" : "none" }}>
                                  <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{b.biomarker_name}</td>
                                  <td style={{ padding: "10px 12px" }}><code style={{ fontSize: 11, color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "2px 6px", borderRadius: 4, fontFamily: "monospace" }}>{b.biomarker_code}</code></td>
                                  <td style={{ padding: "10px 12px", fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", textTransform: "capitalize" }}>{b.category}</td>
                                  <td style={{ padding: "10px 12px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{b.unit || "—"}</td>
                                  <td style={{ padding: "10px 12px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{fmtRange(b.reference_range_min, b.reference_range_max, b.unit, b.reference_range_min_op, b.reference_range_max_op)}</td>
                                  <td style={{ padding: "10px 12px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{fmtRange(b.optimal_range_min, b.optimal_range_max, b.unit, b.optimal_range_min_op, b.optimal_range_max_op)}</td>
                                  <td style={{ padding: "10px 12px", fontSize: 12, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{Number(b.default_weight).toFixed(2)}</td>
                                  <td style={{ padding: "10px 12px" }}>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: b.is_mandatory ? "#5A90A8" : "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{b.is_mandatory ? "Yes" : "No"}</span>
                                  </td>
                                  <td style={{ padding: "10px 12px" }}>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: b.is_active ? "#16A34A" : "#DC2626", fontFamily: "Inter, sans-serif" }}>{b.is_active ? "Active" : "Inactive"}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Biomarker Customisation Requests tab */}
      {tab === "biomarkers" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
              {customisations.length} request{customisations.length !== 1 ? "s" : ""}
              {customisations.filter((c) => c.status === "pending").length > 0 && (
                <span style={{ marginLeft: 8, padding: "2px 9px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: "rgba(180,83,9,0.1)", color: "#B45309", border: "1px solid rgba(180,83,9,0.25)", fontFamily: "Inter, sans-serif" }}>
                  {customisations.filter((c) => c.status === "pending").length} pending
                </span>
              )}
            </span>
          </div>

          {bmLoading && <Spinner />}

          {!bmLoading && customisations.length === 0 && (
            <p style={{ ...labelTxt, textAlign: "center", padding: 32 }}>No biomarker customisation requests yet.</p>
          )}

          {!bmLoading && customisations.map((req) => {
            const bm        = biomarkerMap[req.biomarker_id];
            const bmName    = bm?.biomarker_name  || req.biomarker_id.slice(0, 8) + "…";
            const bmCode    = bm?.biomarker_code;
            const clInfo    = clusters.find((c) => c.cluster_id === req.cluster_id);
            const clName    = clInfo?.cluster?.cluster_name || req.cluster_id.slice(0, 8) + "…";
            const isPending = req.status === "pending";
            const isReviewing = reviewingId === req.id;
            const isExpanded = expandedIds.has(req.id) || isReviewing;

            const CUST_STATUS_MAP = {
              pending:            { label: "Pending",  color: "#B45309", bg: "rgba(180,83,9,0.1)",   border: "rgba(180,83,9,0.25)"   },
              approved:           { label: "Approved", color: "#16A34A", bg: "rgba(22,163,74,0.1)",  border: "rgba(22,163,74,0.25)"  },
              rejected:           { label: "Rejected", color: "#DC2626", bg: "rgba(220,38,38,0.1)",  border: "rgba(220,38,38,0.25)"  },
              partially_approved: { label: "Partial",  color: "#7C3AED", bg: "rgba(124,58,237,0.1)", border: "rgba(124,58,237,0.25)" },
            };
            const ACTION_LBL = { adjust_weight: "Adjust Weight", change_range: "Change Range", add_biomarker: "Add Biomarker", remove_biomarker: "Remove" };
            const st = CUST_STATUS_MAP[req.status] ?? CUST_STATUS_MAP.pending;
            const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

            return (
              <div key={req.id} style={{ ...card, overflow: "hidden", border: `1px solid ${isPending ? "rgba(180,83,9,0.35)" : st.border}` }}>

                {/* ── Card header ── */}
                <div
                  onClick={(e) => { e.stopPropagation(); toggleExpanded(req.id); }}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderBottom: isExpanded ? `1px solid ${st.border}` : "none", flexWrap: "wrap", background: st.bg, cursor: "pointer", userSelect: "none" }}
                >
                  {/* Status colour stripe */}
                  <div style={{ width: 4, alignSelf: "stretch", borderRadius: 4, background: st.color, flexShrink: 0, minHeight: 36 }} />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{bmName}</span>
                      {bmCode && (
                        <code style={{ fontSize: 11, color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "1px 6px", borderRadius: 4, fontFamily: "monospace" }}>{bmCode}</code>
                      )}
                      {isPending && (
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 999, background: "rgba(180,83,9,0.12)", color: "#B45309", border: "1px solid rgba(180,83,9,0.3)", fontFamily: "Inter, sans-serif" }}>
                          Needs Review
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                      Cluster: <strong style={{ color: "#4D7A8E" }}>{clName}</strong>
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
                    <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", background: "rgba(90,144,168,0.1)", color: "#5A90A8", border: "1px solid rgba(90,144,168,0.25)" }}>
                      {ACTION_LBL[req.action] || req.action}
                    </span>
                    <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", color: st.color, background: "rgba(255,255,255,0.7)", border: `1px solid ${st.border}` }}>
                      {st.label}
                    </span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#9BBCCC" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0, transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 200ms ease" }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>

                {isExpanded && (<>

                {/* ── Detail grid ── */}
                <div style={{ padding: "14px 18px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20 }}>

                  {/* Value comparison — current vs proposed */}
                  <div>
                    <p style={{ margin: "0 0 8px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Value Comparison</p>
                    {(() => {
                      const rows = [
                        {
                          label:    "Weight",
                          current:  bm ? Number(bm.default_weight).toFixed(3) : null,
                          proposed: req.proposed_weight != null ? Number(req.proposed_weight).toFixed(3) : null,
                        },
                        {
                          label:    "Optimal Min",
                          current:  bm?.optimal_range_min != null ? String(bm.optimal_range_min) : null,
                          proposed: req.proposed_optimal_min != null ? String(req.proposed_optimal_min) : null,
                        },
                        {
                          label:    "Optimal Max",
                          current:  bm?.optimal_range_max != null ? String(bm.optimal_range_max) : null,
                          proposed: req.proposed_optimal_max != null ? String(req.proposed_optimal_max) : null,
                        },
                        {
                          label:    "Ref Range",
                          current:  bm ? fmtRange(bm.reference_range_min, bm.reference_range_max, bm.unit, bm.reference_range_min_op, bm.reference_range_max_op) : null,
                          proposed: null,
                        },
                      ].filter((r) => r.current !== null || r.proposed !== null);

                      if (rows.length === 0)
                        return <span style={{ ...labelTxt, fontStyle: "italic" }}>No numeric changes</span>;

                      return rows.map((r, i) => (
                        <div key={r.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "7px 0", borderBottom: i < rows.length - 1 ? "1px solid rgba(90,144,168,0.07)" : "none" }}>
                          <span style={{ ...labelTxt, flexShrink: 0 }}>{r.label}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            {/* Current value — muted, strikethrough if being replaced */}
                            <span style={{ fontSize: 12, color: r.proposed ? "#9BBCCC" : "#1C3545", fontFamily: "Inter, sans-serif", textDecoration: r.proposed ? "line-through" : "none", fontWeight: r.proposed ? 400 : 500 }}>
                              {r.current ?? "—"}
                            </span>
                            {/* Arrow + highlighted proposed value */}
                            {r.proposed && (<>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#92400E", background: "rgba(251,191,36,0.2)", border: "1px solid rgba(217,119,6,0.4)", padding: "1px 8px", borderRadius: 6, fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>
                                {r.proposed}
                              </span>
                            </>)}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>

                  {/* Timeline + Metadata */}
                  <div>
                    <p style={{ margin: "0 0 8px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Timeline</p>
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                      <span style={labelTxt}>Submitted</span>
                      <span style={valueTxt}>{fmtDate(req.submitted_at)}</span>
                    </div>
                    {req.reviewed_at && (
                      <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                        <span style={labelTxt}>Reviewed</span>
                        <span style={valueTxt}>{fmtDate(req.reviewed_at)}</span>
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
                      <span style={labelTxt}>Status</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: st.color, fontFamily: "Inter, sans-serif" }}>{st.label}</span>
                    </div>
                    {bm && (
                      <div style={{ marginTop: 10 }}>
                        <p style={{ margin: "0 0 6px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Biomarker Info</p>
                        <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                          <span style={labelTxt}>Category</span>
                          <span style={{ ...valueTxt, textTransform: "capitalize" }}>{bm.category}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                          <span style={labelTxt}>Unit</span>
                          <span style={valueTxt}>{bm.unit || "—"}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                          <span style={labelTxt}>Mandatory</span>
                          <span style={{ ...valueTxt, color: bm.is_mandatory ? "#5A90A8" : "#9BBCCC" }}>{bm.is_mandatory ? "Yes" : "No"}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Clinical reason + supporting reference ── */}
                {(req.reason || req.supporting_reference) && (
                  <div style={{ padding: "12px 18px", borderTop: "1px solid rgba(90,144,168,0.1)", display: "grid", gridTemplateColumns: req.reason && req.supporting_reference ? "1fr 1fr" : "1fr", gap: 16, background: "rgba(90,144,168,0.02)" }}>
                    {req.reason && (
                      <div>
                        <p style={{ margin: "0 0 5px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Clinical Reason</p>
                        <p style={{ margin: 0, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.65 }}>{req.reason}</p>
                      </div>
                    )}
                    {req.supporting_reference && (
                      <div>
                        <p style={{ margin: "0 0 5px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Supporting Reference</p>
                        <p style={{ margin: 0, fontSize: 12, color: "#4D7A8E", fontFamily: "monospace", wordBreak: "break-all" }}>{req.supporting_reference}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Existing admin notes (post-review) ── */}
                {req.admin_notes && !isReviewing && (
                  <div style={{ padding: "10px 18px", borderTop: "1px solid rgba(90,144,168,0.1)", background: "rgba(29,78,216,0.03)" }}>
                    <p style={{ margin: "0 0 4px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#1D4ED8", fontFamily: "Inter, sans-serif" }}>Admin Notes</p>
                    <p style={{ margin: 0, fontSize: 12, color: "#1D4ED8", fontFamily: "Inter, sans-serif", lineHeight: 1.6, fontStyle: "italic" }}>{req.admin_notes}</p>
                  </div>
                )}

                {/* ── Review panel (pending only) ── */}
                {isPending && !isReviewing && (
                  <div style={{ padding: "12px 18px", borderTop: "1px solid rgba(90,144,168,0.1)", display: "flex", gap: 8, alignItems: "center" }}>
                    <Btn small label="Approve" color="#16A34A" bg="rgba(22,163,74,0.1)" border="rgba(22,163,74,0.25)"
                      onClick={(e) => { e.stopPropagation(); setReviewingId(req.id); setReviewNotes(""); }} />
                    <Btn small label="Reject" color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)"
                      onClick={(e) => { e.stopPropagation(); setReviewingId(req.id); setReviewNotes(""); }} />
                  </div>
                )}

                {isReviewing && (
                  <div style={{ padding: "14px 18px", borderTop: "1px solid rgba(90,144,168,0.1)", background: "rgba(90,144,168,0.03)" }}>
                    <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>Admin Notes (optional)</p>
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Add a note visible to the doctor…"
                      rows={2}
                      style={{ ...inputBase, display: "block", width: "100%", resize: "vertical", marginBottom: 12 }}
                    />
                    <div style={{ display: "flex", gap: 8 }}>
                      <Btn label={reviewSaving ? "Saving…" : "Approve"} disabled={reviewSaving}
                        color="#16A34A" bg="rgba(22,163,74,0.1)" border="rgba(22,163,74,0.25)"
                        onClick={(e) => { e.stopPropagation(); reviewCustomisation(req.id, "approved"); }} />
                      <Btn label={reviewSaving ? "Saving…" : "Reject"} disabled={reviewSaving}
                        color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)"
                        onClick={(e) => { e.stopPropagation(); reviewCustomisation(req.id, "rejected"); }} />
                      <Btn label="Cancel" disabled={reviewSaving}
                        color="#9BBCCC" bg="rgba(90,144,168,0.04)" border="rgba(90,144,168,0.15)"
                        onClick={(e) => { e.stopPropagation(); setReviewingId(null); setReviewNotes(""); }} />
                    </div>
                  </div>
                )}

                {/* ── Already reviewed actions (re-review) ── */}
                {!isPending && !isReviewing && (
                  <div style={{ padding: "10px 18px", borderTop: "1px solid rgba(90,144,168,0.1)", display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif", flex: 1 }}>Already reviewed</span>
                    <Btn small label="Re-review" color="#5A90A8" bg="rgba(90,144,168,0.08)" border="rgba(90,144,168,0.25)"
                      onClick={(e) => { e.stopPropagation(); setReviewingId(req.id); setReviewNotes(req.admin_notes || ""); }} />
                  </div>
                )}

                </>)}
              </div>
            );
          })}

          {/* ── Custom Biomarker Proposals ── */}
          {!bmLoading && (() => {
          const CB_STATUS_MAP = {
            pending:  { label: "Pending",  color: "#B45309", bg: "rgba(180,83,9,0.1)",   border: "rgba(180,83,9,0.25)"   },
            approved: { label: "Approved", color: "#16A34A", bg: "rgba(22,163,74,0.1)",  border: "rgba(22,163,74,0.25)"  },
            rejected: { label: "Rejected", color: "#DC2626", bg: "rgba(220,38,38,0.1)",  border: "rgba(220,38,38,0.25)"  },
          };
          return (
            <div style={{ marginTop: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Custom Biomarker Proposals</span>
                {customBiomarkers.filter((b) => b.approval_status === "pending").length > 0 && (
                  <span style={{ padding: "2px 9px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: "rgba(180,83,9,0.1)", color: "#B45309", border: "1px solid rgba(180,83,9,0.25)", fontFamily: "Inter, sans-serif" }}>
                    {customBiomarkers.filter((b) => b.approval_status === "pending").length} pending
                  </span>
                )}
              </div>
              {customBiomarkers.length === 0 && (
                <p style={{ ...labelTxt, fontStyle: "italic", padding: "12px 0" }}>No custom biomarker proposals from this doctor.</p>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {customBiomarkers.map((cb) => {
                  const st = CB_STATUS_MAP[cb.approval_status] ?? CB_STATUS_MAP.pending;
                  const isPending = cb.approval_status === "pending";
                  const isReviewing = cbReviewingId === cb.id;
                  const isExpanded = cbExpandedIds.has(cb.id) || isReviewing;
                  const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
                  const refRange = fmtRange(cb.reference_range_min, cb.reference_range_max, cb.unit, cb.reference_range_min_op, cb.reference_range_max_op);
                  const optRange = fmtRange(cb.optimal_range_min, cb.optimal_range_max, cb.unit, cb.optimal_range_min_op, cb.optimal_range_max_op);
                  return (
                    <div key={cb.id} style={{ ...card, overflow: "hidden", border: `1px solid ${isPending ? "rgba(180,83,9,0.35)" : st.border}` }}>

                      {/* ── Card header (clickable) ── */}
                      <div
                        onClick={(e) => { e.stopPropagation(); toggleCbExpanded(cb.id); }}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderBottom: isExpanded ? `1px solid ${st.border}` : "none", flexWrap: "wrap", background: st.bg, cursor: "pointer", userSelect: "none" }}
                      >
                        <div style={{ width: 4, alignSelf: "stretch", borderRadius: 4, background: st.color, flexShrink: 0, minHeight: 36 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{cb.biomarker_name}</span>
                            <code style={{ fontSize: 11, color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "1px 6px", borderRadius: 4, fontFamily: "monospace" }}>{cb.biomarker_code}</code>
                            {isPending && (
                              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 999, background: "rgba(180,83,9,0.12)", color: "#B45309", border: "1px solid rgba(180,83,9,0.3)", fontFamily: "Inter, sans-serif" }}>
                                Needs Review
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                            Cluster: <strong style={{ color: "#4D7A8E" }}>{cb.cluster?.cluster_name || cb.cluster_id}</strong>
                            {cb.cluster?.cluster_code && <code style={{ marginLeft: 6, fontSize: 10, color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "1px 5px", borderRadius: 3, fontFamily: "monospace" }}>{cb.cluster.cluster_code}</code>}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
                          <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", background: "rgba(90,144,168,0.1)", color: "#5A90A8", border: "1px solid rgba(90,144,168,0.25)" }}>
                            New Biomarker
                          </span>
                          <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", color: st.color, background: "rgba(255,255,255,0.7)", border: `1px solid ${st.border}` }}>
                            {st.label}
                          </span>
                          <svg viewBox="0 0 24 24" fill="none" stroke="#9BBCCC" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0, transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 200ms ease" }}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </div>

                      {isExpanded && (<>

                      {/* ── Detail grid ── */}
                      <div style={{ padding: "14px 18px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20 }}>

                        {/* Biomarker Info */}
                        <div>
                          <p style={{ margin: "0 0 8px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Biomarker Info</p>
                          {[
                            { label: "Category",   val: cb.category,                          capitalize: true },
                            { label: "Unit",        val: cb.unit || "—" },
                            { label: "Weight",      val: Number(cb.proposed_weight).toFixed(3) },
                            { label: "Ref Range",   val: refRange !== "—" ? refRange : null },
                            { label: "Opt Range",   val: optRange !== "—" ? optRange : null },
                          ].filter((r) => r.val !== null).map((r, i, arr) => (
                            <div key={r.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "7px 0", borderBottom: i < arr.length - 1 ? "1px solid rgba(90,144,168,0.07)" : "none" }}>
                              <span style={labelTxt}>{r.label}</span>
                              <span style={{ fontSize: 12, fontWeight: 500, color: "#1C3545", fontFamily: "Inter, sans-serif", textTransform: r.capitalize ? "capitalize" : "none" }}>{r.val}</span>
                            </div>
                          ))}
                        </div>

                        {/* Timeline */}
                        <div>
                          <p style={{ margin: "0 0 8px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Timeline</p>
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                            <span style={labelTxt}>Submitted</span>
                            <span style={valueTxt}>{fmtDate(cb.created_at)}</span>
                          </div>
                          {cb.reviewed_at && (
                            <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                              <span style={labelTxt}>Reviewed</span>
                              <span style={valueTxt}>{fmtDate(cb.reviewed_at)}</span>
                            </div>
                          )}
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
                            <span style={labelTxt}>Status</span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: st.color, fontFamily: "Inter, sans-serif" }}>{st.label}</span>
                          </div>
                        </div>
                      </div>

                      {/* ── Clinical reason + supporting reference ── */}
                      {(cb.reason || cb.supporting_reference) && (
                        <div style={{ padding: "12px 18px", borderTop: "1px solid rgba(90,144,168,0.1)", display: "grid", gridTemplateColumns: cb.reason && cb.supporting_reference ? "1fr 1fr" : "1fr", gap: 16, background: "rgba(90,144,168,0.02)" }}>
                          {cb.reason && (
                            <div>
                              <p style={{ margin: "0 0 5px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Clinical Reason</p>
                              <p style={{ margin: 0, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.65 }}>{cb.reason}</p>
                            </div>
                          )}
                          {cb.supporting_reference && (
                            <div>
                              <p style={{ margin: "0 0 5px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Supporting Reference</p>
                              <p style={{ margin: 0, fontSize: 12, color: "#4D7A8E", fontFamily: "monospace", wordBreak: "break-all" }}>{cb.supporting_reference}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Existing admin notes (post-review) ── */}
                      {cb.admin_notes && !isReviewing && (
                        <div style={{ padding: "10px 18px", borderTop: "1px solid rgba(90,144,168,0.1)", background: "rgba(29,78,216,0.03)" }}>
                          <p style={{ margin: "0 0 4px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#1D4ED8", fontFamily: "Inter, sans-serif" }}>Admin Notes</p>
                          <p style={{ margin: 0, fontSize: 12, color: "#1D4ED8", fontFamily: "Inter, sans-serif", lineHeight: 1.6, fontStyle: "italic" }}>{cb.admin_notes}</p>
                        </div>
                      )}

                      {/* ── Review panel (pending only) ── */}
                      {isPending && !isReviewing && (
                        <div style={{ padding: "12px 18px", borderTop: "1px solid rgba(90,144,168,0.1)", display: "flex", gap: 8, alignItems: "center" }}>
                          <Btn small label="Approve" color="#16A34A" bg="rgba(22,163,74,0.1)" border="rgba(22,163,74,0.25)"
                            onClick={(e) => { e.stopPropagation(); setCbReviewingId(cb.id); setCbReviewNotes(""); }} />
                          <Btn small label="Reject" color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)"
                            onClick={(e) => { e.stopPropagation(); setCbReviewingId(cb.id); setCbReviewNotes(""); }} />
                        </div>
                      )}

                      {/* ── Review form ── */}
                      {isReviewing && (
                        <div style={{ padding: "14px 18px", borderTop: "1px solid rgba(90,144,168,0.1)", background: "rgba(90,144,168,0.03)" }}>
                          <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>Admin Notes (optional)</p>
                          <textarea
                            value={cbReviewNotes}
                            onChange={(e) => setCbReviewNotes(e.target.value)}
                            placeholder="Add a note visible to the doctor…"
                            rows={2}
                            style={{ ...inputBase, display: "block", width: "100%", resize: "vertical", marginBottom: 12 }}
                          />
                          <div style={{ display: "flex", gap: 8 }}>
                            <Btn label={cbReviewSaving ? "Saving…" : "Approve"} disabled={cbReviewSaving}
                              color="#16A34A" bg="rgba(22,163,74,0.1)" border="rgba(22,163,74,0.25)"
                              onClick={(e) => { e.stopPropagation(); reviewCustomBiomarker(cb.id, "approved"); }} />
                            <Btn label={cbReviewSaving ? "Saving…" : "Reject"} disabled={cbReviewSaving}
                              color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)"
                              onClick={(e) => { e.stopPropagation(); reviewCustomBiomarker(cb.id, "rejected"); }} />
                            <Btn label="Cancel" disabled={cbReviewSaving}
                              color="#9BBCCC" bg="rgba(90,144,168,0.04)" border="rgba(90,144,168,0.15)"
                              onClick={(e) => { e.stopPropagation(); setCbReviewingId(null); setCbReviewNotes(""); }} />
                          </div>
                        </div>
                      )}

                      {/* ── Already reviewed / Re-review ── */}
                      {!isPending && !isReviewing && (
                        <div style={{ padding: "10px 18px", borderTop: "1px solid rgba(90,144,168,0.1)", display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif", flex: 1 }}>Already reviewed</span>
                          <Btn small label="Re-review" color="#5A90A8" bg="rgba(90,144,168,0.08)" border="rgba(90,144,168,0.25)"
                            onClick={(e) => { e.stopPropagation(); setCbReviewingId(cb.id); setCbReviewNotes(cb.admin_notes || ""); }} />
                        </div>
                      )}

                      </>)}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
        </div>
      )}

      {/* Custom Assessment Questions tab */}
      {tab === "questions" && (() => {
        const qStatusStyle = {
          pending:  { color: "#B45309", bg: "rgba(180,83,9,0.1)",  border: "rgba(180,83,9,0.25)"  },
          approved: { color: "#16A34A", bg: "rgba(22,163,74,0.1)", border: "rgba(22,163,74,0.25)" },
          rejected: { color: "#DC2626", bg: "rgba(220,38,38,0.1)", border: "rgba(220,38,38,0.25)" },
        };
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p style={{ ...labelTxt, marginBottom: 4 }}>{customQuestions.length} custom question{customQuestions.length !== 1 ? "s" : ""} proposed by doctor</p>
            {customQuestions.length === 0 && (
              <div style={{ ...card, padding: 32, textAlign: "center" }}>
                <p style={{ color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13, margin: 0 }}>No custom assessment questions yet.</p>
              </div>
            )}
            {customQuestions.map((q) => {
              const st = qStatusStyle[q.approval_status] || qStatusStyle.pending;
              const isReviewing = qReviewId === q.id;
              return (
                <div key={q.id} style={{ ...card, padding: "16px 20px" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      {/* Cluster badge */}
                      {q.cluster && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Cluster</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#2D5A72", fontFamily: "Inter, sans-serif" }}>{q.cluster.cluster_name}</span>
                          <code style={{ fontSize: 10, color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "1px 6px", borderRadius: 4, fontFamily: "monospace" }}>{q.cluster.cluster_code}</code>
                          <span style={{ fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "capitalize" }}>{q.cluster.cluster_group?.replace(/_/g, " ")}</span>
                        </div>
                      )}
                      <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{q.question_text}</p>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Type: <strong>{q.question_type}</strong></span>
                        {q.maps_to_score_dimension && <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Dimension: <strong>{q.maps_to_score_dimension}</strong></span>}
                        {q.proposed_weight != null && <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Weight: <strong>{q.proposed_weight}</strong></span>}
                      </div>
                      {q.options?.length > 0 && (
                        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {q.options.map((opt, i) => (
                            <span key={i} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(90,144,168,0.08)", color: "#4D7A8E", fontFamily: "Inter, sans-serif", border: "1px solid rgba(90,144,168,0.15)" }}>{opt}</span>
                          ))}
                        </div>
                      )}
                      {q.admin_notes && <p style={{ margin: "6px 0 0", fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Admin notes: {q.admin_notes}</p>}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 999, whiteSpace: "nowrap", color: st.color, background: st.bg, border: `1px solid ${st.border}`, fontFamily: "Inter, sans-serif" }}>
                      {(q.approval_status || "pending").replace(/_/g, " ")}
                    </span>
                  </div>
                  {!isReviewing && (
                    <Btn small label="Review" color="#5A90A8" bg="rgba(90,144,168,0.1)" border="rgba(90,144,168,0.25)"
                      onClick={() => { setQReviewId(q.id); setQReviewStatus(q.approval_status === "approved" ? "rejected" : "approved"); setQReviewNotes(""); }} />
                  )}
                  {isReviewing && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                      <div style={{ display: "flex", gap: 8 }}>
                        {["approved", "rejected"].map((s) => (
                          <button key={s} onClick={() => setQReviewStatus(s)}
                            style={{ padding: "5px 14px", borderRadius: 8, border: `1px solid ${qReviewStatus === s ? (s === "approved" ? "#16A34A" : "#DC2626") : "rgba(90,144,168,0.2)"}`, background: qReviewStatus === s ? (s === "approved" ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.08)") : "transparent", color: qReviewStatus === s ? (s === "approved" ? "#16A34A" : "#DC2626") : "#7AAABB", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", textTransform: "capitalize" }}>{s}</button>
                        ))}
                      </div>
                      <input value={qReviewNotes} onChange={(e) => setQReviewNotes(e.target.value)} placeholder="Admin notes (optional)…" style={{ ...inputBase, fontSize: 12, padding: "6px 10px" }} />
                      <div style={{ display: "flex", gap: 8 }}>
                        <Btn small label={qReviewSaving ? "Saving…" : "Submit"} color="#FFFFFF" bg="#5A90A8" border="#5A90A8" onClick={reviewCustomQuestion} disabled={qReviewSaving} />
                        <Btn small label="Cancel" color="#7AAABB" bg="transparent" border="rgba(90,144,168,0.2)" onClick={() => setQReviewId(null)} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Programs tab */}
      {tab === "programs" && (() => {
        const PROG_STATUS_MAP = {
          draft:          { label: "Draft",          color: "#9BBCCC", bg: "rgba(90,144,168,0.06)",  border: "rgba(90,144,168,0.15)"  },
          draft:          { label: "Draft",         color: "#6B7280", bg: "rgba(107,114,128,0.08)", border: "rgba(107,114,128,0.2)"  },
          pending_review: { label: "Pending Review", color: "#B45309", bg: "rgba(180,83,9,0.1)",     border: "rgba(180,83,9,0.25)"    },
          approved:       { label: "Approved",       color: "#16A34A", bg: "rgba(22,163,74,0.1)",    border: "rgba(22,163,74,0.25)"   },
          rejected:       { label: "Rejected",       color: "#DC2626", bg: "rgba(220,38,38,0.1)",    border: "rgba(220,38,38,0.25)"   },
          live:           { label: "Live",           color: "#16A34A", bg: "rgba(22,163,74,0.12)",   border: "rgba(22,163,74,0.3)"    },
          paused:         { label: "Paused",         color: "#B45309", bg: "rgba(180,83,9,0.08)",    border: "rgba(180,83,9,0.22)"    },
          archived:       { label: "Archived",       color: "#6B7280", bg: "rgba(107,114,128,0.08)", border: "rgba(107,114,128,0.2)"  },
        };
        const PROG_STATUS_DEFAULT = { label: "Unknown", color: "#6B7280", bg: "rgba(107,114,128,0.08)", border: "rgba(107,114,128,0.2)" };
        const MODE_LBL  = { in_person: "In-Person", video: "Video / Telehealth", async: "Async / Self-paced" };
        const FREQ_LBL  = { daily: "Daily", twice_weekly: "Twice/week", weekly: "Weekly", biweekly: "Biweekly", monthly: "Monthly" };
        const PRICE_LBL = { fixed: "Fixed", subscription: "Subscription", pay_per_session: "Pay-per-Session", custom: "Custom" };
        const INCL_LBL  = { lab_panel: "Lab Panel", supplement: "Supplement", therapy: "Therapy", consultation: "Consultation", wearable_review: "Wearable Review", care_plan: "Care Plan", follow_up: "Follow-Up", report: "Report", other: "Other" };

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {programs.length === 0 && <p style={{ ...labelTxt, textAlign: "center", padding: 32 }}>No programs submitted.</p>}
            {programs.map((p) => {
              const isExpanded = expandedProgId === p.id;
              const st = PROG_STATUS_MAP[p.status] || PROG_STATUS_DEFAULT;
              const inclusions = progInclusionsMap[p.id] || [];
              const inclLoading = inclLoadingSet.has(p.id);

              return (
                <div key={p.id} style={{ ...card, padding: 0, overflow: "hidden", border: `1px solid ${isExpanded ? "rgba(90,144,168,0.3)" : "rgba(90,144,168,0.18)"}` }}>

                  {/* ── Card header (always visible, clickable to expand) ── */}
                  <div
                    onClick={() => toggleProgExpand(p.id)}
                    style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", cursor: "pointer", userSelect: "none", borderBottom: isExpanded ? "1px solid rgba(90,144,168,0.12)" : "none", background: isExpanded ? "rgba(90,144,168,0.03)" : "transparent" }}
                  >
                    {/* Status stripe */}
                    <div style={{ width: 4, alignSelf: "stretch", borderRadius: 4, background: st.color, flexShrink: 0 }} />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: "0 0 2px", fontSize: 14, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{p.program_name}</p>
                      <p style={{ ...labelTxt, margin: 0 }}>
                        {[p.duration_weeks && `${p.duration_weeks} wks`, p.total_sessions && `${p.total_sessions} sessions`, p.price_inr != null && `₹${p.price_inr.toLocaleString()}`].filter(Boolean).join(" · ") || "No details yet"}
                      </p>
                    </div>

                    <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", color: st.color, background: st.bg, border: `1px solid ${st.border}`, whiteSpace: "nowrap" }}>{st.label}</span>

                    <div style={{ display: "flex", gap: 8 }} onClick={(e) => e.stopPropagation()}>
                      {p.status === "pending_review" && <>
                        <Btn small label="Approve" color="#16A34A" bg="rgba(22,163,74,0.1)" border="rgba(22,163,74,0.25)" onClick={() => reviewProgram(p.id, "approved")} />
                        <Btn small label="Reject"  color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)" onClick={() => openRejectModal("Reject Program", (r) => reviewProgram(p.id, "rejected", r))} />
                      </>}
                    </div>

                    <svg viewBox="0 0 24 24" fill="none" stroke="#9BBCCC" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0, transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 200ms ease" }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>

                  {/* ── Expanded detail body ── */}
                  {isExpanded && (
                    <div style={{ padding: "20px 22px 22px", display: "flex", flexDirection: "column", gap: 20 }}>

                      {/* Tagline */}
                      {p.program_tagline && (
                        <p style={{ margin: 0, fontSize: 13, color: "#5A90A8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>"{p.program_tagline}"</p>
                      )}

                      {/* Description */}
                      {p.program_description && (
                        <div>
                          <p style={{ ...labelTxt, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>Description</p>
                          <p style={{ margin: 0, fontSize: 13, color: "#2D5A72", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>{p.program_description}</p>
                        </div>
                      )}

                      {/* Info grid */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
                        {[
                          ["Duration",          p.duration_weeks    ? `${p.duration_weeks} weeks`    : null],
                          ["Total Sessions",    p.total_sessions    ? `${p.total_sessions} sessions`  : null],
                          ["Session Frequency", p.session_frequency ? (FREQ_LBL[p.session_frequency] || p.session_frequency) : null],
                          ["Max Patients",      p.max_patients      ? String(p.max_patients)           : null],
                          ["Pricing Model",     p.pricing_model     ? (PRICE_LBL[p.pricing_model] || p.pricing_model) : null],
                          ["Price (INR)",       p.price_inr != null ? `₹${p.price_inr.toLocaleString()}` : null],
                          ["Price (USD)",       p.price_usd != null ? `$${p.price_usd.toLocaleString()}` : null],
                        ].filter(([, v]) => v).map(([label, value]) => (
                          <div key={label} style={{ background: "rgba(90,144,168,0.04)", borderRadius: 10, padding: "10px 14px", border: "1px solid rgba(90,144,168,0.12)" }}>
                            <p style={{ margin: "0 0 2px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{label}</p>
                            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{value}</p>
                          </div>
                        ))}
                      </div>

                      {/* Delivery mode */}
                      {p.mode?.length > 0 && (
                        <div>
                          <p style={{ ...labelTxt, marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>Delivery Mode</p>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {p.mode.map((m) => <span key={m} style={{ padding: "4px 12px", borderRadius: 999, fontSize: 12, fontFamily: "Inter, sans-serif", color: "#2D5A72", background: "rgba(90,144,168,0.12)", border: "1px solid rgba(90,144,168,0.25)" }}>{MODE_LBL[m] || m}</span>)}
                          </div>
                        </div>
                      )}

                      {/* Target profile */}
                      {p.target_profile && (
                        <div>
                          <p style={{ ...labelTxt, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>Target Patient Profile</p>
                          <p style={{ margin: 0, fontSize: 13, color: "#2D5A72", fontFamily: "Inter, sans-serif", lineHeight: 1.55 }}>{p.target_profile}</p>
                        </div>
                      )}

                      {/* Expected outcomes */}
                      {p.expected_outcomes?.length > 0 && (
                        <div>
                          <p style={{ ...labelTxt, marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>Expected Outcomes</p>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {p.expected_outcomes.map((o, i) => (
                              <span key={i} style={{ padding: "4px 12px", borderRadius: 999, fontSize: 12, fontFamily: "Inter, sans-serif", color: "#16A34A", background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.22)" }}>✓ {o}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Inclusions */}
                      <div>
                        <p style={{ ...labelTxt, marginBottom: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>What's Included</p>
                        {inclLoading && <p style={{ ...labelTxt, margin: 0 }}>Loading inclusions…</p>}
                        {!inclLoading && inclusions.length === 0 && <p style={{ ...labelTxt, margin: 0, fontStyle: "italic" }}>No inclusions listed.</p>}
                        {!inclLoading && inclusions.map((inc) => (
                          <div key={inc.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "9px 0", borderBottom: "1px solid rgba(90,144,168,0.1)" }}>
                            <span style={{ padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: "rgba(90,144,168,0.1)", color: "#5A90A8", border: "1px solid rgba(90,144,168,0.22)", fontFamily: "Inter, sans-serif", flexShrink: 0 }}>{INCL_LBL[inc.inclusion_type] || inc.inclusion_type}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{inc.inclusion_label}</span>
                                {inc.quantity > 1 && <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>×{inc.quantity}</span>}
                                <span style={{ fontSize: 11, fontFamily: "Inter, sans-serif", color: inc.is_included_in_price ? "#16A34A" : "#B45309" }}>
                                  {inc.is_included_in_price ? "Included in price" : `+₹${inc.additional_cost || 0}`}
                                </span>
                              </div>
                              {inc.description && <p style={{ margin: "2px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{inc.description}</p>}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Admin action footer */}
                      {p.status === "pending_review" && (
                        <div style={{ display: "flex", gap: 10, paddingTop: 12, borderTop: "1px solid rgba(90,144,168,0.12)" }}>
                          <Btn label="Approve Program" color="#16A34A" bg="rgba(22,163,74,0.1)" border="rgba(22,163,74,0.25)" onClick={() => reviewProgram(p.id, "approved")} />
                          <Btn label="Reject Program"  color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)" onClick={() => openRejectModal("Reject Program", (r) => reviewProgram(p.id, "rejected", r))} />
                        </div>
                      )}
                      {(p.status === "approved" || p.status === "live") && (
                        <div style={{ paddingTop: 12, borderTop: "1px solid rgba(90,144,168,0.12)" }}>
                          <span style={{ fontSize: 12, color: "#16A34A", fontFamily: "Inter, sans-serif" }}>✓ {p.status === "live" ? "This program is live" : "Approved — awaiting doctor to go live"}</span>
                          {p.approved_at && <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", marginLeft: 12 }}>on {new Date(p.approved_at).toLocaleDateString()}</span>}
                        </div>
                      )}
                      {p.status === "rejected" && (
                        <div style={{ paddingTop: 12, borderTop: "1px solid rgba(220,38,38,0.15)" }}>
                          <span style={{ fontSize: 12, color: "#DC2626", fontFamily: "Inter, sans-serif" }}>This program was rejected.</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Checklist tab */}
      {tab === "checklist" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {checklist.length === 0 && <p style={{ ...labelTxt, textAlign: "center", padding: 32 }}>Checklist not generated yet.</p>}
          {checklist.map((item) => {
            const statusColor = { passed: "#16A34A", failed: "#DC2626", waived: "#B45309", pending: "#9BBCCC" };
            const note = checklistNotes[item.checklist_item] ?? "";
            return (
              <div key={item.id} style={{ ...card, padding: "14px 18px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", flexShrink: 0, background: statusColor[item.status] || "#9BBCCC" }} />
                  <p style={{ flex: 1, margin: 0, fontSize: 13, color: "#2D5A72", fontFamily: "Inter, sans-serif", textTransform: "capitalize" }}>{item.checklist_item?.replace(/_/g, " ")}</p>
                  <span style={{ fontSize: 12, fontWeight: 600, color: statusColor[item.status] || "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "capitalize" }}>{item.status}</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    {item.status !== "passed" && <Btn small label="Pass" color="#16A34A" bg="rgba(22,163,74,0.1)" border="rgba(22,163,74,0.25)" onClick={() => updateChecklist(item.checklist_item, "passed", note || null)} />}
                    {item.status !== "failed" && <Btn small label="Fail" color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)" onClick={() => updateChecklist(item.checklist_item, "failed", note || null)} />}
                    {item.status !== "waived" && <Btn small label="Waive" color="#B45309" bg="rgba(180,83,9,0.08)" border="rgba(180,83,9,0.25)" onClick={() => updateChecklist(item.checklist_item, "waived", note || null)} />}
                  </div>
                </div>
                <input placeholder="Optional note…" value={note} onChange={(e) => setChecklistNotes((prev) => ({ ...prev, [item.checklist_item]: e.target.value }))}
                  style={{ ...inputBase, marginTop: 8, fontSize: 12, padding: "6px 10px" }} />
                {item.notes && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Last note: {item.notes}</p>}
              </div>
            );
          })}
        </div>
      )}

      {/* Go-Live tab */}
      {tab === "golive" && (
        <div>
          <div style={{ ...card, padding: "22px 24px", marginBottom: 20 }}>
            <p style={sectionHeading}>Change Account Status</p>
            <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
              <input value={goLiveReason} onChange={(e) => setGoLiveReason(e.target.value)} placeholder="Reason (required)…" style={{ ...inputBase, flex: 1, minWidth: 200 }} />
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {[
                { event: "went_live",    label: "Set Live",    color: "#16A34A",  bg: "rgba(22,163,74,0.1)",     border: "rgba(22,163,74,0.25)"   },
                { event: "paused",       label: "Pause",       color: "#B45309",  bg: "rgba(180,83,9,0.08)",     border: "rgba(180,83,9,0.25)"    },
                { event: "suspended",    label: "Suspend",     color: "#DC2626",  bg: "rgba(220,38,38,0.08)",    border: "rgba(220,38,38,0.25)"   },
                { event: "reactivated",  label: "Reactivate",  color: "#1D4ED8",  bg: "rgba(29,78,216,0.08)",    border: "rgba(29,78,216,0.25)"   },
                { event: "rejected",     label: "Reject",      color: "#DC2626",  bg: "rgba(220,38,38,0.08)",    border: "rgba(220,38,38,0.25)"   },
                { event: "offboarded",   label: "Offboard",    color: "#6B7280",  bg: "rgba(107,114,128,0.08)",  border: "rgba(107,114,128,0.25)" },
              ].map(({ event, label, color, bg, border }) => (
                <Btn key={event} label={label} color={color} bg={bg} border={border} disabled={goLiveLoading} onClick={() => sendGoLive(event)} />
              ))}
            </div>
          </div>
          <div style={card}>
            <div style={{ padding: "16px 20px" }}>
              <p style={sectionHeading}>Audit Log</p>
              {goLiveLog.length === 0 && <p style={{ ...labelTxt, margin: 0, textAlign: "center", padding: 16 }}>No events yet.</p>}
              {goLiveLog.map((ev) => (
                <div key={ev.id} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: "1px solid rgba(90,144,168,0.1)", alignItems: "flex-start" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", marginTop: 5, flexShrink: 0, background: ev.new_status?.value === "approved" ? "#16A34A" : ev.new_status?.value === "suspended" ? "#DC2626" : "#9BBCCC" }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: "0 0 2px", fontSize: 13, color: "#1C3545", fontFamily: "Inter, sans-serif", fontWeight: 600 }}>{(ev.event_type?.value || ev.event_type || "").replace(/_/g, " ")}</p>
                    {ev.reason && <p style={{ margin: "0 0 2px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{ev.reason}</p>}
                    {ev.actioned_by_name && <p style={{ margin: 0, fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>by {ev.actioned_by_name}</p>}
                  </div>
                  <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{ev.actioned_at ? new Date(ev.actioned_at).toLocaleDateString() : ""}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Payment tab */}
      {tab === "payment" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {paymentLoading && <Spinner />}

          {/* ── Bank Details ── */}
          <div>
            <p style={sectionHeading}>Bank Details & Verification</p>
            {!paymentLoading && !payment && (
              <div style={{ ...card, padding: 32, textAlign: "center" }}>
                <p style={{ color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13, margin: 0 }}>Doctor has not submitted payment details yet.</p>
              </div>
            )}
            {!paymentLoading && payment && (
              <div style={{ ...card, padding: "20px 24px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  {payment.is_verified
                    ? <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, color: "#16A34A", fontFamily: "Inter, sans-serif", fontWeight: 600 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#16A34A", display: "inline-block" }} />Verified</span>
                    : <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, color: "#B45309", fontFamily: "Inter, sans-serif", fontWeight: 600 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#B45309", display: "inline-block" }} />Unverified</span>
                  }
                  {payment.is_verified
                    ? <Btn small label="Revoke Verification" color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)" onClick={() => verifyPayment(false)} disabled={paymentLoading} />
                    : <Btn small label="Mark as Verified" color="#16A34A" bg="rgba(22,163,74,0.1)" border="rgba(22,163,74,0.25)" onClick={() => verifyPayment(true)} disabled={paymentLoading} />
                  }
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {[["Account Holder", payment.account_holder_name], ["Bank Name", payment.bank_name], ["Account No.", payment.account_number_masked], ["IFSC Code", payment.ifsc_code], ["UPI ID", payment.upi_id], ["PAN Number", payment.pan_number_masked], ["GST Number", payment.gst_number]].filter(([, v]) => v).map(([l, v]) => (
                    <div key={l} style={{ ...card, padding: "10px 14px", boxShadow: "none", border: "1px solid rgba(90,144,168,0.15)" }}>
                      <p style={{ ...labelTxt, margin: "0 0 2px" }}>{l}</p>
                      <p style={{ ...valueTxt, margin: 0, fontFamily: "monospace", fontSize: 12, letterSpacing: "0.04em" }}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Fee Agreements ── */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <p style={sectionHeading}>Fee Agreements</p>
              <Btn small label={feeFormOpen ? "Cancel" : "+ New Agreement"} color="#5A90A8" bg="rgba(90,144,168,0.1)" border="rgba(90,144,168,0.25)" onClick={() => setFeeFormOpen((o) => !o)} />
            </div>

            {feeFormOpen && (
              <div style={{ ...card, padding: "18px 20px", marginBottom: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                  <div>
                    <p style={labelTxt}>Version <span style={{ color: "#5A90A8" }}>*</span></p>
                    <input value={feeForm.agreement_version} onChange={(e) => setFeeForm((f) => ({ ...f, agreement_version: e.target.value }))} placeholder="e.g. v1.0" style={inputBase} />
                  </div>
                  <div>
                    <p style={labelTxt}>Fee Model <span style={{ color: "#5A90A8" }}>*</span></p>
                    <select value={feeForm.fee_model} onChange={(e) => setFeeForm((f) => ({ ...f, fee_model: e.target.value }))} style={{ ...inputBase, cursor: "pointer" }}>
                      <option value="flat_monthly">Flat Monthly</option>
                      <option value="per_patient_percentage">Per Patient %</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </div>
                  {(feeForm.fee_model === "flat_monthly" || feeForm.fee_model === "hybrid") && (
                    <div>
                      <p style={labelTxt}>Monthly Fee (₹)</p>
                      <input type="number" value={feeForm.monthly_platform_fee_inr} onChange={(e) => setFeeForm((f) => ({ ...f, monthly_platform_fee_inr: e.target.value }))} placeholder="e.g. 5000" style={inputBase} />
                    </div>
                  )}
                  {(feeForm.fee_model === "per_patient_percentage" || feeForm.fee_model === "hybrid") && (
                    <div>
                      <p style={labelTxt}>Per Patient % Share</p>
                      <input type="number" value={feeForm.per_patient_share_pct} onChange={(e) => setFeeForm((f) => ({ ...f, per_patient_share_pct: e.target.value }))} placeholder="e.g. 10" style={inputBase} />
                    </div>
                  )}
                  <div>
                    <p style={labelTxt}>Minimum Monthly Fee (₹)</p>
                    <input type="number" value={feeForm.minimum_monthly_fee} onChange={(e) => setFeeForm((f) => ({ ...f, minimum_monthly_fee: e.target.value }))} placeholder="e.g. 2000" style={inputBase} />
                  </div>
                  <div>
                    <p style={labelTxt}>Payment Terms</p>
                    <input value={feeForm.payment_terms} onChange={(e) => setFeeForm((f) => ({ ...f, payment_terms: e.target.value }))} placeholder="e.g. Net 30" style={inputBase} />
                  </div>
                  <div>
                    <p style={labelTxt}>Effective From</p>
                    <input type="date" value={feeForm.effective_from} onChange={(e) => setFeeForm((f) => ({ ...f, effective_from: e.target.value }))} style={{ ...inputBase, colorScheme: "light" }} />
                  </div>
                  <div>
                    <p style={labelTxt}>Expires At</p>
                    <input type="date" value={feeForm.expires_at} onChange={(e) => setFeeForm((f) => ({ ...f, expires_at: e.target.value }))} style={{ ...inputBase, colorScheme: "light" }} />
                  </div>
                </div>
                <Btn label={feeSaving ? "Saving…" : "Create Agreement"} color="#FFFFFF" bg="#5A90A8" border="#5A90A8" onClick={submitFeeAgreement} disabled={feeSaving || !feeForm.agreement_version} />
              </div>
            )}

            {feeAgreements.length === 0 && !feeFormOpen && (
              <div style={{ ...card, padding: 28, textAlign: "center" }}>
                <p style={{ color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13, margin: 0 }}>No fee agreements yet.</p>
              </div>
            )}
            {feeAgreements.map((a) => (
              <div key={a.id} style={{ ...card, padding: "14px 18px", marginBottom: 8, borderLeft: `3px solid ${a.is_active ? "#5A90A8" : "rgba(90,144,168,0.2)"}` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>v{a.agreement_version}</span>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {a.is_active && <span style={{ fontSize: 11, fontWeight: 700, color: "#5A90A8", fontFamily: "Inter, sans-serif", padding: "2px 8px", borderRadius: 999, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.25)" }}>ACTIVE</span>}
                    {a.signed_by_doctor
                      ? <span style={{ fontSize: 11, fontWeight: 600, color: "#16A34A", fontFamily: "Inter, sans-serif" }}>✓ Signed</span>
                      : <span style={{ fontSize: 11, color: "#B45309", fontFamily: "Inter, sans-serif" }}>Awaiting signature</span>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                  {[["Model", a.fee_model?.replace(/_/g, " ")], ["Monthly Fee", a.monthly_platform_fee_inr ? `₹${a.monthly_platform_fee_inr}` : null], ["Per Patient %", a.per_patient_share_pct ? `${a.per_patient_share_pct}%` : null], ["Min Monthly", a.minimum_monthly_fee ? `₹${a.minimum_monthly_fee}` : null], ["Terms", a.payment_terms], ["Effective", a.effective_from], ["Expires", a.expires_at]].filter(([, v]) => v).map(([l, v]) => (
                    <div key={l}>
                      <p style={{ ...labelTxt, margin: "0 0 1px" }}>{l}</p>
                      <p style={{ ...valueTxt, margin: 0, fontSize: 12 }}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* ── Payout Dashboard ── */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <p style={sectionHeading}>Payout Dashboard</p>
              <Btn small label={payoutFormOpen ? "Cancel" : "+ Record Payout"} color="#5A90A8" bg="rgba(90,144,168,0.1)" border="rgba(90,144,168,0.25)" onClick={() => setPayoutFormOpen((o) => !o)} />
            </div>

            {payoutFormOpen && (
              <div style={{ ...card, padding: "18px 20px", marginBottom: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                  <div>
                    <p style={labelTxt}>Period Start <span style={{ color: "#5A90A8" }}>*</span></p>
                    <input type="date" value={payoutForm.period_start} onChange={(e) => setPayoutForm((f) => ({ ...f, period_start: e.target.value }))} style={{ ...inputBase, colorScheme: "light" }} />
                  </div>
                  <div>
                    <p style={labelTxt}>Period End <span style={{ color: "#5A90A8" }}>*</span></p>
                    <input type="date" value={payoutForm.period_end} onChange={(e) => setPayoutForm((f) => ({ ...f, period_end: e.target.value }))} style={{ ...inputBase, colorScheme: "light" }} />
                  </div>
                  <div>
                    <p style={labelTxt}>Gross Amount (₹) <span style={{ color: "#5A90A8" }}>*</span></p>
                    <input type="number" value={payoutForm.gross_amount} onChange={(e) => setPayoutForm((f) => ({ ...f, gross_amount: e.target.value }))} placeholder="e.g. 10000" style={inputBase} />
                  </div>
                  <div>
                    <p style={labelTxt}>TDS Rate (%)</p>
                    <input type="number" value={payoutForm.tds_rate} onChange={(e) => setPayoutForm((f) => ({ ...f, tds_rate: e.target.value }))} placeholder="10" style={inputBase} />
                  </div>
                  <div>
                    <p style={labelTxt}>GST Amount (₹)</p>
                    <input type="number" value={payoutForm.gst_amount} onChange={(e) => setPayoutForm((f) => ({ ...f, gst_amount: e.target.value }))} placeholder="0" style={inputBase} />
                  </div>
                  <div>
                    <p style={labelTxt}>Reference No.</p>
                    <input value={payoutForm.reference_no} onChange={(e) => setPayoutForm((f) => ({ ...f, reference_no: e.target.value }))} placeholder="e.g. TXN123456" style={inputBase} />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <p style={labelTxt}>Notes</p>
                    <input value={payoutForm.notes} onChange={(e) => setPayoutForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Optional notes…" style={inputBase} />
                  </div>
                </div>
                {payoutForm.gross_amount && (
                  <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(90,144,168,0.06)", border: "1px solid rgba(90,144,168,0.15)", marginBottom: 10, fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif" }}>
                    TDS: ₹{(Number(payoutForm.gross_amount) * Number(payoutForm.tds_rate || 0) / 100).toFixed(2)} · GST: ₹{Number(payoutForm.gst_amount || 0).toFixed(2)} · <strong>Net: ₹{(Number(payoutForm.gross_amount) - Number(payoutForm.gross_amount) * Number(payoutForm.tds_rate || 0) / 100 - Number(payoutForm.gst_amount || 0)).toFixed(2)}</strong>
                  </div>
                )}
                <Btn label={payoutSaving ? "Saving…" : "Record Payout"} color="#FFFFFF" bg="#5A90A8" border="#5A90A8" onClick={submitPayout} disabled={payoutSaving || !payoutForm.period_start || !payoutForm.period_end || !payoutForm.gross_amount} />
              </div>
            )}

            {payouts.length === 0 && !payoutFormOpen && (
              <div style={{ ...card, padding: 28, textAlign: "center" }}>
                <p style={{ color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13, margin: 0 }}>No payouts recorded yet.</p>
              </div>
            )}
            {payouts.map((p) => {
              const statusColor = { pending: "#B45309", processed: "#16A34A", failed: "#DC2626" }[p.status] || "#9BBCCC";
              return (
                <div key={p.id} style={{ ...card, padding: "14px 18px", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{p.period_start} → {p.period_end}</span>
                      {p.reference_no && <span style={{ marginLeft: 10, fontSize: 11, color: "#7AAABB", fontFamily: "monospace" }}>{p.reference_no}</span>}
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: statusColor, fontFamily: "Inter, sans-serif", textTransform: "capitalize" }}>{p.status}</span>
                  </div>
                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 8 }}>
                    {[["Gross", `₹${Number(p.gross_amount).toFixed(2)}`], ["TDS", p.tds_amount != null ? `₹${Number(p.tds_amount).toFixed(2)} (${p.tds_rate}%)` : "—"], ["GST", p.gst_amount != null ? `₹${Number(p.gst_amount).toFixed(2)}` : "—"], ["Net", `₹${Number(p.net_amount).toFixed(2)}`]].map(([l, v]) => (
                      <div key={l}>
                        <p style={{ ...labelTxt, margin: "0 0 1px" }}>{l}</p>
                        <p style={{ ...valueTxt, margin: 0, fontSize: 12, fontWeight: l === "Net" ? 700 : 400 }}>{v}</p>
                      </div>
                    ))}
                  </div>
                  {p.status === "pending" && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <Btn small label="Mark Processed" color="#16A34A" bg="rgba(22,163,74,0.1)" border="rgba(22,163,74,0.25)" onClick={() => updatePayoutStatus(p.id, "processed")} />
                      <Btn small label="Mark Failed" color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)" onClick={() => updatePayoutStatus(p.id, "failed")} />
                    </div>
                  )}
                  {p.notes && <p style={{ margin: "6px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{p.notes}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Notes tab */}
      {tab === "notes" && (
        <div>
          {/* Internal Notes */}
          <div style={{ ...card, padding: "20px 22px", marginBottom: 16 }}>
            <p style={sectionHeading}>Internal Notes</p>
            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <select value={noteType} onChange={(e) => setNoteType(e.target.value)} style={{ ...inputBase, cursor: "pointer", flexShrink: 0 }}>
                {["general","credential_issue","cluster_discussion","commercial","complaint","escalation"].map((t) => (
                  <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                ))}
              </select>
            </div>
            <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note…" rows={3}
              style={{ ...inputBase, display: "block", width: "100%", resize: "vertical", lineHeight: 1.6, marginBottom: 12 }} />
            <Btn label={noteLoading ? "Saving…" : "Add Note"} disabled={!newNote.trim() || noteLoading} onClick={addNote} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
            {notes.map((n) => (
              <div key={n.id} style={{ ...card, padding: "14px 18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#5A90A8", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>{n.note_type?.replace(/_/g, " ")}</span>
                  <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{n.created_at ? new Date(n.created_at).toLocaleDateString() : ""}</span>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.65 }}>{n.note}</p>
              </div>
            ))}
            {notes.length === 0 && <p style={{ ...labelTxt, textAlign: "center", padding: 24 }}>No notes yet.</p>}
          </div>

          {/* Notification Dispatch */}
          <div style={{ ...card, padding: "20px 22px", marginBottom: 16 }}>
            <p style={sectionHeading}>Send Notification to Doctor</p>
            <input value={notifTitle} onChange={(e) => setNotifTitle(e.target.value)} placeholder="Notification title…"
              style={{ ...inputBase, display: "block", width: "100%", marginBottom: 10 }} />
            <textarea value={notifBody} onChange={(e) => setNotifBody(e.target.value)} placeholder="Message body (optional)…" rows={3}
              style={{ ...inputBase, display: "block", width: "100%", resize: "vertical", lineHeight: 1.6, marginBottom: 12 }} />
            <Btn label={notifSending ? "Sending…" : "Send Notification"} disabled={!notifTitle.trim() || notifSending} onClick={sendNotification} />
          </div>

          {/* Notification History */}
          <p style={{ ...sectionHeading, marginTop: 4, marginBottom: 10 }}>Notification History</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {notifications.map((n) => (
              <div key={n.id} style={{ ...card, padding: "14px 18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#2D6A84", fontFamily: "Inter, sans-serif" }}>{n.title}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif",
                    color: n.is_read ? "#52B788" : "#E9A84C",
                    background: n.is_read ? "#EAF7EF" : "#FEF6E7",
                    borderRadius: 6, padding: "2px 8px",
                  }}>{n.is_read ? "Read" : "Unread"}</span>
                </div>
                {n.body && <p style={{ margin: "4px 0 6px", fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.65 }}>{n.body}</p>}
                <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>
                  Sent {n.created_at ? new Date(n.created_at).toLocaleString() : "—"}
                  {n.is_read && n.read_at ? ` · Read ${new Date(n.read_at).toLocaleString()}` : ""}
                </span>
              </div>
            ))}
            {notifications.length === 0 && <p style={{ ...labelTxt, textAlign: "center", padding: 24 }}>No notifications sent yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   4a. PENDING APPLICATIONS SECTION
═══════════════════════════════════════════════════════════════════════════ */
function PendingApplicationsSection() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [subFilter, setSubFilter] = useState("all"); // "all" | "pending" | "under_review"
  const [selectedId, setSelectedId] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [{ data: pending }, { data: underReview }] = await Promise.all([
          api.get("/admin/doctors?account_status=pending&limit=200"),
          api.get("/admin/doctors?account_status=under_review&limit=200"),
        ]);
        const merge = [
          ...(Array.isArray(pending) ? pending : pending.items || []),
          ...(Array.isArray(underReview) ? underReview : underReview.items || []),
        ];
        merge.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setDoctors(merge);
      } catch { setErr("Failed to load applications."); }
      setLoading(false);
    };
    load();
  }, []);

  const filtered = doctors.filter((d) => {
    if (subFilter === "pending" && d.account_status !== "pending") return false;
    if (subFilter === "under_review" && d.account_status !== "under_review") return false;
    if (search) {
      const t = search.toLowerCase();
      return d.display_name?.toLowerCase().includes(t) || d.city?.toLowerCase().includes(t) || d.country?.toLowerCase().includes(t);
    }
    return true;
  });

  const pendingCount = doctors.filter((d) => d.account_status === "pending").length;
  const underReviewCount = doctors.filter((d) => d.account_status === "under_review").length;

  const pillStyle = (active) => ({
    padding: "5px 14px", borderRadius: 999, border: `1px solid ${active ? "#5A90A8" : "rgba(90,144,168,0.25)"}`,
    background: active ? "rgba(90,144,168,0.12)" : "transparent", color: active ? "#1C3545" : "#7AAABB",
    fontSize: 13, fontWeight: active ? 600 : 400, fontFamily: "Inter, sans-serif", cursor: "pointer",
    transition: "all 150ms", display: "flex", alignItems: "center", gap: 6,
  });

  const countBadge = (n, color) => (
    <span style={{ minWidth: 20, height: 18, borderRadius: 999, background: color, color: "#fff", fontSize: 11, fontWeight: 700, fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 5px" }}>{n}</span>
  );

  if (selectedId) return <DoctorDetailPanel doctorId={selectedId} onBack={() => setSelectedId(null)} />;

  return (
    <div style={{ padding: "32px 28px 60px" }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Pending Applications</h2>
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
          Doctors awaiting review — oldest first · click a row to open their profile
        </p>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={pillStyle(subFilter === "all")} onClick={() => setSubFilter("all")}>
              All Pending {countBadge(doctors.length, "#5A90A8")}
            </button>
            <button style={pillStyle(subFilter === "pending")} onClick={() => setSubFilter("pending")}>
              New {countBadge(pendingCount, "#D97706")}
            </button>
            <button style={pillStyle(subFilter === "under_review")} onClick={() => setSubFilter("under_review")}>
              Under Review {countBadge(underReviewCount, "#2563EB")}
            </button>
          </div>
          <input placeholder="Search name, city…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ ...inputBase, width: 200 }} />
        </div>
      </div>

      <ErrBanner msg={err} onDismiss={() => setErr("")} />

      {loading ? <Spinner /> : (
        <div className="admin-table-wrap" style={{ ...card, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "rgba(90,144,168,0.05)" }}>
              <tr><Th>Doctor</Th><Th>Specialty</Th><Th>Location</Th><Th>Status</Th><Th>Step</Th><Th>Applied</Th></tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <TableRow key={d.id} onClick={() => setSelectedId(d.id)}>
                  <Td>
                    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                      {d.profile_photo_url
                        ? <img src={`${UPLOADS_BASE}${d.profile_photo_url}`} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                        : <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(90,144,168,0.1)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>{(d.display_name || "?")[0]}</div>
                      }
                      <span style={{ fontWeight: 600, color: "#1C3545" }}>{d.display_name || "—"}</span>
                    </div>
                  </Td>
                  <Td style={{ color: "#4D7A8E", maxWidth: 160 }}>{d.primary_specialty || "—"}</Td>
                  <Td style={{ color: "#4D7A8E" }}>{[d.city, d.country].filter(Boolean).join(", ") || "—"}</Td>
                  <Td><Badge map={ACCT_STATUS} value={d.account_status || "pending"} /></Td>
                  <Td style={{ color: "#7AAABB", textTransform: "capitalize", fontSize: 12 }}>{d.onboarding_step || "—"}</Td>
                  <Td style={{ color: "#9BBCCC", fontSize: 12 }}>{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</Td>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: "center", color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13 }}>
                  {doctors.length === 0 ? "No pending applications" : "No results match your filter"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   4. DOCTORS SECTION (list)
═══════════════════════════════════════════════════════════════════════════ */
function DoctorsSection() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ skip: 0, limit: 100 });
        if (statusFilter) params.set("account_status", statusFilter);
        const { data } = await api.get(`/admin/doctors?${params}`);
        setDoctors(Array.isArray(data) ? data : data.items || []);
      } catch { setErr("Failed to load doctors."); }
      setLoading(false);
    };
    load();
  }, [statusFilter]);

  const filtered = doctors.filter((d) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return d.display_name?.toLowerCase().includes(term) || d.city?.toLowerCase().includes(term) || d.country?.toLowerCase().includes(term);
  });

  if (selectedId) return <DoctorDetailPanel doctorId={selectedId} onBack={() => setSelectedId(null)} />;

  return (
    <div style={{ padding: "32px 28px 60px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: "0 0 2px", fontSize: 20, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Doctors</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{doctors.length} total · click a row to review</p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input placeholder="Search name, city…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ ...inputBase, width: 200 }} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ ...inputBase, cursor: "pointer" }}>
            <option value="">All Statuses</option>
            {Object.keys(ACCT_STATUS).map((s) => <option key={s} value={s}>{ACCT_STATUS[s].label}</option>)}
          </select>
        </div>
      </div>
      <ErrBanner msg={err} onDismiss={() => setErr("")} />
      {loading ? <Spinner /> : (
        <div className="admin-table-wrap" style={{ ...card, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "rgba(90,144,168,0.05)" }}>
              <tr><Th>Doctor</Th><Th>Specialty</Th><Th>Location</Th><Th>Status</Th><Th>Step</Th><Th>Joined</Th></tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <TableRow key={d.id} selected={d.id === selectedId} onClick={() => setSelectedId(d.id)}>
                  <Td>
                    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                      {d.profile_photo_url
                        ? <img src={`${UPLOADS_BASE}${d.profile_photo_url}`} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                        : <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(90,144,168,0.1)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>{(d.display_name || "?")[0]}</div>
                      }
                      <span style={{ fontWeight: 600, color: "#1C3545" }}>{d.display_name || "—"}</span>
                    </div>
                  </Td>
                  <Td style={{ color: "#4D7A8E", maxWidth: 160 }}>{d.primary_specialty || "—"}</Td>
                  <Td style={{ color: "#4D7A8E" }}>{[d.city, d.country].filter(Boolean).join(", ") || "—"}</Td>
                  <Td><Badge map={ACCT_STATUS} value={d.account_status || "pending"} /></Td>
                  <Td style={{ color: "#7AAABB", textTransform: "capitalize", fontSize: 12 }}>{d.onboarding_step || "—"}</Td>
                  <Td style={{ color: "#9BBCCC", fontSize: 12 }}>{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</Td>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: "center", color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13 }}>No doctors found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   5. CLUSTERS SECTION
═══════════════════════════════════════════════════════════════════════════ */

// Static files (/uploads/...) are served directly by nginx — not under /api/.
// If API_BASE is a relative path like "/api", the uploads base is just "".
// If API_BASE is an absolute URL like "http://localhost:8000", keep it as-is.
const UPLOADS_BASE = API_BASE.startsWith("/") ? "" : API_BASE;

// Defined at module level so closures always capture the same stable reference.
const EMPTY_BIO = {
  biomarker_name: "", biomarker_code: "", category: "lab", unit: "",
  reference_range_min: "",     reference_range_min_op: "none",
  reference_range_max: "",     reference_range_max_op: "none",
  optimal_range_min: "",       optimal_range_min_op: "none",
  optimal_range_max: "",       optimal_range_max_op: "none",
  default_weight: "", is_mandatory: true, description: "", gender_marker: "Both",
};

// Operator symbol map — includes legacy gte/lte for existing DB rows
const OP_SYM = { gt: ">", gte: ">", lt: "<", lte: "<" };

// Format a biomarker range for display: "> 70 – < 100 mg/dL", "100 mg/dL", "—"
// When op is "none" and value exists, just show the value with no symbol.
const fmtRange = (min, max, unit = "", minOp = "none", maxOp = "none") => {
  const u = unit ? ` ${unit}` : "";
  const parts = [];
  if (min != null) {
    const sym = OP_SYM[minOp];
    parts.push(sym ? `${sym} ${min}` : `${min}`);
  }
  if (max != null) {
    const sym = OP_SYM[maxOp];
    parts.push(sym ? `${sym} ${max}` : `${max}`);
  }
  return parts.length ? parts.join(" – ") + u : "—";
};

class ClusterErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("[ClustersSection crash]", error.message, error.stack, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: "40px 28px" }}>
          <div style={{ padding: "20px 24px", borderRadius: 12, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", fontFamily: "Inter, sans-serif" }}>
            <p style={{ margin: "0 0 6px", fontWeight: 700, color: "#DC2626" }}>Render error — check browser console for details</p>
            <pre style={{ margin: "0 0 16px", fontSize: 11, color: "#7AAABB", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{this.state.error.message}</pre>
            <button
              onClick={() => this.setState({ error: null })}
              style={{ padding: "7px 16px", borderRadius: 7, border: "1px solid rgba(90,144,168,0.3)", background: "rgba(90,144,168,0.1)", color: "#5A90A8", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const EMPTY_Q = { question_text: "", question_type: "text", options: "", maps_to_score_dimension: "", default_weight: "0", is_mandatory: true, display_order: "" };

/* ── TagInput — type a value and press Enter to add a chip tag ── */
function ClusterTagInput({ tags, onChange, placeholder }) {
  const [input, setInput] = React.useState("");
  const add = (val) => {
    const v = val.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setInput("");
  };
  const remove = (tag) => onChange(tags.filter((t) => t !== tag));
  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); add(input); }
    if (e.key === "Backspace" && !input && tags.length > 0) onChange(tags.slice(0, -1));
  };
  return (
    <div>
      {tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {tags.map((tag) => (
            <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 8px 4px 10px", borderRadius: 999, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.3)", color: "#5A90A8", fontSize: 12, fontFamily: "Inter, sans-serif" }}>
              {tag}
              <button type="button" onClick={() => remove(tag)} style={{ background: "none", border: "none", cursor: "pointer", color: "#7AAABB", padding: 0, lineHeight: 1, fontSize: 13 }}>✕</button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text" value={input} placeholder={placeholder || "Type and press Enter…"}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => { if (input.trim()) add(input); }}
        style={{ ...inputBase, width: "100%", display: "block" }}
      />
    </div>
  );
}

/* ── Admin cluster field map (all 20 visibility-controlled fields) ── */
const ADMIN_CLUSTER_FIELD_MAP = [
  { key: "cluster_id",               label: "Cluster ID",             get: (c) => c.cluster_id },
  { key: "score_code",               label: "Score Code",             get: (c) => c.score_code },
  { key: "score_full_name",          label: "Score Full Name",        get: (c) => c.score_full_name },
  { key: "user_facing_name",         label: "User-Facing Name",       get: (c) => c.user_facing_name },
  { key: "user_subtitle",            label: "User Subtitle",          get: (c) => c.user_subtitle },
  { key: "tile_name",                label: "Tile Name",              get: (c) => c.tile_name },
  { key: "clinical_scope",           label: "Clinical Scope",         get: (c) => c.clinical_scope,           multiline: true },
  { key: "what_moves_the_score",     label: "What Moves The Score",   get: (c) => c.what_moves_the_score,     multiline: true },
  { key: "specialist_type",          label: "Specialist Type",        get: (c) => c.specialist_type,          multiline: true },
  { key: "user_persona",             label: "User Persona",           get: (c) => c.user_persona,             multiline: true },
  { key: "biomarkers_in_cluster",    label: "Biomarkers In Cluster",  get: (c) => c.biomarkers_in_cluster != null ? String(c.biomarkers_in_cluster) : null },
  { key: "composite_formula",        label: "Composite Formula",      get: (c) => c.composite_formula,        multiline: true },
  { key: "score_bands",              label: "Score Bands",            get: (c) => c.score_bands,              multiline: true, scoreBands: true },
  { key: "score_intent",             label: "Score Intent",           get: (c) => c.score_intent },
  { key: "physician_oversight",      label: "Physician Oversight",    get: (c) => c.physician_oversight || null },
  { key: "status",                   label: "Status",                 get: (c) => c.status },
  { key: "subjective_slot",          label: "Subjective Slot",        get: (c) => c.subjective_slot },
  { key: "lab_or_vital_markers",     label: "Lab / Vital Markers",    get: (c) => c.lab_or_vital_markers != null ? String(c.lab_or_vital_markers) : null },
  { key: "required_lab_markers",     label: "Required Lab Markers",   get: (c) => c.required_lab_markers != null ? String(c.required_lab_markers) : null },
];
const ADMIN_FIELD_DEFAULTS = {
  cluster_id: "L1", score_code: "L1", score_full_name: "L1", user_facing_name: "L1",
  user_subtitle: "L1", tile_name: "L1", clinical_scope: "L1", what_moves_the_score: "L1",
  specialist_type: "L1", user_persona: "L1", biomarkers_in_cluster: "L1",
  composite_formula: "L1", score_bands: "L1",
  score_intent: "L1", physician_oversight: "L1", status: "L1",
  subjective_slot: "L1", lab_or_vital_markers: "L1", required_lab_markers: "L1",
};

function AdminClusterL2Fields({ fields }) {
  const [open, setOpen] = React.useState(false);
  if (!fields.length) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <button onClick={() => setOpen((p) => !p)}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0", background: "none", border: "none", color: "#5A90A8", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 200ms" }}><polyline points="6 9 12 15 18 9"/></svg>
        {open ? "Show Less" : `Show More (${fields.length} fields)`}
      </button>
      {open && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed rgba(90,144,168,0.2)" }}>
          {fields.filter(f => !f.multiline).map(({ label, val }) => (
            <FieldRow key={label} label={label} val={val} />
          ))}
          {fields.filter(f => f.multiline).length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12, marginTop: 10 }}>
              {fields.filter(f => f.multiline).map(({ label, val, scoreBands: isBands }) => {
                if (isBands) {
                  const bands = parseBands(val);
                  if (bands.length === 0) return null;
                  return (
                    <div key={label} style={{ background: "rgba(90,144,168,0.04)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 12, padding: "14px 18px" }}>
                      <p style={{ margin: "0 0 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>Score Bands</p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {bands.map((b, i) => (
                          <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 12px", borderRadius: 8, background: "rgba(90,144,168,0.08)", border: "1px solid rgba(90,144,168,0.2)", fontFamily: "Inter, sans-serif" }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#1C3545" }}>{b.label}</span>
                            {(b.min !== "" && b.min != null) || (b.max !== "" && b.max != null) ? (
                              <span style={{ fontSize: 11, color: "#5A90A8" }}>{b.min != null && b.min !== "" ? b.min : "—"}{" – "}{b.max != null && b.max !== "" ? b.max : "—"}</span>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return <InfoBlock key={label} label={label} text={String(val)} />;
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ClustersSection({ resetSignal = 0 }) {
  /* ── cluster list state ── */
  const [clusters, setClusters]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const EMPTY_CLUSTER_FORM = {
    cluster_id: "", cluster_code: "", cluster_name: "", cluster_group: "core", description: "", display_order: "",
    score_code: "", score_full_name: "", user_facing_name: "", user_subtitle: "", tile_name: "",
    clinical_scope: "", what_moves_the_score: "", specialist_type: [], user_persona: [],
    composite_formula: "", score_bands: [], score_intent: "",
    physician_oversight: "", status: "", subjective_slot: "", lab_or_vital_markers: "", required_lab_markers: "",
  };
  const [form, setForm]               = useState(EMPTY_CLUSTER_FORM);
  const [iconFile, setIconFile]       = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [iconUploading, setIconUploading] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState("");

  /* ── edit cluster state ── */
  const [editClusterId, setEditClusterId]   = useState(null);
  const [editClusterForm, setEditClusterForm] = useState({});
  const [editClusterSaving, setEditClusterSaving] = useState(false);

  /* ── cluster panel sub-tab ── */
  const [clusterSubTab, setClusterSubTab] = useState("overview"); // "overview" | "biomarkers" | "questions"

  /* ── biomarker panel state ── */
  const [selCluster, setSelCluster]   = useState(null);
  const [biomarkers, setBiomarkers]   = useState([]);
  const [bLoading, setBLoading]       = useState(false);
  const [showBioForm, setShowBioForm] = useState(false);
  const [bioForm, setBioForm]         = useState(EMPTY_BIO);
  const [bioSaving, setBioSaving]     = useState(false);
  const [bErr, setBErr]               = useState("");
  const [bmLibrary, setBmLibrary]     = useState([]);
  const [selectedBmIdToAssign, setSelectedBmIdToAssign] = useState("");
  const [assignWeight, setAssignWeight] = useState("");
  const [assignScored, setAssignScored] = useState("scored");

  /* ── edit biomarker state ── */
  const [editBmId, setEditBmId]         = useState(null);
  const [editBmForm, setEditBmForm]     = useState({});
  const [editBmSaving, setEditBmSaving] = useState(false);

  /* ── remove biomarker confirmation modal ── */
  const [removeBmConfirm, setRemoveBmConfirm] = useState({ open: false, bm: null });

  /* ── admin cluster field visibility settings ── */
  const [adminClusterFieldSettings, setAdminClusterFieldSettings] = useState({});

  /* ── question bank state ── */
  const [questions, setQuestions]   = useState([]);
  const [qLoading, setQLoading]     = useState(false);
  const [showQForm, setShowQForm]   = useState(false);
  const [qForm, setQForm]           = useState(EMPTY_Q);
  const [qSaving, setQSaving]       = useState(false);
  const [qErr, setQErr]             = useState("");
  const [editQId, setEditQId]       = useState(null);
  const [editQForm, setEditQForm]   = useState({});

  const safeErrMsg = (e, fallback) => {
    if (!e?.response) return `${fallback} (no server response — check console)`;
    const d = e.response.data?.detail;
    if (!d) return `${fallback} (HTTP ${e.response.status} — check console)`;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((v) => v?.msg || String(v)).join("; ");
    return `${fallback} (HTTP ${e.response.status})`;
  };

  useEffect(() => {
    Promise.allSettled([
      api.get("/admin/clusters", { params: { active_only: false } }),
      api.get("/admin/settings/cluster-fields"),
    ]).then(([clRes, cfvRes]) => {
      if (clRes.status === "fulfilled") setClusters(Array.isArray(clRes.value.data) ? clRes.value.data : []);
      else setErr("Failed to load clusters.");
      if (cfvRes.status === "fulfilled") {
        const adminSettings = {};
        (cfvRes.value.data || []).forEach((row) => { if (row.admin) adminSettings[row.field_name] = row.admin; });
        setAdminClusterFieldSettings(adminSettings);
      }
    }).finally(() => setLoading(false));
  }, []);

  // Reset all UI state when the parent nav triggers a re-visit to this section
  useEffect(() => {
    if (resetSignal === 0) return;
    setShowForm(false);
    setSelCluster(null);
    setBiomarkers([]);
    setQuestions([]);
    setEditClusterId(null);
    setEditQId(null);
    setEditBmId(null);
    setClusterSubTab("overview");
    setErr("");
  }, [resetSignal]);

  const handleIconChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIconFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setIconPreview(ev.target.result);
    reader.readAsDataURL(file);
  };
  const clearIcon = () => { setIconFile(null); setIconPreview(null); };

  const resetForm = () => {
    setForm(EMPTY_CLUSTER_FORM);
    clearIcon();
    setShowForm(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      let icon_url = null;
      if (iconFile) {
        setIconUploading(true);
        const fd = new FormData();
        fd.append("file", iconFile);
        const { data: uploadData } = await api.post("/admin/clusters/icon-upload", fd);
        icon_url = uploadData.icon_url;
        setIconUploading(false);
      }
      const payload = {
        ...form,
        icon_url,
        cluster_id: form.cluster_id || null,
        display_order: form.display_order !== "" ? Number(form.display_order) : null,
        score_code: form.score_code || null,
        score_full_name: form.score_full_name || null,
        user_facing_name: form.user_facing_name || null,
        user_subtitle: form.user_subtitle || null,
        tile_name: form.tile_name || null,
        clinical_scope: form.clinical_scope || null,
        what_moves_the_score: form.what_moves_the_score || null,
        specialist_type: form.specialist_type.length > 0 ? form.specialist_type.join("\n") : null,
        user_persona: form.user_persona.length > 0 ? form.user_persona.join("\n") : null,
        composite_formula: form.composite_formula || null,
        score_bands: (() => { const b = form.score_bands.filter((x) => x.label.trim()); return b.length > 0 ? JSON.stringify(b) : null; })(),
        score_intent: form.score_intent || null,
        physician_oversight: form.physician_oversight || null,
        status: form.status || null,
        subjective_slot: form.subjective_slot || null,
        lab_or_vital_markers: form.lab_or_vital_markers !== "" ? Number(form.lab_or_vital_markers) : null,
        required_lab_markers: form.required_lab_markers !== "" ? Number(form.required_lab_markers) : null,
      };
      const { data } = await api.post("/admin/clusters", payload);
      setClusters((prev) => [data, ...prev]);
      resetForm();
    } catch (e) {
      setIconUploading(false);
      setErr(safeErrMsg(e, "Failed to create cluster."));
    }
    setSaving(false);
  };

  /* ── biomarker helpers ── */
  const selectCluster = async (cluster) => {
    setShowForm(false); // close new-cluster form whenever a row is selected
    if (selCluster?.id === cluster.id) {
      setSelCluster(null); setBiomarkers([]); setShowBioForm(false); setBErr("");
      setQuestions([]); setQErr(""); setClusterSubTab("overview"); return;
    }
    setSelCluster(cluster); setShowBioForm(false); setBErr(""); setBLoading(true); setBiomarkers([]);
    setQuestions([]); setQErr(""); setQLoading(true); setClusterSubTab("overview");
    setEditBmId(null); setEditQId(null);
    const [bioRes, qRes] = await Promise.allSettled([
      api.get(`/admin/clusters/${cluster.id}/biomarkers`),
      api.get(`/admin/clusters/${cluster.id}/assessment-questions`),
    ]);
    if (bioRes.status === "fulfilled") setBiomarkers(Array.isArray(bioRes.value.data) ? bioRes.value.data : []);
    else setBErr("Failed to load biomarkers.");
    if (qRes.status === "fulfilled") setQuestions(Array.isArray(qRes.value.data) ? qRes.value.data : []);
    setBLoading(false);
    setQLoading(false);
  };

  const saveBiomarker = async () => {
    if (!selectedBmIdToAssign) { setBErr("Please select a biomarker."); return; }
    setBioSaving(true); setBErr("");
    try {
      await api.post(`/admin/biomarkers/${selectedBmIdToAssign}/clusters`, {
        cluster_id: selCluster.id,
        default_weight: assignWeight !== "" ? Number(assignWeight) : null,
        scored: assignScored,
      });
      const { data } = await api.get(`/admin/clusters/${selCluster.id}/biomarkers`);
      setBiomarkers(Array.isArray(data) ? data : []);
      setSelectedBmIdToAssign(""); setAssignWeight(""); setAssignScored("scored"); setShowBioForm(false);
    } catch (e) { setBErr(safeErrMsg(e, "Failed to assign biomarker.")); }
    finally { setBioSaving(false); }
  };

  const toggleBiomarker = async (bm) => {
    try {
      const { data } = await api.patch(`/admin/biomarkers/${bm.id}/clusters/${selCluster.id}/toggle`);
      setBiomarkers((prev) => prev.map((b) => b.id === data.biomarker_id ? { ...b, is_active: data.is_active } : b));
    } catch (e) { setBErr(safeErrMsg(e, "Failed to toggle biomarker.")); }
  };

  const saveEditBiomarker = async () => {
    setEditBmSaving(true); setBErr("");
    try {
      const payload = {
        default_weight: editBmForm.default_weight !== "" && editBmForm.default_weight != null
          ? Number(editBmForm.default_weight) : null,
        scored: editBmForm.scored || "scored",
      };
      const { data } = await api.patch(`/admin/biomarkers/${editBmId}/clusters/${selCluster.id}`, payload);
      setBiomarkers((prev) => prev.map((b) =>
        b.id === data.biomarker_id
          ? { ...b, default_weight: data.default_weight, scored: data.scored }
          : b
      ));
      setEditBmId(null); setEditBmForm({});
    } catch (e) { setBErr(safeErrMsg(e, "Failed to update assignment.")); }
    setEditBmSaving(false);
  };

  const removeBiomarker = (bm) => setRemoveBmConfirm({ open: true, bm });

  const confirmRemoveBiomarker = async () => {
    const bm = removeBmConfirm.bm;
    setRemoveBmConfirm({ open: false, bm: null });
    try {
      await api.delete(`/admin/biomarkers/${bm.id}/clusters/${selCluster.id}`);
      setBiomarkers((prev) => prev.filter((b) => b.id !== bm.id));
    } catch (e) { setBErr(safeErrMsg(e, "Failed to remove biomarker.")); }
  };

  const saveEditCluster = async () => {
    setEditClusterSaving(true); setErr("");
    try {
      const payload = {
        ...editClusterForm,
        cluster_id: editClusterForm.cluster_id || null,
        display_order: editClusterForm.display_order !== "" ? Number(editClusterForm.display_order) : null,
        score_code: editClusterForm.score_code || null,
        score_full_name: editClusterForm.score_full_name || null,
        user_facing_name: editClusterForm.user_facing_name || null,
        user_subtitle: editClusterForm.user_subtitle || null,
        tile_name: editClusterForm.tile_name || null,
        clinical_scope: editClusterForm.clinical_scope || null,
        what_moves_the_score: editClusterForm.what_moves_the_score || null,
        specialist_type: (editClusterForm.specialist_type || []).length > 0 ? editClusterForm.specialist_type.join("\n") : null,
        user_persona: (editClusterForm.user_persona || []).length > 0 ? editClusterForm.user_persona.join("\n") : null,
        composite_formula: editClusterForm.composite_formula || null,
        score_bands: (() => { const b = (editClusterForm.score_bands || []).filter((x) => x.label.trim()); return b.length > 0 ? JSON.stringify(b) : null; })(),
        score_intent: editClusterForm.score_intent || null,
        physician_oversight: editClusterForm.physician_oversight || null,
        status: editClusterForm.status || null,
        subjective_slot: editClusterForm.subjective_slot || null,
        lab_or_vital_markers: editClusterForm.lab_or_vital_markers !== "" && editClusterForm.lab_or_vital_markers != null ? Number(editClusterForm.lab_or_vital_markers) : null,
        required_lab_markers: editClusterForm.required_lab_markers !== "" && editClusterForm.required_lab_markers != null ? Number(editClusterForm.required_lab_markers) : null,
      };
      const { data } = await api.patch(`/admin/clusters/${editClusterId}`, payload);
      setClusters((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      if (selCluster?.id === data.id) setSelCluster(data);
      setEditClusterId(null); setEditClusterForm({});
    } catch (e) { setErr(safeErrMsg(e, "Failed to update cluster.")); }
    setEditClusterSaving(false);
  };

  /* ── question bank helpers ── */
  const loadQuestions = async (cluster) => {
    setQLoading(true); setQErr(""); setQuestions([]);
    try {
      const { data } = await api.get(`/admin/clusters/${cluster.id}/assessment-questions`);
      setQuestions(Array.isArray(data) ? data : []);
    } catch { setQErr("Failed to load questions."); }
    finally { setQLoading(false); }
  };

  const saveQuestion = async () => {
    setQSaving(true); setQErr("");
    try {
      const optionsArr = qForm.question_type === "multiple_choice"
        ? qForm.options.split(",").map((s) => s.trim()).filter(Boolean)
        : null;
      const payload = {
        question_text: qForm.question_text,
        question_type: qForm.question_type,
        options: optionsArr,
        maps_to_score_dimension: qForm.maps_to_score_dimension || null,
        default_weight: qForm.default_weight !== "" ? Number(qForm.default_weight) : 0,
        is_mandatory: qForm.is_mandatory,
        display_order: qForm.display_order !== "" ? Number(qForm.display_order) : null,
      };
      const { data } = await api.post(`/admin/clusters/${selCluster.id}/assessment-questions`, payload);
      setQuestions((prev) => [...prev, data]);
      setQForm(EMPTY_Q); setShowQForm(false);
    } catch (e) { setQErr(safeErrMsg(e, "Failed to add question.")); }
    setQSaving(false);
  };

  const saveEditQuestion = async () => {
    setQSaving(true); setQErr("");
    try {
      const optionsArr = editQForm.question_type === "multiple_choice"
        ? (editQForm.options || "").split(",").map((s) => s.trim()).filter(Boolean)
        : null;
      const payload = {
        ...editQForm,
        options: optionsArr,
        maps_to_score_dimension: editQForm.maps_to_score_dimension || null,
        default_weight: editQForm.default_weight !== "" ? Number(editQForm.default_weight) : 0,
        display_order: editQForm.display_order !== "" ? Number(editQForm.display_order) : null,
      };
      const { data } = await api.patch(`/admin/clusters/${selCluster.id}/assessment-questions/${editQId}`, payload);
      setQuestions((prev) => prev.map((q) => (q.id === data.id ? data : q)));
      setEditQId(null); setEditQForm({});
    } catch (e) { setQErr(safeErrMsg(e, "Failed to update question.")); }
    setQSaving(false);
  };

  const toggleQuestion = async (q) => {
    try {
      const { data } = await api.patch(`/admin/clusters/${selCluster.id}/assessment-questions/${q.id}/toggle`);
      setQuestions((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    } catch (e) { setQErr(safeErrMsg(e, "Failed to toggle question.")); }
  };

  const CLUSTER_GROUPS  = ["core", "amplifier", "condition_based"];
  const BIO_CATEGORIES  = ["Core", "Secondary", "Mechanistic", "Vital", "Optional", "Protective"];
  const isBusy          = saving || iconUploading;
  const bioCanSave      = !bioSaving && !!selectedBmIdToAssign;

  return (
    <div style={{ padding: "32px 28px 60px" }}>
      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: "0 0 2px", fontSize: 20, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Longevity Clusters</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
            {clusters.length} clusters · click a row to manage its biomarkers
          </p>
        </div>
        <Btn label="+ New Cluster" onClick={() => setShowForm((p) => !p)} />
      </div>
      <ErrBanner msg={err} onDismiss={() => setErr("")} />

      {/* ── New Cluster Form ── */}
      {showForm && (
        <div style={{ ...card, padding: "22px 24px", marginBottom: 20 }}>
          <p style={sectionHeading}>New Cluster</p>

          {/* Basic Identity */}
          <p style={{ margin: "0 0 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Basic Identity</p>
          <div className="ob-grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginBottom: 14 }}>
            {[["cluster_id","Cluster ID"], ["cluster_code","Code (immutable) *"], ["cluster_name","Name *"]].map(([k, l]) => (
              <div key={k}>
                <p style={{ ...labelTxt, margin: "0 0 5px" }}>{l}</p>
                <input value={form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
              </div>
            ))}
            <div>
              <p style={{ ...labelTxt, margin: "0 0 5px" }}>Group</p>
              <select value={form.cluster_group} onChange={(e) => setForm((p) => ({ ...p, cluster_group: e.target.value }))} style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
                {CLUSTER_GROUPS.map((g) => <option key={g} value={g}>{g.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div>
              <p style={{ ...labelTxt, margin: "0 0 5px" }}>Display Order</p>
              <input type="number" value={form.display_order} onChange={(e) => setForm((p) => ({ ...p, display_order: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
            </div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <p style={{ ...labelTxt, margin: "0 0 5px" }}>Description</p>
            <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={2} style={{ ...inputBase, display: "block", width: "100%", resize: "vertical" }} />
          </div>

          {/* Score & Display */}
          <p style={{ margin: "0 0 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Score & Display</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginBottom: 18 }}>
            {[["score_code","Score Code"],["score_full_name","Score Full Name"],["user_facing_name","User-Facing Name"],["user_subtitle","User Subtitle"],["tile_name","Tile Name"],["score_intent","Score Intent"]].map(([k, l]) => (
              <div key={k}>
                <p style={{ ...labelTxt, margin: "0 0 5px" }}>{l}</p>
                <input value={form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
              </div>
            ))}
          </div>

          {/* Clinical & Scoring */}
          <p style={{ margin: "0 0 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Clinical & Scoring</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14, marginBottom: 10 }}>
            {[["clinical_scope","Clinical Scope"],["what_moves_the_score","What Moves The Score"],["composite_formula","Composite Formula"]].map(([k, l]) => (
              <div key={k}>
                <p style={{ ...labelTxt, margin: "0 0 5px" }}>{l}</p>
                <textarea value={form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} rows={2} style={{ ...inputBase, display: "block", width: "100%", resize: "vertical" }} />
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14, marginBottom: 14 }}>
            {[["specialist_type","Specialist Type","e.g. Endocrinologist, Dietitian…"],["user_persona","User Persona","e.g. Mid-Life Risk Manager…"]].map(([k, l, ph]) => (
              <div key={k}>
                <p style={{ ...labelTxt, margin: "0 0 5px" }}>{l} <span style={{ fontSize: 10, color: "#A8C8D8", fontFamily: "Inter, sans-serif" }}>— type &amp; press Enter to add</span></p>
                <ClusterTagInput tags={form[k]} onChange={(v) => setForm((p) => ({ ...p, [k]: v }))} placeholder={ph} />
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 14 }}>
            <p style={{ ...labelTxt, margin: "0 0 6px" }}>Score Bands <span style={{ fontSize: 10, color: "#A8C8D8", fontFamily: "Inter, sans-serif" }}>— each band has a label, min and max score</span></p>
            <ScoreBandEditor bands={form.score_bands} onChange={(v) => setForm((p) => ({ ...p, score_bands: v }))} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14, marginBottom: 18 }}>
            {[["lab_or_vital_markers","Lab / Vital Markers"],["required_lab_markers","Required Lab Markers"]].map(([k, l]) => (
              <div key={k}>
                <p style={{ ...labelTxt, margin: "0 0 5px" }}>{l}</p>
                <input type="number" min="0" value={form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
              </div>
            ))}
          </div>

          {/* Flags */}
          <p style={{ margin: "0 0 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Flags & Configuration</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginBottom: 18 }}>
            <div>
              <p style={{ ...labelTxt, margin: "0 0 5px" }}>Status</p>
              <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
                <option value="">— select —</option>
                <option value="validated">Validated</option>
                <option value="provisional">Provisional</option>
              </select>
            </div>
            <div>
              <p style={{ ...labelTxt, margin: "0 0 5px" }}>Subjective Slot</p>
              <input value={form.subjective_slot} onChange={(e) => setForm((p) => ({ ...p, subjective_slot: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
            </div>
            <div>
              <p style={{ ...labelTxt, margin: "0 0 5px" }}>Physician Oversight</p>
              <input value={form.physician_oversight} onChange={(e) => setForm((p) => ({ ...p, physician_oversight: e.target.value }))} style={{ ...inputBase, width: "100%" }} placeholder="e.g. Required, Optional" />
            </div>
          </div>

          {/* Icon Upload */}
          <div style={{ marginBottom: 18 }}>
            <p style={{ ...labelTxt, margin: "0 0 8px" }}>Cluster Icon <span style={{ color: "#A8C8D8" }}>(optional · jpg, png, webp, svg · max 5 MB)</span></p>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {iconPreview ? (
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <img src={iconPreview} alt="Icon preview" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 10, border: "1px solid rgba(90,144,168,0.25)", background: "#F5FAFC" }} />
                  <button onClick={clearIcon} title="Remove icon" style={{ position: "absolute", top: -8, right: -8, width: 20, height: 20, borderRadius: "50%", background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.3)", color: "#5A90A8", cursor: "pointer", fontSize: 12, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>×</button>
                </div>
              ) : (
                <div style={{ width: 64, height: 64, borderRadius: 10, border: "1px dashed rgba(90,144,168,0.3)", background: "rgba(90,144,168,0.04)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ fontSize: 24, opacity: 0.35 }}>🖼</span>
                </div>
              )}
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 16px", borderRadius: 7, border: "1px solid rgba(90,144,168,0.3)", background: "rgba(90,144,168,0.08)", color: "#5A90A8", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
                {iconFile ? "Change Icon" : "Upload Icon"}
                <input type="file" accept=".jpg,.jpeg,.png,.webp,.svg" onChange={handleIconChange} style={{ display: "none" }} />
              </label>
              {iconFile && <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>{iconFile.name}</span>}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <Btn label={iconUploading ? "Uploading icon…" : saving ? "Creating…" : "Create"} disabled={isBusy || !form.cluster_code || !form.cluster_name} onClick={save} />
            <Btn label="Cancel" color="#7AAABB" bg="rgba(90,144,168,0.06)" border="rgba(90,144,168,0.2)" onClick={resetForm} />
          </div>
        </div>
      )}

      {/* ── Cluster Table ── */}
      {loading ? <Spinner /> : (
        <div className="admin-table-wrap" style={{ ...card, overflow: "hidden", marginBottom: selCluster ? 20 : 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
            <thead style={{ background: "rgba(90,144,168,0.05)" }}>
              <tr><Th w={48}>Icon</Th><Th>Code</Th><Th>Name</Th><Th>Group</Th><Th>Order</Th><Th>Active</Th><Th w={80}>Edit</Th></tr>
            </thead>
            <tbody>
              {clusters.map((c) => (
                <React.Fragment key={c.id}>
                  <TableRow onClick={() => { if (editClusterId !== c.id) selectCluster(c); }} selected={selCluster?.id === c.id}>
                    <Td>
                      {c.icon_url ? (
                        <img src={c.icon_url.startsWith("http") ? c.icon_url : `${UPLOADS_BASE}${c.icon_url}`} alt={c.cluster_name} style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 8, border: "1px solid rgba(90,144,168,0.18)", background: "#F5FAFC", display: "block" }} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(90,144,168,0.08)", border: "1px solid rgba(90,144,168,0.18)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>🧬</div>
                      )}
                    </Td>
                    <Td><code style={{ fontSize: 12, color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "2px 6px", borderRadius: 4, fontFamily: "monospace" }}>{c.cluster_code}</code></Td>
                    <Td style={{ fontWeight: 600 }}>{c.cluster_name}</Td>
                    <Td style={{ color: "#4D7A8E", textTransform: "capitalize" }}>{c.cluster_group?.replace(/_/g, " ")}</Td>
                    <Td style={{ color: "#7AAABB" }}>{c.display_order}</Td>
                    <Td><span style={{ fontSize: 12, fontWeight: 600, color: c.is_active ? "#16A34A" : "#DC2626" }}>{c.is_active ? "Active" : "Inactive"}</span></Td>
                    <Td>
                      <Btn small label="Edit" onClick={(e) => {
                        e.stopPropagation();
                        if (editClusterId === c.id) { setEditClusterId(null); return; }
                        setEditClusterId(c.id);
                        setEditClusterForm({
                          cluster_id: c.cluster_id || "",
                          cluster_name: c.cluster_name, cluster_group: c.cluster_group,
                          description: c.description || "", display_order: c.display_order ?? "", is_active: c.is_active,
                          score_code: c.score_code || "", score_full_name: c.score_full_name || "",
                          user_facing_name: c.user_facing_name || "", user_subtitle: c.user_subtitle || "",
                          tile_name: c.tile_name || "", clinical_scope: c.clinical_scope || "",
                          what_moves_the_score: c.what_moves_the_score || "",
                          specialist_type: c.specialist_type ? c.specialist_type.split("\n").filter(Boolean) : [],
                          user_persona: c.user_persona ? c.user_persona.split("\n").filter(Boolean) : [],
                          composite_formula: c.composite_formula || "",
                          score_bands: parseBands(c.score_bands),
                          score_intent: c.score_intent || "", physician_oversight: c.physician_oversight || "",
                          status: c.status || "", subjective_slot: c.subjective_slot || "",
                          lab_or_vital_markers: c.lab_or_vital_markers ?? "", required_lab_markers: c.required_lab_markers ?? "",
                        });
                      }} />
                    </Td>
                  </TableRow>
                  {editClusterId === c.id && (
                    <tr>
                      <td colSpan={7} style={{ padding: 0 }}>
                        <div style={{ background: "rgba(90,144,168,0.04)", borderTop: "1px solid rgba(90,144,168,0.12)", padding: "18px 20px" }} onClick={(e) => e.stopPropagation()}>
                          <p style={{ margin: "0 0 14px", fontSize: 11, fontWeight: 700, color: "#5A90A8", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "Inter, sans-serif" }}>Edit — {c.cluster_code}</p>

                          {/* Basic */}
                          <p style={{ margin: "0 0 8px", fontSize: 9, fontWeight: 700, color: "#9BBCCC", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "Inter, sans-serif" }}>Basic Identity</p>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, marginBottom: 14 }}>
                            <div>
                              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Cluster ID</p>
                              <input value={editClusterForm.cluster_id || ""} onChange={(e) => setEditClusterForm((p) => ({ ...p, cluster_id: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
                            </div>
                            <div>
                              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Name</p>
                              <input value={editClusterForm.cluster_name || ""} onChange={(e) => setEditClusterForm((p) => ({ ...p, cluster_name: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
                            </div>
                            <div>
                              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Group</p>
                              <select value={editClusterForm.cluster_group || "core"} onChange={(e) => setEditClusterForm((p) => ({ ...p, cluster_group: e.target.value }))} style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
                                {CLUSTER_GROUPS.map((g) => <option key={g} value={g}>{g.replace(/_/g, " ")}</option>)}
                              </select>
                            </div>
                            <div>
                              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Display Order</p>
                              <input type="number" value={editClusterForm.display_order ?? ""} onChange={(e) => setEditClusterForm((p) => ({ ...p, display_order: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
                            </div>
                            <div style={{ display: "flex", gap: 16, alignItems: "center", paddingTop: 18 }}>
                              {[["is_active","Active"]].map(([k, l]) => (
                                <label key={k} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                                  <div onClick={() => setEditClusterForm((p) => ({ ...p, [k]: !p[k] }))}
                                    style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${editClusterForm[k] ? "#5A90A8" : "rgba(90,144,168,0.3)"}`, background: editClusterForm[k] ? "#5A90A8" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    {editClusterForm[k] && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3 5.5L8 1" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                  </div>
                                  <span style={{ ...labelTxt, fontSize: 11 }}>{l}</span>
                                </label>
                              ))}
                            </div>
                            <div style={{ marginTop: 10 }}>
                              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Physician Oversight</p>
                              <input value={editClusterForm.physician_oversight || ""} onChange={(e) => setEditClusterForm((p) => ({ ...p, physician_oversight: e.target.value }))} style={{ ...inputBase, width: "100%" }} placeholder="e.g. Required, Optional" />
                            </div>
                          </div>
                          <div style={{ marginBottom: 14 }}>
                            <p style={{ ...labelTxt, margin: "0 0 4px" }}>Description</p>
                            <textarea value={editClusterForm.description || ""} onChange={(e) => setEditClusterForm((p) => ({ ...p, description: e.target.value }))} rows={2} style={{ ...inputBase, display: "block", width: "100%", resize: "vertical" }} />
                          </div>

                          {/* Score & Display */}
                          <p style={{ margin: "0 0 8px", fontSize: 9, fontWeight: 700, color: "#9BBCCC", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "Inter, sans-serif" }}>Score & Display</p>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, marginBottom: 14 }}>
                            {[["score_code","Score Code"],["score_full_name","Score Full Name"],["user_facing_name","User-Facing Name"],["user_subtitle","User Subtitle"],["tile_name","Tile Name"],["score_intent","Score Intent"],["subjective_slot","Subjective Slot"]].map(([k, l]) => (
                              <div key={k}>
                                <p style={{ ...labelTxt, margin: "0 0 4px" }}>{l}</p>
                                <input value={editClusterForm[k] || ""} onChange={(e) => setEditClusterForm((p) => ({ ...p, [k]: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
                              </div>
                            ))}
                          </div>

                          {/* Status */}
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, marginBottom: 14 }}>
                            <div>
                              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Status</p>
                              <select value={editClusterForm.status || ""} onChange={(e) => setEditClusterForm((p) => ({ ...p, status: e.target.value }))} style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
                                <option value="">— select —</option>
                                <option value="validated">Validated</option>
                                <option value="provisional">Provisional</option>
                              </select>
                            </div>
                          </div>

                          {/* Clinical & Scoring */}
                          <p style={{ margin: "0 0 8px", fontSize: 9, fontWeight: 700, color: "#9BBCCC", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "Inter, sans-serif" }}>Clinical & Scoring</p>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12, marginBottom: 10 }}>
                            {[["clinical_scope","Clinical Scope"],["what_moves_the_score","What Moves The Score"],["composite_formula","Composite Formula"]].map(([k, l]) => (
                              <div key={k}>
                                <p style={{ ...labelTxt, margin: "0 0 4px" }}>{l}</p>
                                <textarea value={editClusterForm[k] || ""} onChange={(e) => setEditClusterForm((p) => ({ ...p, [k]: e.target.value }))} rows={2} style={{ ...inputBase, display: "block", width: "100%", resize: "vertical" }} />
                              </div>
                            ))}
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12, marginBottom: 12 }}>
                            {[["specialist_type","Specialist Type","e.g. Endocrinologist, Dietitian…"],["user_persona","User Persona","e.g. Mid-Life Risk Manager…"]].map(([k, l, ph]) => (
                              <div key={k}>
                                <p style={{ ...labelTxt, margin: "0 0 4px" }}>{l} <span style={{ fontSize: 10, color: "#A8C8D8", fontFamily: "Inter, sans-serif" }}>— type &amp; press Enter to add</span></p>
                                <ClusterTagInput tags={editClusterForm[k] || []} onChange={(v) => setEditClusterForm((p) => ({ ...p, [k]: v }))} placeholder={ph} />
                              </div>
                            ))}
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <p style={{ ...labelTxt, margin: "0 0 6px" }}>Score Bands <span style={{ fontSize: 10, color: "#A8C8D8", fontFamily: "Inter, sans-serif" }}>— each band has a label, min and max score</span></p>
                            <ScoreBandEditor bands={editClusterForm.score_bands || []} onChange={(v) => setEditClusterForm((p) => ({ ...p, score_bands: v }))} />
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, marginBottom: 14 }}>
                            {[["lab_or_vital_markers","Lab / Vital Markers"],["required_lab_markers","Required Lab Markers"]].map(([k, l]) => (
                              <div key={k}>
                                <p style={{ ...labelTxt, margin: "0 0 4px" }}>{l}</p>
                                <input type="number" min="0" value={editClusterForm[k] ?? ""} onChange={(e) => setEditClusterForm((p) => ({ ...p, [k]: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
                              </div>
                            ))}
                          </div>

                          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                            <Btn label={editClusterSaving ? "Saving…" : "Save Changes"} disabled={editClusterSaving || !editClusterForm.cluster_name} onClick={saveEditCluster} />
                            <Btn label="Cancel" color="#7AAABB" bg="rgba(90,144,168,0.06)" border="rgba(90,144,168,0.2)" onClick={() => { setEditClusterId(null); setEditClusterForm({}); }} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {clusters.length === 0 && <tr><td colSpan={7} style={{ padding: 32, textAlign: "center", color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13 }}>No clusters yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Remove biomarker confirmation modal ── */}
      <ConfirmModal
        open={removeBmConfirm.open}
        title="Remove Biomarker"
        subtitle={`Remove "${removeBmConfirm.bm?.biomarker_name}" from this cluster?`}
        body="This will unassign the biomarker from the cluster. The biomarker itself will remain in the global library."
        confirmLabel="Remove"
        confirmColor="#DC2626"
        confirmBg="rgba(220,38,38,0.08)"
        confirmBorder="rgba(220,38,38,0.25)"
        onConfirm={confirmRemoveBiomarker}
        onCancel={() => setRemoveBmConfirm({ open: false, bm: null })}
      />

      {/* ── Cluster Detail Panel (shown when a cluster row is selected) ── */}
      {selCluster && (
        <div style={{ ...card, padding: "22px 24px" }} onClick={(e) => e.stopPropagation()}>

          {/* Panel header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 0 }}>
              {/* Cluster icon */}
              <div style={{ width: 52, height: 52, borderRadius: 12, overflow: "hidden", flexShrink: 0, border: "1px solid rgba(90,144,168,0.25)", background: "rgba(90,144,168,0.06)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {selCluster.icon_url
                  ? <img src={selCluster.icon_url.startsWith("http") ? selCluster.icon_url : `${API_BASE}${selCluster.icon_url}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <span style={{ fontSize: 24 }}>🧬</span>
                }
              </div>
              <div style={{ minWidth: 0 }}>
                <h3 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.015em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selCluster.cluster_name}
                </h3>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <code style={{ fontSize: 11, color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "2px 7px", borderRadius: 4, fontFamily: "monospace" }}>{selCluster.cluster_code}</code>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(90,144,168,0.08)", color: "#5A90A8", fontFamily: "Inter, sans-serif", fontWeight: 600, textTransform: "capitalize" }}>{selCluster.cluster_group?.replace(/_/g, " ")}</span>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, fontWeight: 700, fontFamily: "Inter, sans-serif", color: selCluster.is_active ? "#16A34A" : "#DC2626", background: selCluster.is_active ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.08)", border: `1px solid ${selCluster.is_active ? "rgba(22,163,74,0.25)" : "rgba(220,38,38,0.2)"}` }}>
                    {selCluster.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
            <Btn label="✕ Close" small color="#9BBCCC" bg="rgba(90,144,168,0.04)" border="rgba(90,144,168,0.15)"
              onClick={(e) => { e.stopPropagation(); setSelCluster(null); setBiomarkers([]); setShowBioForm(false); setBErr(""); setQuestions([]); setClusterSubTab("overview"); }} />
          </div>

          {/* Sub-tabs — scrollable on mobile */}
          <div style={{ display: "flex", gap: 2, marginBottom: 20, borderBottom: "1px solid rgba(90,144,168,0.15)", overflowX: "auto" }} className="sc-no-scrollbar">
            {[["overview", "Overview"], ["biomarkers", `Biomarkers (${biomarkers.length})`], ["questions", `Questions (${questions.length})`]].map(([key, label]) => (
              <button key={key} onClick={(e) => {
                e.stopPropagation();
                setClusterSubTab(key);
                if (key === "biomarkers" && biomarkers.length === 0 && !bLoading) {
                  setBLoading(true);
                  api.get(`/admin/clusters/${selCluster.id}/biomarkers`).then(({ data }) => setBiomarkers(Array.isArray(data) ? data : [])).catch(() => setBErr("Failed to load biomarkers.")).finally(() => setBLoading(false));
                }
              }} style={{ padding: "9px 18px", border: "none", background: "none", cursor: "pointer", fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: clusterSubTab === key ? 700 : 400, color: clusterSubTab === key ? "#2D6A84" : "#9BBCCC", borderBottom: clusterSubTab === key ? "2px solid #2D6A84" : "2px solid transparent", marginBottom: -1, transition: "all 150ms", whiteSpace: "nowrap", flexShrink: 0 }}>
                {label}
              </button>
            ))}
          </div>

          <ErrBanner msg={bErr} onDismiss={() => setBErr("")} />

          {/* ══ OVERVIEW SUB-TAB ══ */}
          {clusterSubTab === "overview" && (() => {
            const settings = Object.keys(adminClusterFieldSettings).length > 0 ? adminClusterFieldSettings : ADMIN_FIELD_DEFAULTS;
            const l1short = ADMIN_CLUSTER_FIELD_MAP.filter(({ key, multiline }) => !multiline && settings[key] === "L1").map(({ label, get }) => ({ label, val: get(selCluster) })).filter(({ val }) => val != null && val !== "");
            const l1text  = ADMIN_CLUSTER_FIELD_MAP.filter(({ key, multiline }) =>  multiline && settings[key] === "L1").map(({ label, get, scoreBands }) => ({ label, val: get(selCluster), scoreBands })).filter(({ val }) => val != null && val !== "");
            const l2      = ADMIN_CLUSTER_FIELD_MAP.filter(({ key }) => settings[key] === "L2").map(({ label, get, multiline, scoreBands }) => ({ label, val: get(selCluster), multiline, scoreBands })).filter(({ val }) => val != null && val !== "");
            return (
              <div>
                {/* ── Header: always-shown cluster identity ── */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 16 }}>
                  <div style={{ background: "rgba(90,144,168,0.04)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 14, padding: "16px 20px" }}>
                    <p style={{ ...sectionHeading, marginBottom: 12, fontSize: 9 }}>Identity</p>
                    <FieldRow label="Cluster Name" val={<strong style={{ color: "#1C3545", fontSize: 14 }}>{selCluster.cluster_name}</strong>} />
                    <FieldRow label="Cluster Code" val={<code style={{ fontSize: 13, color: "#5A90A8", background: "rgba(90,144,168,0.12)", padding: "3px 8px", borderRadius: 5, fontFamily: "monospace" }}>{selCluster.cluster_code}</code>} />
                    <FieldRow label="Group" val={<span style={{ textTransform: "capitalize", color: "#2D5A72", fontWeight: 600 }}>{selCluster.cluster_group?.replace(/_/g, " ")}</span>} />
                    <FieldRow label="Status" val={<span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, fontFamily: "Inter, sans-serif", color: selCluster.is_active ? "#16A34A" : "#DC2626", background: selCluster.is_active ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.08)" }}>{selCluster.is_active ? "Active" : "Inactive"}</span>} />
                    <FieldRow label="Display Order" val={selCluster.display_order ?? "—"} />
                  </div>
                  <div style={{ background: "rgba(90,144,168,0.04)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 14, padding: "16px 20px" }}>
                    <p style={{ ...sectionHeading, marginBottom: 12, fontSize: 9 }}>Stats</p>
                    <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                      <div style={{ textAlign: "center" }}>
                        <p style={{ margin: 0, fontSize: 28, fontWeight: 700, color: "#5A90A8", fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{bLoading ? "…" : biomarkers.length}</p>
                        <p style={{ margin: "4px 0 0", fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Biomarkers</p>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <p style={{ margin: 0, fontSize: 28, fontWeight: 700, color: "#2563EB", fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{qLoading ? "…" : questions.length}</p>
                        <p style={{ margin: "4px 0 0", fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Questions</p>
                      </div>
                    </div>
                    {selCluster.icon_url && (
                      <div style={{ marginTop: 14 }}>
                        <p style={{ margin: "0 0 8px", ...labelTxt }}>Icon</p>
                        <div style={{ width: 56, height: 56, borderRadius: 12, overflow: "hidden", border: "1px solid rgba(90,144,168,0.25)", background: "rgba(90,144,168,0.06)" }}>
                          <img src={selCluster.icon_url.startsWith("http") ? selCluster.icon_url : `${API_BASE}${selCluster.icon_url}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Description ── */}
                {selCluster.description && <InfoBlock label="Description" text={selCluster.description} mb={14} />}

                {/* ── L1 short fields ── */}
                {l1short.length > 0 && (
                  <div style={{ background: "rgba(90,144,168,0.04)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 14, padding: "16px 20px", marginBottom: 14 }}>
                    <p style={{ ...sectionHeading, marginBottom: 12, fontSize: 9 }}>Cluster Fields</p>
                    {l1short.map(({ label, val }) => <FieldRow key={label} label={label} val={val} />)}
                  </div>
                )}

                {/* ── L1 text/multiline fields ── */}
                {l1text.length > 0 && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14, marginBottom: 14 }}>
                    {l1text.map(({ label, val, scoreBands: isBands }) => {
                      if (isBands) {
                        const bands = parseBands(val);
                        if (bands.length === 0) return null;
                        return (
                          <div key={label} style={{ background: "rgba(90,144,168,0.04)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 12, padding: "14px 18px" }}>
                            <p style={{ margin: "0 0 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>Score Bands</p>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                              {bands.map((b, i) => (
                                <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 12px", borderRadius: 8, background: "rgba(90,144,168,0.08)", border: "1px solid rgba(90,144,168,0.2)", fontFamily: "Inter, sans-serif" }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1C3545" }}>{b.label}</span>
                                  {(b.min !== "" && b.min != null) || (b.max !== "" && b.max != null) ? (
                                    <span style={{ fontSize: 11, color: "#5A90A8" }}>{b.min != null && b.min !== "" ? b.min : "—"}{" – "}{b.max != null && b.max !== "" ? b.max : "—"}</span>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return <InfoBlock key={label} label={label} text={String(val)} />;
                    })}
                  </div>
                )}

                {/* ── L2 fields (collapsible) ── */}
                {l2.length > 0 && (
                  <div style={{ background: "rgba(90,144,168,0.04)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 14, padding: "14px 20px", marginBottom: 14 }}>
                    <AdminClusterL2Fields fields={l2} />
                  </div>
                )}

                {/* ── Quick Actions ── */}
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
                  <button onClick={(e) => { e.stopPropagation(); setClusterSubTab("biomarkers"); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 10, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.25)", color: "#5A90A8", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
                    Manage Biomarkers ({bLoading ? "…" : biomarkers.length})
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setClusterSubTab("questions"); if (questions.length === 0 && !qLoading) loadQuestions(selCluster); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 10, background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.22)", color: "#2563EB", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
                    Manage Questions ({qLoading ? "…" : questions.length})
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ══ BIOMARKERS SUB-TAB ══ */}
          {clusterSubTab === "biomarkers" && (<>

          {/* Header row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            {biomarkers.length > 0 ? (
              <div style={{ background: "rgba(90,144,168,0.06)", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 8, padding: "6px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Total Objective Weight</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>
                  {biomarkers.reduce((sum, b) => sum + (parseFloat(b.default_weight) || 0), 0).toFixed(4)}
                </span>
              </div>
            ) : <div />}
            <Btn label={showBioForm ? "Cancel" : "+ Assign Biomarker"} onClick={(e) => {
              e.stopPropagation();
              if (!showBioForm && bmLibrary.length === 0) {
                api.get("/admin/biomarkers").then(({ data }) => setBmLibrary(Array.isArray(data) ? data : []));
              }
              setShowBioForm((p) => !p); setSelectedBmIdToAssign(""); setAssignWeight(""); setAssignScored("scored"); setBErr("");
            }} />
          </div>

          {/* ── Assign Biomarker from Library Form ── */}
          {showBioForm && (
            <div style={{ background: "rgba(90,144,168,0.04)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}>
              <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 700, color: "#5A90A8", fontFamily: "Inter, sans-serif", letterSpacing: "0.1em", textTransform: "uppercase" }}>Assign Biomarker from Library</p>
              <p style={{ margin: "0 0 14px", fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>
                Pick a biomarker from the global library to add it to this cluster. To create a new biomarker, go to the Biomarker Library tab.
              </p>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <p style={{ ...labelTxt, margin: "0 0 5px" }}>Biomarker <span style={{ color: "#5A90A8" }}>*</span></p>
                  <select value={selectedBmIdToAssign} onChange={(e) => setSelectedBmIdToAssign(e.target.value)}
                    style={{ ...inputBase, minWidth: 320, cursor: "pointer" }}>
                    <option value="">— select biomarker —</option>
                    {bmLibrary
                      .filter((bm) => !biomarkers.some((b) => b.id === bm.id))
                      .map((bm) => (
                        <option key={bm.id} value={bm.id}>
                          {bm.biomarker_name} ({bm.biomarker_code}) · {bm.category}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <p style={{ ...labelTxt, margin: "0 0 5px" }}>Objective Weights</p>
                  <input type="number" step="0.01" min="0" value={assignWeight}
                    onChange={(e) => setAssignWeight(e.target.value)}
                    placeholder="e.g. 0.5"
                    style={{ ...inputBase, width: 120 }} />
                </div>
                <div>
                  <p style={{ ...labelTxt, margin: "0 0 5px" }}>Scored</p>
                  <select value={assignScored} onChange={(e) => setAssignScored(e.target.value)}
                    style={{ ...inputBase, cursor: "pointer" }}>
                    <option value="scored">Scored</option>
                    <option value="input">Input</option>
                  </select>
                </div>
                <Btn label={bioSaving ? "Assigning…" : "Assign to Cluster"} disabled={!bioCanSave} onClick={(e) => { e.stopPropagation(); saveBiomarker(); }} />
              </div>
            </div>
          )}

          {/* ── Biomarker Table ── */}
          {bLoading ? <Spinner /> : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                <thead style={{ background: "rgba(90,144,168,0.05)" }}>
                  <tr>
                    <Th>Name</Th><Th>Code</Th><Th>Category</Th><Th>Weight</Th><Th>Scored</Th><Th>Unit</Th>
                    <Th>Ref Range</Th><Th>Optimal Range</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {biomarkers.map((b) => (
                    <React.Fragment key={b.id}>
                      <TableRow>
                        <Td style={{ fontWeight: 600, color: "#1C3545" }}>{b.biomarker_name}</Td>
                        <Td><code style={{ fontSize: 11, color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "2px 6px", borderRadius: 4, fontFamily: "monospace" }}>{b.biomarker_code}</code></Td>
                        <Td style={{ textTransform: "capitalize", color: "#4D7A8E" }}>{b.category}</Td>
                        <Td style={{ color: "#2D6A84", fontWeight: 600, fontSize: 12 }}>{b.default_weight != null ? parseFloat(b.default_weight).toFixed(4) : "—"}</Td>
                        <Td><span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, fontFamily: "Inter, sans-serif", color: b.scored === "input" ? "#9333EA" : "#2D6A84", background: b.scored === "input" ? "rgba(147,51,234,0.08)" : "rgba(45,106,132,0.08)", border: `1px solid ${b.scored === "input" ? "rgba(147,51,234,0.2)" : "rgba(45,106,132,0.2)"}` }}>{b.scored || "scored"}</span></Td>
                        <Td style={{ color: "#7AAABB" }}>{b.unit || "—"}</Td>
                        <Td style={{ color: "#7AAABB", fontSize: 12 }}>{fmtRange(b.reference_range_min, b.reference_range_max, b.unit, b.reference_range_min_op, b.reference_range_max_op)}</Td>
                        <Td style={{ color: "#7AAABB", fontSize: 12 }}>{fmtRange(b.optimal_range_min, b.optimal_range_max, b.unit, b.optimal_range_min_op, b.optimal_range_max_op)}</Td>
                        <Td>
                          <div style={{ display: "flex", gap: 6 }}>
                            <Btn small label={editBmId === b.id ? "Cancel" : "Edit Assignment"} onClick={(e) => {
                              e.stopPropagation();
                              if (editBmId === b.id) { setEditBmId(null); return; }
                              setEditBmId(b.id);
                              setEditBmForm({
                                default_weight: b.default_weight ?? "",
                                scored: b.scored || "scored",
                              });
                            }} />
                            <Btn small label="Remove"
                              color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)"
                              onClick={(e) => { e.stopPropagation(); removeBiomarker(b); }} />
                          </div>
                        </Td>
                      </TableRow>
                      {editBmId === b.id && (
                        <tr>
                          <td colSpan={9} style={{ padding: 0 }}>
                            <div style={{ background: "rgba(90,144,168,0.04)", borderTop: "1px solid rgba(90,144,168,0.1)", padding: "12px 16px" }} onClick={(e) => e.stopPropagation()}>
                              <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, color: "#5A90A8", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "Inter, sans-serif" }}>Edit Assignment — {b.biomarker_name}</p>
                              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
                                <div>
                                  <p style={{ ...labelTxt, margin: "0 0 4px" }}>Objective Weight</p>
                                  <input type="number" step="0.01" min="0" value={editBmForm.default_weight ?? ""}
                                    onChange={(e) => setEditBmForm((p) => ({ ...p, default_weight: e.target.value }))}
                                    placeholder="e.g. 0.5" style={{ ...inputBase, width: 120 }} />
                                </div>
                                <div>
                                  <p style={{ ...labelTxt, margin: "0 0 4px" }}>Scored</p>
                                  <select value={editBmForm.scored || "scored"}
                                    onChange={(e) => setEditBmForm((p) => ({ ...p, scored: e.target.value }))}
                                    style={{ ...inputBase, cursor: "pointer" }}>
                                    <option value="scored">Scored</option>
                                    <option value="input">Input</option>
                                  </select>
                                </div>
                                <Btn label={editBmSaving ? "Saving…" : "Save"} disabled={editBmSaving} onClick={(e) => { e.stopPropagation(); saveEditBiomarker(); }} />
                                <Btn label="Cancel" color="#7AAABB" bg="rgba(90,144,168,0.06)" border="rgba(90,144,168,0.2)" onClick={(e) => { e.stopPropagation(); setEditBmId(null); }} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {biomarkers.length === 0 && (
                    <tr><td colSpan={9} style={{ padding: 36, textAlign: "center", color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13 }}>No biomarkers yet — assign the first one above</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          </>)}

          {/* ══ QUESTION BANK SUB-TAB ══ */}
          {clusterSubTab === "questions" && (<>
            <ErrBanner msg={qErr} onDismiss={() => setQErr("")} />

            {/* Header */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
              <Btn label={showQForm ? "Cancel" : "+ Add Question"} onClick={(e) => { e.stopPropagation(); setShowQForm((p) => !p); setQForm(EMPTY_Q); setQErr(""); }} />
            </div>

            {/* Add Question Form */}
            {showQForm && (
              <div style={{ background: "rgba(90,144,168,0.04)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 12, padding: "18px 20px", marginBottom: 20 }} onClick={(e) => e.stopPropagation()}>
                <p style={{ margin: "0 0 14px", fontSize: 11, fontWeight: 700, color: "#5A90A8", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "Inter, sans-serif" }}>New Master Question</p>
                <div style={{ marginBottom: 12 }}>
                  <p style={{ ...labelTxt, margin: "0 0 4px" }}>Question Text <span style={{ color: "#5A90A8" }}>*</span></p>
                  <textarea value={qForm.question_text} onChange={(e) => setQForm((p) => ({ ...p, question_text: e.target.value }))} rows={2} placeholder="Enter the question…" style={{ ...inputBase, display: "block", width: "100%", resize: "vertical" }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto", gap: 12, marginBottom: 12, alignItems: "end" }}>
                  <div>
                    <p style={{ ...labelTxt, margin: "0 0 4px" }}>Type <span style={{ color: "#5A90A8" }}>*</span></p>
                    <select value={qForm.question_type} onChange={(e) => setQForm((p) => ({ ...p, question_type: e.target.value }))} style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
                      {["text","number","boolean","multiple_choice","scale"].map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                    </select>
                  </div>
                  <div>
                    <p style={{ ...labelTxt, margin: "0 0 4px" }}>Dimension</p>
                    <input value={qForm.maps_to_score_dimension} onChange={(e) => setQForm((p) => ({ ...p, maps_to_score_dimension: e.target.value }))} placeholder="e.g. stress" style={{ ...inputBase, width: "100%" }} />
                  </div>
                  <div>
                    <p style={{ ...labelTxt, margin: "0 0 4px" }}>Weight (0–1)</p>
                    <input type="number" min="0" max="1" step="0.01" value={qForm.default_weight} onChange={(e) => setQForm((p) => ({ ...p, default_weight: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
                  </div>
                  <div>
                    <p style={{ ...labelTxt, margin: "0 0 4px" }}>Order</p>
                    <input type="number" value={qForm.display_order} onChange={(e) => setQForm((p) => ({ ...p, display_order: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
                  </div>
                  <div style={{ paddingBottom: 2 }}>
                    <p style={{ ...labelTxt, margin: "0 0 8px" }}>Mandatory</p>
                    <div onClick={() => setQForm((p) => ({ ...p, is_mandatory: !p.is_mandatory }))}
                      style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${qForm.is_mandatory ? "#5A90A8" : "rgba(90,144,168,0.3)"}`, background: qForm.is_mandatory ? "#5A90A8" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                      {qForm.is_mandatory && <svg width="11" height="8" viewBox="0 0 11 8" fill="none"><path d="M1 4L4 7L10 1" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                  </div>
                </div>
                {qForm.question_type === "multiple_choice" && (
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ ...labelTxt, margin: "0 0 4px" }}>Options <span style={{ color: "#9BBCCC", fontWeight: 400 }}>(comma-separated)</span> <span style={{ color: "#5A90A8" }}>*</span></p>
                    <input value={qForm.options} onChange={(e) => setQForm((p) => ({ ...p, options: e.target.value }))} placeholder="Option A, Option B, Option C" style={{ ...inputBase, width: "100%" }} />
                  </div>
                )}
                <Btn label={qSaving ? "Adding…" : "Add Question"} disabled={qSaving || !qForm.question_text || (qForm.question_type === "multiple_choice" && !qForm.options.trim())} onClick={(e) => { e.stopPropagation(); saveQuestion(); }} />
              </div>
            )}

            {/* Question Table */}
            {qLoading ? <Spinner /> : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
                  <thead style={{ background: "rgba(90,144,168,0.05)" }}>
                    <tr><Th>Question</Th><Th>Type</Th><Th>Dimension</Th><Th>Weight</Th><Th>Mandatory</Th><Th>Status</Th><Th>Actions</Th></tr>
                  </thead>
                  <tbody>
                    {questions.map((q) => (
                      <React.Fragment key={q.id}>
                        <TableRow>
                          <Td style={{ maxWidth: 280, whiteSpace: "normal", lineHeight: 1.5, fontSize: 13 }}>{q.question_text}</Td>
                          <Td style={{ textTransform: "capitalize", color: "#4D7A8E" }}>{q.question_type?.replace(/_/g, " ")}</Td>
                          <Td style={{ color: "#7AAABB" }}>{q.maps_to_score_dimension || "—"}</Td>
                          <Td style={{ fontWeight: 600 }}>{Number(q.default_weight).toFixed(2)}</Td>
                          <Td><span style={{ fontSize: 12, fontWeight: 600, color: q.is_mandatory ? "#5A90A8" : "#9BBCCC" }}>{q.is_mandatory ? "Yes" : "No"}</span></Td>
                          <Td><span style={{ fontSize: 12, fontWeight: 600, color: q.is_active ? "#16A34A" : "#DC2626" }}>{q.is_active ? "Active" : "Inactive"}</span></Td>
                          <Td>
                            <div style={{ display: "flex", gap: 6 }}>
                              <Btn small label={editQId === q.id ? "Cancel" : "Edit"} onClick={(e) => {
                                e.stopPropagation();
                                if (editQId === q.id) { setEditQId(null); return; }
                                setEditQId(q.id);
                                setEditQForm({
                                  question_text: q.question_text, question_type: q.question_type,
                                  options: Array.isArray(q.options) ? q.options.join(", ") : "",
                                  maps_to_score_dimension: q.maps_to_score_dimension || "",
                                  default_weight: q.default_weight, is_mandatory: q.is_mandatory,
                                  display_order: q.display_order ?? "", is_active: q.is_active,
                                });
                              }} />
                              <Btn small label={q.is_active ? "Deactivate" : "Reactivate"}
                                color={q.is_active ? "#DC2626" : "#16A34A"} bg={q.is_active ? "rgba(220,38,38,0.08)" : "rgba(22,163,74,0.08)"} border={q.is_active ? "rgba(220,38,38,0.25)" : "rgba(22,163,74,0.25)"}
                                onClick={(e) => { e.stopPropagation(); toggleQuestion(q); }} />
                            </div>
                          </Td>
                        </TableRow>
                        {editQId === q.id && (
                          <tr>
                            <td colSpan={7} style={{ padding: 0 }}>
                              <div style={{ background: "rgba(90,144,168,0.04)", borderTop: "1px solid rgba(90,144,168,0.1)", padding: "14px 16px" }} onClick={(e) => e.stopPropagation()}>
                                <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, color: "#5A90A8", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "Inter, sans-serif" }}>Edit Question</p>
                                <div style={{ marginBottom: 10 }}>
                                  <p style={{ ...labelTxt, margin: "0 0 4px" }}>Question Text</p>
                                  <textarea value={editQForm.question_text || ""} onChange={(e) => setEditQForm((p) => ({ ...p, question_text: e.target.value }))} rows={2} style={{ ...inputBase, display: "block", width: "100%", resize: "vertical" }} />
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto auto", gap: 10, marginBottom: 10, alignItems: "end" }}>
                                  <div>
                                    <p style={{ ...labelTxt, margin: "0 0 4px" }}>Type</p>
                                    <select value={editQForm.question_type || "text"} onChange={(e) => setEditQForm((p) => ({ ...p, question_type: e.target.value }))} style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
                                      {["text","number","boolean","multiple_choice","scale"].map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <p style={{ ...labelTxt, margin: "0 0 4px" }}>Dimension</p>
                                    <input value={editQForm.maps_to_score_dimension || ""} onChange={(e) => setEditQForm((p) => ({ ...p, maps_to_score_dimension: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
                                  </div>
                                  <div>
                                    <p style={{ ...labelTxt, margin: "0 0 4px" }}>Weight</p>
                                    <input type="number" min="0" max="1" step="0.01" value={editQForm.default_weight ?? ""} onChange={(e) => setEditQForm((p) => ({ ...p, default_weight: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
                                  </div>
                                  <div>
                                    <p style={{ ...labelTxt, margin: "0 0 4px" }}>Order</p>
                                    <input type="number" value={editQForm.display_order ?? ""} onChange={(e) => setEditQForm((p) => ({ ...p, display_order: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
                                  </div>
                                  <div style={{ paddingBottom: 2 }}>
                                    <p style={{ ...labelTxt, margin: "0 0 8px" }}>Mandatory</p>
                                    <div onClick={() => setEditQForm((p) => ({ ...p, is_mandatory: !p.is_mandatory }))}
                                      style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${editQForm.is_mandatory ? "#5A90A8" : "rgba(90,144,168,0.3)"}`, background: editQForm.is_mandatory ? "#5A90A8" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                                      {editQForm.is_mandatory && <svg width="11" height="8" viewBox="0 0 11 8" fill="none"><path d="M1 4L4 7L10 1" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                    </div>
                                  </div>
                                  <div style={{ paddingBottom: 2 }}>
                                    <p style={{ ...labelTxt, margin: "0 0 8px" }}>Active</p>
                                    <div onClick={() => setEditQForm((p) => ({ ...p, is_active: !p.is_active }))}
                                      style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${editQForm.is_active ? "#5A90A8" : "rgba(90,144,168,0.3)"}`, background: editQForm.is_active ? "#5A90A8" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                                      {editQForm.is_active && <svg width="11" height="8" viewBox="0 0 11 8" fill="none"><path d="M1 4L4 7L10 1" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                    </div>
                                  </div>
                                </div>
                                {editQForm.question_type === "multiple_choice" && (
                                  <div style={{ marginBottom: 10 }}>
                                    <p style={{ ...labelTxt, margin: "0 0 4px" }}>Options <span style={{ color: "#9BBCCC", fontWeight: 400 }}>(comma-separated)</span></p>
                                    <input value={editQForm.options || ""} onChange={(e) => setEditQForm((p) => ({ ...p, options: e.target.value }))} placeholder="Option A, Option B" style={{ ...inputBase, width: "100%" }} />
                                  </div>
                                )}
                                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                                  <Btn label={qSaving ? "Saving…" : "Save Changes"} disabled={qSaving || !editQForm.question_text} onClick={(e) => { e.stopPropagation(); saveEditQuestion(); }} />
                                  <Btn label="Cancel" color="#7AAABB" bg="rgba(90,144,168,0.06)" border="rgba(90,144,168,0.2)" onClick={(e) => { e.stopPropagation(); setEditQId(null); }} />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    {questions.length === 0 && (
                      <tr><td colSpan={7} style={{ padding: 36, textAlign: "center", color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13 }}>No questions yet — add the first one above</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>)}

        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   5b. BIOMARKERS SECTION  — global biomarker library
═══════════════════════════════════════════════════════════════════════════ */

const EMPTY_BIO_LIB = {
  biomarker_name: "", biomarker_code: "", category: "lab", unit: "",
  reference_range_min: "",     reference_range_min_op: "none",
  reference_range_max: "",     reference_range_max_op: "none",
  optimal_range_min: "",       optimal_range_min_op: "none",
  optimal_range_max: "",       optimal_range_max_op: "none",
  reference_range_min_female: "", reference_range_min_op_female: "none",
  reference_range_max_female: "", reference_range_max_op_female: "none",
  optimal_range_min_female: "",   optimal_range_min_op_female: "none",
  optimal_range_max_female: "",   optimal_range_max_op_female: "none",
  default_weight: "", is_mandatory: true, description: "", gender_marker: "Both",
  backend_field: "", data_type: [], score_direction: [], scoring_type: [],
  provenance: "", clinical_rationale: "",
  plausibility_min: "", plausibility_max: "",
  categorical_map: [],
  categorical: false,
};

function BiomarkersSection() {
  const [biomarkers, setBiomarkers]     = useState([]);
  const [clusters,   setClusters]       = useState([]);
  const [loading,    setLoading]        = useState(true);
  const [err,        setErr]            = useState("");

  const [showForm,   setShowForm]       = useState(false);
  const [form,       setForm]           = useState({ ...EMPTY_BIO_LIB });
  const [saving,     setSaving]         = useState(false);

  const [filterCluster, setFilterCluster] = useState("");

  const [editBmId,      setEditBmId]      = useState(null);
  const [editBmForm,    setEditBmForm]    = useState({});
  const [editBmSaving,  setEditBmSaving]  = useState(false);
  const [expandedBmId,  setExpandedBmId]  = useState(null);

  const [assigningBmId,      setAssigningBmId]      = useState(null);
  const [assignTargetCluster, setAssignTargetCluster] = useState("");
  const [assigning,           setAssigning]           = useState(false);

  const safeErr = (e, fb) => {
    if (!e?.response) return `${fb} (no server response)`;
    const d = e.response.data?.detail;
    if (!d) return `${fb} (HTTP ${e.response.status})`;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((v) => v?.msg || String(v)).join("; ");
    return `${fb} (HTTP ${e.response.status})`;
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [bmRes, clRes] = await Promise.allSettled([
        api.get("/admin/biomarkers"),
        api.get("/admin/clusters", { params: { active_only: false } }),
      ]);
      if (bmRes.status === "fulfilled") setBiomarkers(Array.isArray(bmRes.value.data) ? bmRes.value.data : []);
      else setErr("Failed to load biomarkers.");
      if (clRes.status === "fulfilled") setClusters(Array.isArray(clRes.value.data) ? clRes.value.data : []);
      setLoading(false);
    })();
  }, []);

  const saveBiomarker = async () => {
    setSaving(true); setErr("");
    try {
      const toNum = (v) => v !== "" && v != null ? Number(v) : null;
      const payload = {
        ...form,
        unit:                form.unit        || null,
        description:         form.description || null,
        reference_range_min: toNum(form.reference_range_min),
        reference_range_max: toNum(form.reference_range_max),
        optimal_range_min:   toNum(form.optimal_range_min),
        optimal_range_max:   toNum(form.optimal_range_max),
        reference_range_min_female: toNum(form.reference_range_min_female),
        reference_range_max_female: toNum(form.reference_range_max_female),
        optimal_range_min_female:   toNum(form.optimal_range_min_female),
        optimal_range_max_female:   toNum(form.optimal_range_max_female),
        default_weight:      Number(form.default_weight),
        backend_field:       form.backend_field || null,
        data_type:           form.data_type.length ? form.data_type : null,
        score_direction:     form.score_direction.length ? form.score_direction : null,
        scoring_type:        form.scoring_type.length ? form.scoring_type : null,
        provenance:          form.provenance || null,
        clinical_rationale:  form.clinical_rationale || null,
        plausibility_min:    form.plausibility_min !== "" ? parseFloat(form.plausibility_min) : null,
        plausibility_max:    form.plausibility_max !== "" ? parseFloat(form.plausibility_max) : null,
        categorical_map:     form.categorical_map.filter(r => r.label.trim()).length
                               ? Object.fromEntries(form.categorical_map.filter(r => r.label.trim()).map(r => [r.label.trim(), r.score !== "" ? Number(r.score) : 0]))
                               : null,
        categorical:         form.categorical || false,
      };
      const { data } = await api.post("/admin/biomarkers", payload);
      setBiomarkers((prev) => [data, ...prev]);
      setForm({ ...EMPTY_BIO_LIB });
      setShowForm(false);
    } catch (e) { setErr(safeErr(e, "Failed to create biomarker.")); }
    setSaving(false);
  };

  const saveEditBiomarker = async () => {
    setEditBmSaving(true); setErr("");
    try {
      const bm = biomarkers.find((b) => b.id === editBmId);
      if (!bm) return;
      const toNum = (v) => v !== "" && v != null ? Number(v) : null;
      const payload = {
        ...editBmForm,
        reference_range_min: toNum(editBmForm.reference_range_min),
        reference_range_max: toNum(editBmForm.reference_range_max),
        optimal_range_min:   toNum(editBmForm.optimal_range_min),
        optimal_range_max:   toNum(editBmForm.optimal_range_max),
        reference_range_min_female: toNum(editBmForm.reference_range_min_female),
        reference_range_max_female: toNum(editBmForm.reference_range_max_female),
        optimal_range_min_female:   toNum(editBmForm.optimal_range_min_female),
        optimal_range_max_female:   toNum(editBmForm.optimal_range_max_female),
        default_weight: editBmForm.default_weight !== "" ? Number(editBmForm.default_weight) : undefined,
        unit:               editBmForm.unit || null,
        description:        editBmForm.description || null,
        backend_field:      editBmForm.backend_field || null,
        data_type:          Array.isArray(editBmForm.data_type) && editBmForm.data_type.length ? editBmForm.data_type : null,
        score_direction:    Array.isArray(editBmForm.score_direction) && editBmForm.score_direction.length ? editBmForm.score_direction : null,
        scoring_type:       Array.isArray(editBmForm.scoring_type) && editBmForm.scoring_type.length ? editBmForm.scoring_type : null,
        provenance:         editBmForm.provenance || null,
        clinical_rationale: editBmForm.clinical_rationale || null,
        plausibility_min:   editBmForm.plausibility_min !== "" && editBmForm.plausibility_min != null ? parseFloat(editBmForm.plausibility_min) : null,
        plausibility_max:   editBmForm.plausibility_max !== "" && editBmForm.plausibility_max != null ? parseFloat(editBmForm.plausibility_max) : null,
        categorical_map:    Array.isArray(editBmForm.categorical_map) && editBmForm.categorical_map.filter(r => r.label.trim()).length
                               ? Object.fromEntries(editBmForm.categorical_map.filter(r => r.label.trim()).map(r => [r.label.trim(), r.score !== "" ? Number(r.score) : 0]))
                               : null,
        categorical:        editBmForm.categorical || false,
      };
      const { data } = await api.patch(`/admin/biomarkers/${editBmId}`, payload);
      setBiomarkers((prev) => prev.map((b) => b.id === data.id ? data : b));
      setEditBmId(null); setEditBmForm({});
    } catch (e) { setErr(safeErr(e, "Failed to update biomarker.")); }
    setEditBmSaving(false);
  };

  const toggleBiomarker = async (bm) => {
    try {
      const { data } = await api.patch(`/admin/biomarkers/${bm.id}/toggle`);
      setBiomarkers((prev) => prev.map((b) => b.id === data.id ? data : b));
    } catch (e) { setErr(safeErr(e, "Failed to toggle biomarker.")); }
  };

  const assignToCluster = async (bmId) => {
    if (!assignTargetCluster) { setErr("Please select a target cluster."); return; }
    setAssigning(true); setErr("");
    try {
      const { data } = await api.post(`/admin/biomarkers/${bmId}/clusters`, { cluster_id: assignTargetCluster });
      setBiomarkers((prev) => prev.map((b) => {
        if (b.id !== bmId) return b;
        return { ...b, cluster_assignments: [...(b.cluster_assignments || []), data] };
      }));
      setAssigningBmId(null); setAssignTargetCluster("");
    } catch (e) { setErr(safeErr(e, "Failed to assign biomarker to cluster.")); }
    setAssigning(false);
  };

  const BIO_CATS = ["Core", "Secondary", "Mechanistic", "Vital", "Optional", "Protective"];
  const canSave  = !saving && form.biomarker_name && form.biomarker_code && form.gender_marker;
  const displayed = filterCluster
    ? biomarkers.filter((b) => b.cluster_assignments?.some((a) => a.cluster_id === filterCluster))
    : biomarkers;

  const clusterLabel = (bm) => {
    const assignments = bm.cluster_assignments || [];
    if (assignments.length === 0) return "Unassigned";
    return assignments.map((a) => {
      const cl = a.cluster || clusters.find((c) => c.id === a.cluster_id);
      return cl ? cl.cluster_name : a.cluster_id?.slice(0, 8) + "…";
    }).join(", ");
  };
  const clusterCode = (bm) => {
    const assignments = bm.cluster_assignments || [];
    return assignments.map((a) => {
      const cl = a.cluster || clusters.find((c) => c.id === a.cluster_id);
      return cl?.cluster_code;
    }).filter(Boolean).join(", ") || null;
  };

  return (
    <div style={{ padding: "32px 28px 60px" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: "0 0 2px", fontSize: 20, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Biomarker Library</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
            {biomarkers.length} biomarker{biomarkers.length !== 1 ? "s" : ""} · create once, assign to multiple clusters
          </p>
        </div>
        <Btn label={showForm ? "Cancel" : "+ New Biomarker"} onClick={() => { setShowForm((p) => !p); setForm({ ...EMPTY_BIO_LIB }); setErr(""); }} />
      </div>

      <ErrBanner msg={err} onDismiss={() => setErr("")} />

      {/* ── New Biomarker Form ── */}
      {showForm && (
        <div style={{ background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 16, boxShadow: "0 2px 8px rgba(90,144,168,0.08)", padding: "22px 24px", marginBottom: 20 }}>
          <p style={sectionHeading}>New Biomarker</p>
          <p style={{ margin: "0 0 14px", fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>
            Biomarkers are created globally. Use "+ Cluster" on any row to assign a biomarker to one or more clusters.
          </p>

          {/* Row 1: Name · Code · Category · Gender · Unit · Categorical */}
          <div className="bio-lib-form-row" style={{ display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1fr auto", gap: 12, marginBottom: 12, alignItems: "end" }}>
            <div>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Name <span style={{ color: "#5A90A8" }}>*</span></p>
              <input value={form.biomarker_name} onChange={(e) => setForm((p) => ({ ...p, biomarker_name: e.target.value }))} placeholder="e.g. HbA1c" style={{ ...inputBase, width: "100%" }} />
            </div>
            <div>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Code <span style={{ color: "#5A90A8" }}>*</span></p>
              <input value={form.biomarker_code} onChange={(e) => setForm((p) => ({ ...p, biomarker_code: e.target.value.toUpperCase().replace(/\s/g, "") }))} placeholder="HBA1C" style={{ ...inputBase, width: "100%", fontFamily: "monospace", letterSpacing: "0.04em" }} />
            </div>
            <div>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Category <span style={{ color: "#5A90A8" }}>*</span></p>
              <select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
                {BIO_CATS.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Gender (marker) <span style={{ color: "#5A90A8" }}>*</span></p>
              <select value={form.gender_marker} onChange={(e) => setForm((p) => ({ ...p, gender_marker: e.target.value }))} style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
                {["Male", "Female", "Both"].map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Unit</p>
              <input value={form.unit} onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value }))} placeholder="%, mmol/L…" style={{ ...inputBase, width: "100%" }} />
            </div>
            <div style={{ paddingBottom: 2 }}>
              <p style={{ ...labelTxt, margin: "0 0 8px" }}>Categorical</p>
              <div onClick={() => setForm((p) => ({ ...p, categorical: !p.categorical }))}
                style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${form.categorical ? "#7C3AED" : "rgba(90,144,168,0.3)"}`, background: form.categorical ? "#7C3AED" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 150ms", flexShrink: 0 }}>
                {form.categorical && <svg width="11" height="8" viewBox="0 0 11 8" fill="none"><path d="M1 4L4 7L10 1" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
            </div>
          </div>

          {/* Range fields — gender-aware (hidden when categorical); Male blocks first, Female blocks after */}
          {!form.categorical && (() => {
            const isBoth = form.gender_marker === "Both";
            const rangeGroups = [
              { label: "Cutoff Range",
                primary:   [{ key: "reference_range_min", opKey: "reference_range_min_op", sub: "Low Cutoff" }, { key: "reference_range_max", opKey: "reference_range_max_op", sub: "High Cutoff" }],
                secondary: [{ key: "reference_range_min_female", opKey: "reference_range_min_op_female", sub: "Low Cutoff" }, { key: "reference_range_max_female", opKey: "reference_range_max_op_female", sub: "High Cutoff" }],
              },
              { label: "Optimal Range",
                primary:   [{ key: "optimal_range_min", opKey: "optimal_range_min_op", sub: "Optimal-Low" }, { key: "optimal_range_max", opKey: "optimal_range_max_op", sub: "Optimal-High" }],
                secondary: [{ key: "optimal_range_min_female", opKey: "optimal_range_min_op_female", sub: "Optimal-Low" }, { key: "optimal_range_max_female", opKey: "optimal_range_max_op_female", sub: "Optimal-High" }],
              },
            ];
            const renderBlock = (fields, label, genderTag) => (
              <div key={`${label}-${genderTag || "single"}`} style={{ background: "rgba(90,144,168,0.03)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                  <p style={{ ...labelTxt, margin: 0, fontWeight: 600 }}>{label}</p>
                  {genderTag && <span style={{ fontSize: 10, padding: "2px 9px", borderRadius: 999, fontFamily: "Inter, sans-serif", fontWeight: 700, background: genderTag === "Male" ? "rgba(37,99,235,0.08)" : "rgba(236,72,153,0.08)", color: genderTag === "Male" ? "#1D4ED8" : "#BE185D", border: `1px solid ${genderTag === "Male" ? "rgba(37,99,235,0.2)" : "rgba(236,72,153,0.2)"}` }}>{genderTag}</span>}
                  <span style={{ fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>optional — fill only the bounds you need</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {fields.map(({ key, opKey, sub }) => {
                    const op = form[opKey] || "none"; const isActive = op !== "none";
                    return (
                      <div key={key}>
                        <p style={{ ...labelTxt, margin: "0 0 5px" }}>{sub}</p>
                        <div style={{ display: "flex" }}>
                          <select value={op} onChange={(e) => setForm((p) => ({ ...p, [opKey]: e.target.value }))}
                            style={{ ...inputBase, flexShrink: 0, width: 130, borderRight: "none", borderRadius: "8px 0 0 8px", cursor: "pointer", fontWeight: 600, color: isActive ? "#1D4ED8" : "#9BBCCC" }}>
                            <option value="none">None</option>
                            <option value="lt">{"< (Less than)"}</option>
                            <option value="gt">{"> (Greater than)"}</option>
                          </select>
                          <input type="number" step="any" value={form[key] ?? ""} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                            placeholder="Value (optional)" style={{ ...inputBase, flex: 1, borderRadius: "0 8px 8px 0", minWidth: 0 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
            return (
              <>
                {rangeGroups.map(({ label, primary }) => renderBlock(primary, label, isBoth ? "Male" : null))}
                {isBoth && rangeGroups.map(({ label, secondary }) => renderBlock(secondary, label, "Female"))}
              </>
            );
          })()}

          {/* Plausibility Range — hidden when categorical */}
          {!form.categorical && (
            <div style={{ background: "rgba(90,144,168,0.03)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <p style={{ ...labelTxt, margin: 0, fontWeight: 600 }}>Plausibility Range</p>
                <span style={{ fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>absolute physiological limits — values outside are implausible</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <p style={{ ...labelTxt, margin: "0 0 5px" }}>Min</p>
                  <input type="number" step="any" value={form.plausibility_min} onChange={(e) => setForm((p) => ({ ...p, plausibility_min: e.target.value }))} placeholder="e.g. 0" style={{ ...inputBase, width: "100%" }} />
                </div>
                <div>
                  <p style={{ ...labelTxt, margin: "0 0 5px" }}>Max</p>
                  <input type="number" step="any" value={form.plausibility_max} onChange={(e) => setForm((p) => ({ ...p, plausibility_max: e.target.value }))} placeholder="e.g. 500" style={{ ...inputBase, width: "100%" }} />
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          <div style={{ marginBottom: 12 }}>
            <p style={{ ...labelTxt, margin: "0 0 4px" }}>Description</p>
            <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={2} style={{ ...inputBase, display: "block", width: "100%", resize: "vertical" }} />
          </div>

          {/* Extended metadata */}
          <div style={{ background: "rgba(90,144,168,0.03)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
            <p style={{ ...labelTxt, margin: "0 0 10px", fontWeight: 600 }}>Extended Metadata</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div>
                <p style={{ ...labelTxt, margin: "0 0 4px" }}>Backend Field</p>
                <input value={form.backend_field} onChange={(e) => setForm((p) => ({ ...p, backend_field: e.target.value }))} placeholder="e.g. hs_crp_mgl" style={{ ...inputBase, width: "100%", fontFamily: "monospace" }} />
              </div>
              <div>
                <p style={{ ...labelTxt, margin: "0 0 4px" }}>Provenance</p>
                <input value={form.provenance} onChange={(e) => setForm((p) => ({ ...p, provenance: e.target.value }))} placeholder="inherited / authored" style={{ ...inputBase, width: "100%" }} />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div>
                <p style={{ ...labelTxt, margin: "0 0 4px" }}>Data Type</p>
                <BioTagInput tags={form.data_type} onChange={(v) => setForm((p) => ({ ...p, data_type: v }))} placeholder="e.g. Blood" suggestions={["Blood", "Urine", "Saliva", "Stool", "Tissue", "Breath"]} />
              </div>
              <div>
                <p style={{ ...labelTxt, margin: "0 0 4px" }}>Score Direction</p>
                <BioTagInput tags={form.score_direction} onChange={(v) => setForm((p) => ({ ...p, score_direction: v }))} placeholder="e.g. HIGHER_IS_WORSE" suggestions={["HIGHER_IS_WORSE", "LOWER_IS_WORSE", "WITHIN_RANGE", "CATEGORICAL"]} />
              </div>
              <div>
                <p style={{ ...labelTxt, margin: "0 0 4px" }}>Scoring Type</p>
                <BioTagInput tags={form.scoring_type} onChange={(v) => setForm((p) => ({ ...p, scoring_type: v }))} placeholder="e.g. PIECEWISE-LINEAR" suggestions={["PIECEWISE-LINEAR", "CATEGORICAL-PANEL"]} />
              </div>
            </div>
            <div>
              <p style={{ ...labelTxt, margin: "0 0 4px" }}>Clinical Rationale</p>
              <textarea value={form.clinical_rationale} onChange={(e) => setForm((p) => ({ ...p, clinical_rationale: e.target.value }))} rows={2} placeholder="Clinical explanation…" style={{ ...inputBase, display: "block", width: "100%", resize: "vertical" }} />
            </div>
            {form.categorical && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <p style={{ ...labelTxt, margin: 0, fontWeight: 600 }}>Categorical Map</p>
                  <span style={{ fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>category label → score value</span>
                </div>
                <CategoricalMapEditor rows={form.categorical_map} onChange={(v) => setForm((p) => ({ ...p, categorical_map: v }))} />
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <Btn label={saving ? "Creating…" : "Create Biomarker"} disabled={!canSave} onClick={saveBiomarker} />
            <Btn label="Cancel" color="#7AAABB" bg="rgba(90,144,168,0.06)" border="rgba(90,144,168,0.2)" onClick={() => { setShowForm(false); setForm({ ...EMPTY_BIO_LIB }); }} />
          </div>
        </div>
      )}

      {/* ── Filter bar ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <p style={{ ...labelTxt, margin: 0, flexShrink: 0 }}>Filter:</p>
        <select value={filterCluster} onChange={(e) => setFilterCluster(e.target.value)}
          style={{ ...inputBase, cursor: "pointer", minWidth: 200, maxWidth: 320 }}>
          <option value="">All clusters ({biomarkers.length})</option>
          {clusters.map((c) => {
            const count = biomarkers.filter((b) => b.cluster_assignments?.some((a) => a.cluster_id === c.id)).length;
            return <option key={c.id} value={c.id}>{c.cluster_name} ({count})</option>;
          })}
        </select>
        {filterCluster && (
          <button onClick={() => setFilterCluster("")}
            style={{ fontSize: 12, color: "#5A90A8", background: "none", border: "none", cursor: "pointer", fontFamily: "Inter, sans-serif", padding: "2px 0" }}>
            Clear
          </button>
        )}
      </div>

      {/* ── Biomarker Card List ── */}
      {loading ? <Spinner /> : (
        displayed.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13 }}>
            {filterCluster ? "No biomarkers in this cluster." : "No biomarkers yet — create the first one above."}
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {displayed.map((b) => {
              const isExpanded = expandedBmId === b.id;
              const isEditing  = editBmId === b.id;
              const isAssigning = assigningBmId === b.id;
              return (
                <div key={b.id} style={{ background: "#FFFFFF", border: `1px solid ${isExpanded ? "rgba(90,144,168,0.3)" : "rgba(90,144,168,0.15)"}`, borderRadius: 12, overflow: "hidden", boxShadow: isExpanded ? "0 4px 16px rgba(90,144,168,0.1)" : "0 1px 4px rgba(90,144,168,0.06)", transition: "all 200ms" }}>

                  {/* ── Summary bar ── */}
                  <div onClick={() => { setExpandedBmId((p) => p === b.id ? null : b.id); if (isEditing) setEditBmId(null); }}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", cursor: "pointer", background: isExpanded ? "rgba(90,144,168,0.04)" : "transparent" }}>
                    {/* Left: name + code + cluster + category */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{b.biomarker_name}</span>
                        <code style={{ fontSize: 11, color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "2px 7px", borderRadius: 4, fontFamily: "monospace" }}>{b.biomarker_code}</code>
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(90,144,168,0.08)", color: "#4D7A8E", fontFamily: "Inter, sans-serif", textTransform: "capitalize" }}>{b.category}</span>
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(124,58,237,0.07)", color: "#7C3AED", fontFamily: "Inter, sans-serif" }}>{clusterLabel(b)}</span>
                        {b.is_mandatory && <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 999, background: "rgba(90,144,168,0.12)", color: "#5A90A8", fontFamily: "Inter, sans-serif", fontWeight: 600 }}>Mandatory</span>}
                        {!b.is_active && <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 999, background: "rgba(220,38,38,0.08)", color: "#DC2626", fontFamily: "Inter, sans-serif", fontWeight: 600 }}>Inactive</span>}
                      </div>
                      {b.description && <p style={{ margin: "4px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 600 }}>{b.description}</p>}
                    </div>
                    {/* Right: quick stats */}
                    <div style={{ display: "flex", gap: 20, flexShrink: 0, alignItems: "center" }}>
                      <div style={{ textAlign: "right" }}>
                        <p style={{ margin: 0, fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.06em" }}>Weight</p>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{typeof b.default_weight === "number" ? b.default_weight.toFixed(3) : Number(b.default_weight).toFixed(3)}</p>
                      </div>
                      {b.unit && <div style={{ textAlign: "right" }}>
                        <p style={{ margin: 0, fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.06em" }}>Unit</p>
                        <p style={{ margin: 0, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif" }}>{b.unit}</p>
                      </div>}
                    </div>
                    {/* Chevron */}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9BBCCC" strokeWidth="2.2" strokeLinecap="round" style={{ transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 200ms", flexShrink: 0 }}><polyline points="6 9 12 15 18 9"/></svg>
                  </div>

                  {/* ── Expanded detail panel ── */}
                  {isExpanded && !isEditing && (
                    <div style={{ borderTop: "1px solid rgba(90,144,168,0.1)", padding: "16px 18px" }}>
                      {/* Action buttons */}
                      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                        <Btn small label="Edit" onClick={(e) => { e.stopPropagation(); setEditBmId(b.id); setAssigningBmId(null); setEditBmForm({ biomarker_name: b.biomarker_name, category: b.category, gender_marker: b.gender_marker || "Both", unit: b.unit || "", reference_range_min: b.reference_range_min ?? "", reference_range_min_op: b.reference_range_min_op || "none", reference_range_max: b.reference_range_max ?? "", reference_range_max_op: b.reference_range_max_op || "none", optimal_range_min: b.optimal_range_min ?? "", optimal_range_min_op: b.optimal_range_min_op || "none", optimal_range_max: b.optimal_range_max ?? "", optimal_range_max_op: b.optimal_range_max_op || "none", default_weight: b.default_weight, is_mandatory: b.is_mandatory, description: b.description || "", backend_field: b.backend_field || "", data_type: Array.isArray(b.data_type) ? b.data_type : [], score_direction: Array.isArray(b.score_direction) ? b.score_direction : [], scoring_type: Array.isArray(b.scoring_type) ? b.scoring_type : [], provenance: b.provenance || "", clinical_rationale: b.clinical_rationale || "", plausibility_min: b.plausibility_min ?? "", plausibility_max: b.plausibility_max ?? "", categorical_map: b.categorical_map && typeof b.categorical_map === "object" ? Object.entries(b.categorical_map).map(([label, score]) => ({ label, score: String(score) })) : [], categorical: b.categorical || false, reference_range_min_female: b.reference_range_min_female ?? "", reference_range_min_op_female: b.reference_range_min_op_female || "none", reference_range_max_female: b.reference_range_max_female ?? "", reference_range_max_op_female: b.reference_range_max_op_female || "none", optimal_range_min_female: b.optimal_range_min_female ?? "", optimal_range_min_op_female: b.optimal_range_min_op_female || "none", optimal_range_max_female: b.optimal_range_max_female ?? "", optimal_range_max_op_female: b.optimal_range_max_op_female || "none" }); }} />
                        <Btn small label={b.is_active ? "Deactivate" : "Reactivate"} color={b.is_active ? "#DC2626" : "#16A34A"} bg={b.is_active ? "rgba(220,38,38,0.08)" : "rgba(22,163,74,0.08)"} border={b.is_active ? "rgba(220,38,38,0.25)" : "rgba(22,163,74,0.25)"} onClick={(e) => { e.stopPropagation(); toggleBiomarker(b); }} />
                        <Btn small label={isAssigning ? "Cancel Assign" : "+ Assign Cluster"} color="#7C3AED" bg="rgba(124,58,237,0.08)" border="rgba(124,58,237,0.25)" onClick={(e) => { e.stopPropagation(); if (isAssigning) { setAssigningBmId(null); setAssignTargetCluster(""); } else { setAssigningBmId(b.id); } }} />
                      </div>

                      {/* Detail grid */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
                        {/* Core info */}
                        <div>
                          <p style={{ margin: "0 0 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>Core Info</p>
                          <div style={{ display: "grid", gap: 6 }}>
                            {[["Category", b.category, true], ["Gender", b.gender_marker || "—", false], ["Unit", b.unit || "—", false], ["Default Weight", (typeof b.default_weight === "number" ? b.default_weight : Number(b.default_weight)).toFixed(4), false], ["Mandatory", b.is_mandatory ? "Yes" : "No", false], ["Categorical", b.categorical ? "Yes" : "No", false], ["Status", b.is_active ? "Active" : "Inactive", false], ["Cluster", clusterLabel(b), false]].map(([lbl, val, cap]) => (
                              <div key={lbl} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", flexShrink: 0 }}>{lbl}</span>
                                <span style={{ fontSize: 12, color: "#2D5A72", fontFamily: "Inter, sans-serif", textAlign: "right", textTransform: cap ? "capitalize" : "none" }}>{val}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Ranges — hidden for categorical biomarkers */}
                        {!b.categorical && <div>
                          <p style={{ margin: "0 0 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>Ranges</p>
                          <div style={{ display: "grid", gap: 6 }}>
                            {(() => {
                              const isBoth = b.gender_marker === "Both";
                              const rows = [];
                              if (isBoth) {
                                rows.push(["Low Cutoff — Male",   fmtRange(b.reference_range_min, b.reference_range_max, b.unit, b.reference_range_min_op, b.reference_range_max_op)]);
                                rows.push(["Low Cutoff — Female", fmtRange(b.reference_range_min_female, b.reference_range_max_female, b.unit, b.reference_range_min_op_female, b.reference_range_max_op_female)]);
                                rows.push(["Optimal — Male",      fmtRange(b.optimal_range_min, b.optimal_range_max, b.unit, b.optimal_range_min_op, b.optimal_range_max_op)]);
                                rows.push(["Optimal — Female",    fmtRange(b.optimal_range_min_female, b.optimal_range_max_female, b.unit, b.optimal_range_min_op_female, b.optimal_range_max_op_female)]);
                              } else {
                                rows.push(["Low / High Cutoff", fmtRange(b.reference_range_min, b.reference_range_max, b.unit, b.reference_range_min_op, b.reference_range_max_op)]);
                                rows.push(["Optimal-Low / High",  fmtRange(b.optimal_range_min, b.optimal_range_max, b.unit, b.optimal_range_min_op, b.optimal_range_max_op)]);
                              }
                              rows.push(["Plausibility Range", (b.plausibility_min != null || b.plausibility_max != null) ? `${b.plausibility_min ?? "—"} – ${b.plausibility_max ?? "—"}` : "—"]);
                              return rows.map(([lbl, val]) => (
                                <div key={lbl} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                  <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", flexShrink: 0 }}>{lbl}</span>
                                  <span style={{ fontSize: 12, color: "#2D5A72", fontFamily: "Inter, sans-serif", textAlign: "right" }}>{val}</span>
                                </div>
                              ));
                            })()}
                          </div>
                          {b.description && (
                            <div style={{ marginTop: 14 }}>
                              <p style={{ margin: "0 0 4px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>Description</p>
                              <p style={{ margin: 0, fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>{b.description}</p>
                            </div>
                          )}
                        </div>}
                        {/* Categorical Map panel — shown only for categorical biomarkers */}
                        {b.categorical && b.categorical_map && Object.keys(b.categorical_map).length > 0 && (
                          <div>
                            <p style={{ margin: "0 0 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#7C3AED", fontFamily: "Inter, sans-serif" }}>Categorical Map</p>
                            <div style={{ display: "grid", gap: 4 }}>
                              {Object.entries(b.categorical_map).map(([lbl, score]) => (
                                <div key={lbl} style={{ display: "flex", justifyContent: "space-between", padding: "5px 10px", background: "rgba(124,58,237,0.04)", borderRadius: 6, border: "1px solid rgba(124,58,237,0.1)" }}>
                                  <span style={{ fontSize: 12, color: "#4D3A72", fontFamily: "Inter, sans-serif" }}>{lbl}</span>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: "#7C3AED", fontFamily: "monospace" }}>{score}</span>
                                </div>
                              ))}
                            </div>
                            {b.description && (
                              <div style={{ marginTop: 14 }}>
                                <p style={{ margin: "0 0 4px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>Description</p>
                                <p style={{ margin: 0, fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>{b.description}</p>
                              </div>
                            )}
                          </div>
                        )}
                        {b.categorical && !(b.categorical_map && Object.keys(b.categorical_map).length) && (
                          <div>
                            <p style={{ margin: "0 0 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#7C3AED", fontFamily: "Inter, sans-serif" }}>Categorical Map</p>
                            <p style={{ margin: 0, fontSize: 12, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No category mappings defined yet.</p>
                            {b.description && (
                              <div style={{ marginTop: 14 }}>
                                <p style={{ margin: "0 0 4px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>Description</p>
                                <p style={{ margin: 0, fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>{b.description}</p>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Extended metadata */}
                        <div>
                          <p style={{ margin: "0 0 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>Extended Metadata</p>
                          <div style={{ display: "grid", gap: 8 }}>
                            {b.backend_field && (
                              <div style={{ padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Backend Field</span>
                                <code style={{ display: "block", marginTop: 2, fontSize: 11, color: "#5A90A8", background: "rgba(90,144,168,0.08)", padding: "2px 6px", borderRadius: 4 }}>{b.backend_field}</code>
                              </div>
                            )}
                            {b.provenance && (
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", flexShrink: 0 }}>Provenance</span>
                                <span style={{ fontSize: 12, color: "#2D5A72", fontFamily: "Inter, sans-serif" }}>{b.provenance}</span>
                              </div>
                            )}
                            {b.data_type?.length > 0 && (
                              <div style={{ padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", display: "block", marginBottom: 4 }}>Data Type</span>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                  {b.data_type.map((t) => <span key={t} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.25)", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>{t}</span>)}
                                </div>
                              </div>
                            )}
                            {b.score_direction?.length > 0 && (
                              <div style={{ padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", display: "block", marginBottom: 4 }}>Score Direction</span>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                  {b.score_direction.map((t) => <span key={t} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", color: "#B45309", fontFamily: "Inter, sans-serif" }}>{t}</span>)}
                                </div>
                              </div>
                            )}
                            {b.scoring_type?.length > 0 && (
                              <div style={{ padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", display: "block", marginBottom: 4 }}>Scoring Type</span>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                  {b.scoring_type.map((t) => <span key={t} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(37,99,235,0.07)", border: "1px solid rgba(37,99,235,0.2)", color: "#1D4ED8", fontFamily: "Inter, sans-serif" }}>{t}</span>)}
                                </div>
                              </div>
                            )}
                            {b.clinical_rationale && (
                              <div style={{ padding: "5px 0" }}>
                                <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", display: "block", marginBottom: 4 }}>Clinical Rationale</span>
                                <p style={{ margin: 0, fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>{b.clinical_rationale}</p>
                              </div>
                            )}
                            {!b.categorical && !b.backend_field && !b.provenance && !b.data_type?.length && !b.score_direction?.length && !b.scoring_type?.length && !b.clinical_rationale && b.plausibility_min == null && b.plausibility_max == null && (
                              <p style={{ margin: 0, fontSize: 12, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No extended metadata.</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* ── Assign-to-cluster panel (inline, shown when + Assign Cluster clicked) ── */}
                      {isAssigning && (
                        <div style={{ marginTop: 16, background: "rgba(124,58,237,0.04)", border: "1px solid rgba(124,58,237,0.15)", borderRadius: 8, padding: "14px 16px" }} onClick={(e) => e.stopPropagation()}>
                          <p style={{ margin: "0 0 12px", fontSize: 11, fontWeight: 700, color: "#7C3AED", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "Inter, sans-serif" }}>
                            Assign <code style={{ background: "rgba(124,58,237,0.1)", padding: "1px 6px", borderRadius: 4, fontSize: 11 }}>{b.biomarker_code}</code> to Another Cluster
                          </p>
                          <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
                            <div>
                              <p style={{ ...labelTxt, margin: "0 0 5px" }}>Target Cluster <span style={{ color: "#7C3AED" }}>*</span></p>
                              <select value={assignTargetCluster} onChange={(e) => setAssignTargetCluster(e.target.value)}
                                style={{ ...inputBase, minWidth: 260, cursor: "pointer" }}>
                                <option value="">— select cluster —</option>
                                {clusters
                                  .filter((c) => !b.cluster_assignments?.some((a) => a.cluster_id === c.id))
                                  .map((c) => <option key={c.id} value={c.id}>{c.cluster_name} ({c.cluster_code})</option>)}
                              </select>
                            </div>
                            <Btn label={assigning ? "Assigning…" : "Assign to Cluster"} disabled={!assignTargetCluster || assigning}
                              color="#7C3AED" bg="rgba(124,58,237,0.12)" border="rgba(124,58,237,0.3)"
                              onClick={(e) => { e.stopPropagation(); assignToCluster(b.id); }} />
                          </div>
                          <p style={{ margin: "10px 0 0", fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>
                            Assigns {b.biomarker_name} to the selected cluster. Per-cluster weight and mandatory settings can be customised after assignment.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Edit form (in-card) ── */}
                  {isEditing && (
                    <div style={{ borderTop: "1px solid rgba(90,144,168,0.15)", padding: "16px 18px", background: "rgba(90,144,168,0.025)" }} onClick={(e) => e.stopPropagation()}>
                      <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, color: "#5A90A8", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "Inter, sans-serif" }}>Edit — {b.biomarker_code}</p>
                      {/* Row 1 — core identity fields */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10, marginBottom: 10, alignItems: "end" }}>
                        <div style={{ gridColumn: "span 2" }}>
                          <p style={{ ...labelTxt, margin: "0 0 4px" }}>Name <span style={{ color: "#5A90A8" }}>*</span></p>
                          <input value={editBmForm.biomarker_name || ""} onChange={(e) => setEditBmForm((p) => ({ ...p, biomarker_name: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
                        </div>
                        <div>
                          <p style={{ ...labelTxt, margin: "0 0 4px" }}>Category</p>
                          <select value={editBmForm.category || "lab"} onChange={(e) => setEditBmForm((p) => ({ ...p, category: e.target.value }))} style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
                            {BIO_CATS.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                          </select>
                        </div>
                        <div>
                          <p style={{ ...labelTxt, margin: "0 0 4px" }}>Gender (marker)</p>
                          <select value={editBmForm.gender_marker || "Both"} onChange={(e) => setEditBmForm((p) => ({ ...p, gender_marker: e.target.value }))} style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
                            {["Male", "Female", "Both"].map((g) => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </div>
                        <div>
                          <p style={{ ...labelTxt, margin: "0 0 4px" }}>Unit</p>
                          <input value={editBmForm.unit || ""} onChange={(e) => setEditBmForm((p) => ({ ...p, unit: e.target.value }))} style={{ ...inputBase, width: "100%" }} />
                        </div>
                        <div style={{ paddingBottom: 2 }}>
                          <p style={{ ...labelTxt, margin: "0 0 8px" }}>Categorical</p>
                          <div onClick={() => setEditBmForm((p) => ({ ...p, categorical: !p.categorical }))}
                            style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${editBmForm.categorical ? "#7C3AED" : "rgba(90,144,168,0.3)"}`, background: editBmForm.categorical ? "#7C3AED" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                            {editBmForm.categorical && <svg width="11" height="8" viewBox="0 0 11 8" fill="none"><path d="M1 4L4 7L10 1" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </div>
                        </div>
                      </div>

                      {/* Ranges — gender-aware (hidden when categorical); Male blocks first, Female blocks after */}
                      {!editBmForm.categorical && (() => {
                        const isBoth = editBmForm.gender_marker === "Both";
                        const editRangeGroups = [
                          { label: "Cutoff Range",
                            primary:   [{ key: "reference_range_min", opKey: "reference_range_min_op", sub: "Low Cutoff" }, { key: "reference_range_max", opKey: "reference_range_max_op", sub: "High Cutoff" }],
                            secondary: [{ key: "reference_range_min_female", opKey: "reference_range_min_op_female", sub: "Low Cutoff" }, { key: "reference_range_max_female", opKey: "reference_range_max_op_female", sub: "High Cutoff" }],
                          },
                          { label: "Optimal Range",
                            primary:   [{ key: "optimal_range_min", opKey: "optimal_range_min_op", sub: "Optimal-Low" }, { key: "optimal_range_max", opKey: "optimal_range_max_op", sub: "Optimal-High" }],
                            secondary: [{ key: "optimal_range_min_female", opKey: "optimal_range_min_op_female", sub: "Optimal-Low" }, { key: "optimal_range_max_female", opKey: "optimal_range_max_op_female", sub: "Optimal-High" }],
                          },
                        ];
                        const renderEditBlock = (fields, label, genderTag) => (
                          <div key={`edit-${label}-${genderTag || "single"}`} style={{ background: "rgba(90,144,168,0.03)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                              <p style={{ ...labelTxt, margin: 0, fontWeight: 600, fontSize: 11 }}>{label}</p>
                              {genderTag && <span style={{ fontSize: 10, padding: "2px 9px", borderRadius: 999, fontFamily: "Inter, sans-serif", fontWeight: 700, background: genderTag === "Male" ? "rgba(37,99,235,0.08)" : "rgba(236,72,153,0.08)", color: genderTag === "Male" ? "#1D4ED8" : "#BE185D", border: `1px solid ${genderTag === "Male" ? "rgba(37,99,235,0.2)" : "rgba(236,72,153,0.2)"}` }}>{genderTag}</span>}
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                              {fields.map(({ key, opKey, sub }) => {
                                const op = editBmForm[opKey] || "none"; const isActive = op !== "none";
                                return (
                                  <div key={key}>
                                    <p style={{ ...labelTxt, margin: "0 0 4px", fontSize: 11 }}>{sub}</p>
                                    <div style={{ display: "flex" }}>
                                      <select value={op} onChange={(e) => setEditBmForm((p) => ({ ...p, [opKey]: e.target.value }))}
                                        style={{ ...inputBase, flexShrink: 0, width: 120, borderRight: "none", borderRadius: "8px 0 0 8px", cursor: "pointer", fontWeight: 600, fontSize: 11, color: isActive ? "#1D4ED8" : "#9BBCCC" }}>
                                        <option value="none">None</option>
                                        <option value="lt">{"< Less than"}</option>
                                        <option value="gt">{"> Greater than"}</option>
                                      </select>
                                      <input type="number" step="any" value={editBmForm[key] ?? ""} onChange={(e) => setEditBmForm((p) => ({ ...p, [key]: e.target.value }))}
                                        placeholder="Value" style={{ ...inputBase, flex: 1, borderRadius: "0 8px 8px 0", minWidth: 0, fontSize: 12 }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                        return (
                          <>
                            {editRangeGroups.map(({ label, primary }) => renderEditBlock(primary, label, isBoth ? "Male" : null))}
                            {isBoth && editRangeGroups.map(({ label, secondary }) => renderEditBlock(secondary, label, "Female"))}
                          </>
                        );
                      })()}

                      {/* Plausibility Range — hidden when categorical */}
                      {!editBmForm.categorical && (
                        <div style={{ background: "rgba(90,144,168,0.03)", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                          <p style={{ ...labelTxt, margin: "0 0 8px", fontWeight: 600, fontSize: 11 }}>Plausibility Range</p>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            <div>
                              <p style={{ ...labelTxt, margin: "0 0 4px", fontSize: 11 }}>Min</p>
                              <input type="number" step="any" value={editBmForm.plausibility_min ?? ""} onChange={(e) => setEditBmForm((p) => ({ ...p, plausibility_min: e.target.value }))} placeholder="e.g. 0" style={{ ...inputBase, width: "100%", fontSize: 12 }} />
                            </div>
                            <div>
                              <p style={{ ...labelTxt, margin: "0 0 4px", fontSize: 11 }}>Max</p>
                              <input type="number" step="any" value={editBmForm.plausibility_max ?? ""} onChange={(e) => setEditBmForm((p) => ({ ...p, plausibility_max: e.target.value }))} placeholder="e.g. 500" style={{ ...inputBase, width: "100%", fontSize: 12 }} />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Description */}
                      <div style={{ marginBottom: 10 }}>
                        <p style={{ ...labelTxt, margin: "0 0 4px", fontSize: 11 }}>Description</p>
                        <textarea value={editBmForm.description || ""} onChange={(e) => setEditBmForm((p) => ({ ...p, description: e.target.value }))} rows={2} style={{ ...inputBase, display: "block", width: "100%", fontSize: 12, resize: "vertical" }} />
                      </div>

                      {/* Extended metadata edit */}
                      <div style={{ background: "rgba(90,144,168,0.03)", border: "1px solid rgba(90,144,168,0.12)", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                        <p style={{ ...labelTxt, margin: "0 0 8px", fontWeight: 600 }}>Extended Metadata</p>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                          <div>
                            <p style={{ ...labelTxt, margin: "0 0 3px", fontSize: 11 }}>Backend Field</p>
                            <input value={editBmForm.backend_field || ""} onChange={(e) => setEditBmForm((p) => ({ ...p, backend_field: e.target.value }))} style={{ ...inputBase, width: "100%", fontSize: 12, fontFamily: "monospace" }} />
                          </div>
                          <div>
                            <p style={{ ...labelTxt, margin: "0 0 3px", fontSize: 11 }}>Provenance</p>
                            <input value={editBmForm.provenance || ""} onChange={(e) => setEditBmForm((p) => ({ ...p, provenance: e.target.value }))} style={{ ...inputBase, width: "100%", fontSize: 12 }} />
                          </div>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8, alignItems: "start" }}>
                          <div>
                            <p style={{ ...labelTxt, margin: "0 0 3px", fontSize: 11 }}>Data Type</p>
                            <BioTagInput tags={Array.isArray(editBmForm.data_type) ? editBmForm.data_type : []} onChange={(v) => setEditBmForm((p) => ({ ...p, data_type: v }))} placeholder="e.g. Blood" suggestions={["Blood", "Urine", "Saliva", "Stool", "Tissue", "Breath"]} />
                          </div>
                          <div>
                            <p style={{ ...labelTxt, margin: "0 0 3px", fontSize: 11 }}>Score Direction</p>
                            <BioTagInput tags={Array.isArray(editBmForm.score_direction) ? editBmForm.score_direction : []} onChange={(v) => setEditBmForm((p) => ({ ...p, score_direction: v }))} placeholder="e.g. HIGHER_IS_WORSE" suggestions={["HIGHER_IS_WORSE", "LOWER_IS_WORSE", "WITHIN_RANGE", "CATEGORICAL"]} />
                          </div>
                          <div>
                            <p style={{ ...labelTxt, margin: "0 0 3px", fontSize: 11 }}>Scoring Type</p>
                            <BioTagInput tags={Array.isArray(editBmForm.scoring_type) ? editBmForm.scoring_type : []} onChange={(v) => setEditBmForm((p) => ({ ...p, scoring_type: v }))} placeholder="e.g. PIECEWISE-LINEAR" suggestions={["PIECEWISE-LINEAR", "CATEGORICAL-PANEL"]} />
                          </div>
                        </div>
                        <div>
                          <p style={{ ...labelTxt, margin: "0 0 3px", fontSize: 11 }}>Clinical Rationale</p>
                          <textarea value={editBmForm.clinical_rationale || ""} onChange={(e) => setEditBmForm((p) => ({ ...p, clinical_rationale: e.target.value }))} rows={2} style={{ ...inputBase, display: "block", width: "100%", fontSize: 12, resize: "vertical" }} />
                        </div>
                        {editBmForm.categorical && (
                          <div style={{ marginTop: 10 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                              <p style={{ ...labelTxt, margin: 0, fontWeight: 600, fontSize: 11 }}>Categorical Map</p>
                              <span style={{ fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>category label → score value</span>
                            </div>
                            <CategoricalMapEditor rows={Array.isArray(editBmForm.categorical_map) ? editBmForm.categorical_map : []} onChange={(v) => setEditBmForm((p) => ({ ...p, categorical_map: v }))} />
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                        <Btn label={editBmSaving ? "Saving…" : "Save Changes"} disabled={editBmSaving || !editBmForm.biomarker_name} onClick={(e) => { e.stopPropagation(); saveEditBiomarker(); }} />
                        <Btn label="Cancel" color="#7AAABB" bg="rgba(90,144,168,0.06)" border="rgba(90,144,168,0.2)" onClick={() => { setEditBmId(null); setEditBmForm({}); }} />
                      </div>
                    </div>
                  )}

                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   5b. BIOMARKER ALIASES SECTION — full CRUD (add / edit / delete)
═══════════════════════════════════════════════════════════════════════════ */
const ALIAS_CATEGORY_COLORS = {
  lab:    { label: "Lab",    color: "#1D4ED8", bg: "rgba(29,78,216,0.1)",  border: "rgba(29,78,216,0.25)" },
  vitals: { label: "Vitals", color: "#16A34A", bg: "rgba(22,163,74,0.1)", border: "rgba(22,163,74,0.25)" },
};

function AliasFormModal({ open, mode, form, setForm, biomarkers, biomarkerName, err, saving, onSave, onCancel }) {
  if (!open) return null;
  const isEdit = mode === "edit";
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onCancel}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 460, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 12px 48px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column", gap: 16, boxSizing: "border-box" }}
        onClick={(e) => e.stopPropagation()}>
        <div>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>
            {isEdit ? "Edit Alias" : "Add Alias"}
          </p>
          <p style={{ margin: "3px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
            {isEdit ? `For ${biomarkerName || "this biomarker"}` : "Add a known synonym / alternate lab name for a biomarker"}
          </p>
        </div>

        <ErrBanner msg={err} onDismiss={null} />

        {!isEdit && (
          <div>
            <p style={{ ...labelTxt, margin: "0 0 4px" }}>Biomarker *</p>
            <select value={form.biomarker_id} onChange={(e) => setForm((p) => ({ ...p, biomarker_id: e.target.value }))}
              style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
              <option value="">Select a biomarker…</option>
              {biomarkers.map((b) => (
                <option key={b.id} value={b.id}>{b.biomarker_name} ({b.backend_field || b.biomarker_code})</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <p style={{ ...labelTxt, margin: "0 0 4px" }}>Alias Name *</p>
          <input value={form.alias_name} onChange={(e) => setForm((p) => ({ ...p, alias_name: e.target.value }))} placeholder="e.g. hsCRP" style={{ ...inputBase, width: "100%" }} />
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <Btn label="Cancel" color="#9BBCCC" bg="rgba(90,144,168,0.06)" border="rgba(90,144,168,0.2)" onClick={onCancel} disabled={saving} />
          <Btn label={saving ? "Saving…" : (isEdit ? "Save Changes" : "Add Alias")} onClick={onSave} disabled={saving} />
        </div>
      </div>
    </div>
  );
}

function BiomarkerAliasesSection() {
  const [aliases, setAliases]       = useState([]);
  const [biomarkers, setBiomarkers] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [err, setErr]               = useState("");

  const [formState, setFormState]   = useState(null); // null | { mode: "add" } | { mode: "edit", alias }
  const [form, setForm]             = useState({});
  const [formErr, setFormErr]       = useState("");
  const [saving, setSaving]         = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  const safeErrMsg = (e, fallback) => {
    if (!e?.response) return `${fallback} (no server response — check console)`;
    const d = e.response.data?.detail;
    if (!d) return `${fallback} (HTTP ${e.response.status} — check console)`;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((v) => v?.msg || String(v)).join("; ");
    return `${fallback} (HTTP ${e.response.status})`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      const qs = params.toString();
      const { data } = await api.get(`/admin/biomarker-aliases${qs ? `?${qs}` : ""}`);
      setAliases(Array.isArray(data) ? data : []);
      setErr("");
    } catch {
      setErr("Failed to load biomarker aliases.");
    }
    setLoading(false);
  }, [search]);

  // Debounce search input so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/admin/biomarkers").then(({ data }) => setBiomarkers(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const biomarkerCount = new Set(aliases.map((a) => a.biomarker_id)).size;

  const openAdd = () => {
    setForm({ biomarker_id: "", alias_name: "" });
    setFormErr("");
    setFormState({ mode: "add" });
  };
  const openEdit = (a) => {
    setForm({ biomarker_id: a.biomarker_id, alias_name: a.alias_name });
    setFormErr("");
    setFormState({ mode: "edit", alias: a });
  };
  const closeForm = () => { if (!saving) { setFormState(null); setForm({}); setFormErr(""); } };

  const submitForm = async () => {
    if (formState.mode === "add" && !form.biomarker_id) { setFormErr("Select a biomarker."); return; }
    if (!form.alias_name || !form.alias_name.trim()) { setFormErr("Alias name is required."); return; }

    setSaving(true); setFormErr("");
    try {
      if (formState.mode === "add") {
        await api.post("/admin/biomarker-aliases", { biomarker_id: form.biomarker_id, alias_name: form.alias_name.trim() });
      } else {
        await api.patch(`/admin/biomarker-aliases/${formState.alias.id}`, { alias_name: form.alias_name.trim() });
      }
      setFormState(null); setForm({});
      load();
    } catch (e) {
      setFormErr(safeErrMsg(e, "Save failed."));
    }
    setSaving(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/biomarker-aliases/${deleteTarget.id}`);
      setAliases((prev) => prev.filter((a) => a.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      setErr(safeErrMsg(e, "Delete failed."));
      setDeleteTarget(null);
    }
    setDeleting(false);
  };

  return (
    <div style={{ padding: "32px 28px 60px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: "0 0 2px", fontSize: 20, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Biomarker Aliases</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
            {loading ? "Loading…" : `${aliases.length} alias${aliases.length === 1 ? "" : "es"} across ${biomarkerCount} biomarker${biomarkerCount === 1 ? "" : "s"}`}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            placeholder="Search alias, biomarker, or field…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...inputBase, width: "min(280px, 100%)" }}
          />
          <Btn label="+ Add Alias" onClick={openAdd} />
        </div>
      </div>

      <ErrBanner msg={err} onDismiss={() => setErr("")} />

      {loading ? <Spinner /> : (
        <div className="admin-table-wrap" style={{ ...card, overflow: "hidden" }}>
          <table className="aliases-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "rgba(255,255,255,0.03)" }}>
              <tr>
                <Th>Biomarker Name</Th>
                <Th>Backend Field</Th>
                <Th>Category</Th>
                <Th>Alias Name</Th>
                <Th>Added</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {aliases.map((a, idx) => {
                const isNewGroup = idx === 0 || aliases[idx - 1].biomarker_id !== a.biomarker_id;
                return (
                  <TableRow key={a.id}>
                    <Td style={{ fontWeight: isNewGroup ? 600 : 400, color: isNewGroup ? "#1C3545" : "#B0CDD8", borderTop: isNewGroup && idx > 0 ? "1px solid rgba(90,144,168,0.18)" : undefined }}>
                      {isNewGroup ? a.biomarker_name : ""}
                    </Td>
                    <Td style={{ color: isNewGroup ? "#7AAABB" : "#D3E3EA", fontFamily: "monospace", fontSize: 12, borderTop: isNewGroup && idx > 0 ? "1px solid rgba(90,144,168,0.18)" : undefined }}>
                      {isNewGroup ? a.backend_field : ""}
                    </Td>
                    <Td style={{ borderTop: isNewGroup && idx > 0 ? "1px solid rgba(90,144,168,0.18)" : undefined }}>
                      {isNewGroup && <Badge map={ALIAS_CATEGORY_COLORS} value={a.category} />}
                    </Td>
                    <Td>{a.alias_name}</Td>
                    <Td style={{ color: "#9BBCCC", fontSize: 12 }}>
                      {a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}
                    </Td>
                    <Td>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <Btn small label="Edit" onClick={() => openEdit(a)} />
                        <Btn small label="Delete" color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)" onClick={() => setDeleteTarget(a)} />
                      </div>
                    </Td>
                  </TableRow>
                );
              })}
              {aliases.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: "center", color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13 }}>No aliases found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <AliasFormModal
        open={!!formState}
        mode={formState?.mode}
        form={form}
        setForm={setForm}
        biomarkers={biomarkers}
        biomarkerName={formState?.alias?.biomarker_name}
        err={formErr}
        saving={saving}
        onSave={submitForm}
        onCancel={closeForm}
      />

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete Alias"
        subtitle={deleteTarget ? `Remove "${deleteTarget.alias_name}" from ${deleteTarget.biomarker_name}?` : ""}
        body="This alias will no longer be recognized when normalizing incoming lab-report field names for this biomarker."
        confirmLabel={deleting ? "Deleting…" : "Delete"}
        confirmColor="#DC2626"
        confirmBg="rgba(220,38,38,0.08)"
        confirmBorder="rgba(220,38,38,0.25)"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   5c. BIOMARKER ALTERNATE UNITS SECTION — full CRUD (add / edit / delete)
═══════════════════════════════════════════════════════════════════════════ */
function AltUnitFormModal({ open, mode, form, setForm, biomarkers, biomarkerName, err, saving, onSave, onCancel }) {
  if (!open) return null;
  const isEdit = mode === "edit";
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onCancel}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 560, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 12px 48px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column", gap: 16, boxSizing: "border-box" }}
        onClick={(e) => e.stopPropagation()}>
        <div>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>
            {isEdit ? "Edit Alternate Unit" : "Add Alternate Unit"}
          </p>
          <p style={{ margin: "3px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
            {isEdit ? `For ${biomarkerName || "this biomarker"}` : "Define an incoming unit and its multiply factor into the biomarker's canonical unit"}
          </p>
        </div>

        <ErrBanner msg={err} onDismiss={null} />

        {!isEdit && (
          <div>
            <p style={{ ...labelTxt, margin: "0 0 4px" }}>Biomarker *</p>
            <select value={form.biomarker_id} onChange={(e) => setForm((p) => ({ ...p, biomarker_id: e.target.value }))}
              style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
              <option value="">Select a biomarker…</option>
              {biomarkers.map((b) => (
                <option key={b.id} value={b.id}>{b.biomarker_name} ({b.backend_field || b.biomarker_code})</option>
              ))}
            </select>
            {form.biomarker_id && (
              <p style={{ margin: "6px 0 0", fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>
                Canonical unit: <strong>{biomarkers.find((b) => b.id === form.biomarker_id)?.unit || "—"}</strong>
              </p>
            )}
          </div>
        )}

        <div className="alt-unit-form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div>
            <p style={{ ...labelTxt, margin: "0 0 4px" }}>Alt Order</p>
            <select value={form.alt_order} onChange={(e) => setForm((p) => ({ ...p, alt_order: e.target.value }))} style={{ ...inputBase, width: "100%", cursor: "pointer" }}>
              <option value={1}>1</option>
              <option value={2}>2</option>
            </select>
          </div>
          <div>
            <p style={{ ...labelTxt, margin: "0 0 4px" }}>Incoming Unit *</p>
            <input value={form.unit} onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value }))} placeholder="e.g. mg/dL" style={{ ...inputBase, width: "100%" }} />
          </div>
          <div>
            <p style={{ ...labelTxt, margin: "0 0 4px" }}>× Factor *</p>
            <input type="number" step="any" min="0" value={form.factor} onChange={(e) => setForm((p) => ({ ...p, factor: e.target.value }))} placeholder="e.g. 0.4006" style={{ ...inputBase, width: "100%" }} />
          </div>
        </div>

        <div>
          <p style={{ ...labelTxt, margin: "0 0 4px" }}>Conversion Formula</p>
          <input value={form.conversion_formula} onChange={(e) => setForm((p) => ({ ...p, conversion_formula: e.target.value }))} placeholder="e.g. nmol/L × 0.4006 = ng/mL" style={{ ...inputBase, width: "100%" }} />
        </div>
        <div>
          <p style={{ ...labelTxt, margin: "0 0 4px" }}>Worked Example</p>
          <input value={form.worked_example} onChange={(e) => setForm((p) => ({ ...p, worked_example: e.target.value }))} placeholder="e.g. 75 nmol/L → 30.0 ng/mL" style={{ ...inputBase, width: "100%" }} />
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <Btn label="Cancel" color="#9BBCCC" bg="rgba(90,144,168,0.06)" border="rgba(90,144,168,0.2)" onClick={onCancel} disabled={saving} />
          <Btn label={saving ? "Saving…" : (isEdit ? "Save Changes" : "Add Unit")} onClick={onSave} disabled={saving} />
        </div>
      </div>
    </div>
  );
}

function BiomarkerAlternateUnitsSection() {
  const [units, setUnits]           = useState([]);
  const [biomarkers, setBiomarkers] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [err, setErr]               = useState("");

  const [formState, setFormState]   = useState(null); // null | { mode: "add" } | { mode: "edit", unit }
  const [form, setForm]             = useState({});
  const [formErr, setFormErr]       = useState("");
  const [saving, setSaving]         = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  const safeErrMsg = (e, fallback) => {
    if (!e?.response) return `${fallback} (no server response — check console)`;
    const d = e.response.data?.detail;
    if (!d) return `${fallback} (HTTP ${e.response.status} — check console)`;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((v) => v?.msg || String(v)).join("; ");
    return `${fallback} (HTTP ${e.response.status})`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      const qs = params.toString();
      const { data } = await api.get(`/admin/biomarker-alternate-units${qs ? `?${qs}` : ""}`);
      setUnits(Array.isArray(data) ? data : []);
      setErr("");
    } catch {
      setErr("Failed to load alternate units.");
    }
    setLoading(false);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/admin/biomarkers").then(({ data }) => setBiomarkers(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const biomarkerCount = new Set(units.map((u) => u.biomarker_id)).size;

  const openAdd = () => {
    setForm({ biomarker_id: "", alt_order: 1, unit: "", factor: "", conversion_formula: "", worked_example: "" });
    setFormErr("");
    setFormState({ mode: "add" });
  };
  const openEdit = (u) => {
    setForm({ biomarker_id: u.biomarker_id, alt_order: u.alt_order, unit: u.unit, factor: String(u.factor), conversion_formula: u.conversion_formula || "", worked_example: u.worked_example || "" });
    setFormErr("");
    setFormState({ mode: "edit", unit: u });
  };
  const closeForm = () => { if (!saving) { setFormState(null); setForm({}); setFormErr(""); } };

  const submitForm = async () => {
    if (formState.mode === "add" && !form.biomarker_id) { setFormErr("Select a biomarker."); return; }
    if (!form.unit || !form.unit.trim()) { setFormErr("Incoming unit is required."); return; }
    const factorNum = Number(form.factor);
    if (form.factor === "" || Number.isNaN(factorNum) || factorNum <= 0) { setFormErr("Factor must be a positive number."); return; }

    setSaving(true); setFormErr("");
    const payload = {
      alt_order: Number(form.alt_order) || 1,
      unit: form.unit.trim(),
      factor: factorNum,
      conversion_formula: form.conversion_formula?.trim() || null,
      worked_example: form.worked_example?.trim() || null,
    };
    try {
      if (formState.mode === "add") {
        await api.post("/admin/biomarker-alternate-units", { ...payload, biomarker_id: form.biomarker_id });
      } else {
        await api.patch(`/admin/biomarker-alternate-units/${formState.unit.id}`, payload);
      }
      setFormState(null); setForm({});
      load();
    } catch (e) {
      setFormErr(safeErrMsg(e, "Save failed."));
    }
    setSaving(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/biomarker-alternate-units/${deleteTarget.id}`);
      setUnits((prev) => prev.filter((u) => u.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      setErr(safeErrMsg(e, "Delete failed."));
      setDeleteTarget(null);
    }
    setDeleting(false);
  };

  return (
    <div style={{ padding: "32px 28px 60px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: "0 0 2px", fontSize: 20, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Biomarker Alternate Units</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
            {loading ? "Loading…" : `${units.length} alternate unit${units.length === 1 ? "" : "s"} across ${biomarkerCount} biomarker${biomarkerCount === 1 ? "" : "s"}`}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            placeholder="Search unit, biomarker, or field…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...inputBase, width: "min(280px, 100%)" }}
          />
          <Btn label="+ Add Alternate Unit" onClick={openAdd} />
        </div>
      </div>

      <ErrBanner msg={err} onDismiss={() => setErr("")} />

      {loading ? <Spinner /> : (
        <div className="admin-table-wrap" style={{ ...card, overflow: "hidden" }}>
          <table className="alt-units-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "rgba(255,255,255,0.03)" }}>
              <tr>
                <Th>Biomarker Name</Th>
                <Th>Backend Field</Th>
                <Th>Canonical Unit</Th>
                <Th w={50}>#</Th>
                <Th>Incoming Unit</Th>
                <Th>× Factor</Th>
                <Th>Conversion Formula</Th>
                <Th>Worked Example</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {units.map((u, idx) => {
                const isNewGroup = idx === 0 || units[idx - 1].biomarker_id !== u.biomarker_id;
                return (
                  <TableRow key={u.id}>
                    <Td style={{ fontWeight: isNewGroup ? 600 : 400, color: isNewGroup ? "#1C3545" : "#B0CDD8", borderTop: isNewGroup && idx > 0 ? "1px solid rgba(90,144,168,0.18)" : undefined }}>
                      {isNewGroup ? u.biomarker_name : ""}
                    </Td>
                    <Td style={{ color: isNewGroup ? "#7AAABB" : "#D3E3EA", fontFamily: "monospace", fontSize: 12, borderTop: isNewGroup && idx > 0 ? "1px solid rgba(90,144,168,0.18)" : undefined }}>
                      {isNewGroup ? u.backend_field : ""}
                    </Td>
                    <Td style={{ color: isNewGroup ? "#7AAABB" : "#D3E3EA", borderTop: isNewGroup && idx > 0 ? "1px solid rgba(90,144,168,0.18)" : undefined }}>
                      {isNewGroup ? u.canonical_unit : ""}
                    </Td>
                    <Td style={{ color: "#9BBCCC" }}>{u.alt_order}</Td>
                    <Td style={{ fontWeight: 600 }}>{u.unit}</Td>
                    <Td>{u.factor}</Td>
                    <Td style={{ color: "#7AAABB", fontSize: 12 }}>{u.conversion_formula || "—"}</Td>
                    <Td style={{ color: "#9BBCCC", fontSize: 12 }}>{u.worked_example || "—"}</Td>
                    <Td>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <Btn small label="Edit" onClick={() => openEdit(u)} />
                        <Btn small label="Delete" color="#DC2626" bg="rgba(220,38,38,0.08)" border="rgba(220,38,38,0.25)" onClick={() => setDeleteTarget(u)} />
                      </div>
                    </Td>
                  </TableRow>
                );
              })}
              {units.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 32, textAlign: "center", color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13 }}>No alternate units found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <AltUnitFormModal
        open={!!formState}
        mode={formState?.mode}
        form={form}
        setForm={setForm}
        biomarkers={biomarkers}
        biomarkerName={formState?.unit?.biomarker_name}
        err={formErr}
        saving={saving}
        onSave={submitForm}
        onCancel={closeForm}
      />

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete Alternate Unit"
        subtitle={deleteTarget ? `Remove "${deleteTarget.unit}" from ${deleteTarget.biomarker_name}?` : ""}
        body="This alternate unit will no longer be recognized when normalizing incoming lab values for this biomarker."
        confirmLabel={deleting ? "Deleting…" : "Delete"}
        confirmColor="#DC2626"
        confirmBg="rgba(220,38,38,0.08)"
        confirmBorder="rgba(220,38,38,0.25)"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   6. SETTINGS SECTION
═══════════════════════════════════════════════════════════════════════════ */
const CATEGORY_LABELS = { features: "Features", visibility: "Visibility", limits: "Limits", platform: "Platform", notifications: "Notifications" };
const CATEGORY_ICONS  = { features: "⚡", visibility: "👁", limits: "🔢", platform: "🌐", notifications: "🔔" };

const VIS_OPTIONS = ["L1", "L2", "hidden"];
const VIS_COLORS  = { L1: "#22c55e", L2: "#f59e0b", hidden: "#94a3b8" };
const VIS_LABELS  = { L1: "L1 — Always visible", L2: "L2 — Show on expand", hidden: "— Hidden" };

function ClusterFieldVisibilityEditor() {
  const [fields, setFields]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty]     = useState({});   // key: `${field_name}.${role}`, val: new vis
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState("");
  const [ok, setOk]           = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings/cluster-fields");
      setFields(Array.isArray(data) ? data : []);
    } catch { setErr("Failed to load cluster field settings."); }
    setLoading(false);
  };

  const seed = async () => {
    try {
      const { data } = await api.post("/admin/settings/cluster-fields/seed");
      setFields(Array.isArray(data.fields) ? data.fields : []);
      setOk(`Seeded ${data.inserted} default settings.`);
    } catch { setErr("Seed failed."); }
  };

  useEffect(() => { load(); }, []);

  const getVis = (f, role) => {
    const k = `${f.field_name}.${role}`;
    return dirty[k] !== undefined ? dirty[k] : (f[role] || "hidden");
  };

  const markDirty = (fieldName, role, val) => {
    setDirty((p) => ({ ...p, [`${fieldName}.${role}`]: val }));
  };

  const saveAll = async () => {
    if (Object.keys(dirty).length === 0) return;
    setSaving(true);
    setErr(""); setOk("");
    try {
      const settings = Object.entries(dirty).map(([k, visibility]) => {
        const [field_name, role] = k.split(".");
        return { field_name, role, visibility };
      });
      const { data } = await api.put("/admin/settings/cluster-fields", { settings });
      setFields(Array.isArray(data) ? data : []);
      setDirty({});
      setOk("Cluster field visibility saved.");
    } catch (e) { setErr(e.response?.data?.detail || "Save failed."); }
    setSaving(false);
  };

  const isDirtyAny = Object.keys(dirty).length > 0;

  const VisSelect = ({ fieldName, role, currentVis }) => {
    const v = dirty[`${fieldName}.${role}`] !== undefined ? dirty[`${fieldName}.${role}`] : currentVis;
    return (
      <select value={v} onChange={(e) => markDirty(fieldName, role, e.target.value)}
        style={{ border: `2px solid ${VIS_COLORS[v] || "#CBD5E1"}`, borderRadius: 8, padding: "4px 8px", fontSize: 12, fontWeight: 600, color: VIS_COLORS[v] || "#64748B", background: `${VIS_COLORS[v] || "#CBD5E1"}12`, fontFamily: "Inter, sans-serif", cursor: "pointer", outline: "none", minWidth: 130 }}>
        {VIS_OPTIONS.map((opt) => <option key={opt} value={opt}>{VIS_LABELS[opt]}</option>)}
      </select>
    );
  };

  if (loading) return <Spinner label="Loading cluster field settings…" />;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <p style={{ margin: "0 0 2px", fontSize: 15, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Cluster Field Visibility</p>
          <p style={{ margin: 0, fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
            L1 = always visible &nbsp;·&nbsp; L2 = behind "Show More" &nbsp;·&nbsp; — = hidden
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {fields.length === 0 && <Btn label="Seed Defaults" onClick={seed} />}
          {isDirtyAny && (
            <Btn label={saving ? "Saving…" : "Save Changes"} disabled={saving} onClick={saveAll} />
          )}
        </div>
      </div>
      {err && <ErrBanner msg={err} onDismiss={() => setErr("")} />}
      {ok  && <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", color: "#166534", fontSize: 13, fontFamily: "Inter, sans-serif" }}>{ok}</div>}

      {/* Desktop table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 13, fontFamily: "Inter, sans-serif" }}>
          <thead>
            <tr style={{ background: "rgba(90,144,168,0.06)" }}>
              <th style={{ textAlign: "left", padding: "10px 16px", fontWeight: 600, color: "#4D7A8E", borderBottom: "2px solid rgba(90,144,168,0.15)", whiteSpace: "nowrap" }}>Field</th>
              {["doctor", "admin", "patient"].map((role) => (
                <th key={role} style={{ textAlign: "center", padding: "10px 16px", fontWeight: 600, color: "#4D7A8E", borderBottom: "2px solid rgba(90,144,168,0.15)", textTransform: "capitalize", whiteSpace: "nowrap" }}>{role}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fields.map((f, idx) => (
              <tr key={f.field_name} style={{ background: idx % 2 === 0 ? "#FFFFFF" : "rgba(90,144,168,0.02)" }}>
                <td style={{ padding: "10px 16px", borderBottom: "1px solid rgba(90,144,168,0.08)" }}>
                  <p style={{ margin: 0, fontWeight: 500, color: "#1C3545" }}>{f.field_label}</p>
                  <p style={{ margin: "1px 0 0", fontSize: 10, color: "#A8C8D8", fontFamily: "monospace" }}>{f.field_name}</p>
                </td>
                {["doctor", "admin", "patient"].map((role) => (
                  <td key={role} style={{ padding: "8px 12px", textAlign: "center", borderBottom: "1px solid rgba(90,144,168,0.08)" }}>
                    <VisSelect fieldName={f.field_name} role={role} currentVis={f[role] || "hidden"} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card view */}
      <style>{`
        @media (max-width: 640px) {
          .cfv-table { display: none !important; }
          .cfv-cards { display: flex !important; }
        }
        @media (min-width: 641px) {
          .cfv-cards { display: none !important; }
        }
      `}</style>
    </div>
  );
}

function BiomarkerFieldVisibilityEditor() {
  const [fields, setFields]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty]     = useState({});
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState("");
  const [ok, setOk]           = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings/biomarker-fields");
      setFields(Array.isArray(data) ? data : []);
    } catch { setErr("Failed to load biomarker field settings."); }
    setLoading(false);
  };

  const seed = async () => {
    try {
      const { data } = await api.post("/admin/settings/biomarker-fields/seed");
      setFields(Array.isArray(data.fields) ? data.fields : []);
      setOk(`Seeded ${data.inserted} default settings.`);
    } catch { setErr("Seed failed."); }
  };

  useEffect(() => { load(); }, []);

  const markDirty = (fieldName, role, val) => {
    setDirty((p) => ({ ...p, [`${fieldName}.${role}`]: val }));
  };

  const saveAll = async () => {
    if (Object.keys(dirty).length === 0) return;
    setSaving(true); setErr(""); setOk("");
    try {
      const settings = Object.entries(dirty).map(([k, visibility]) => {
        const [field_name, role] = k.split(".");
        return { field_name, role, visibility };
      });
      const { data } = await api.put("/admin/settings/biomarker-fields", { settings });
      setFields(Array.isArray(data) ? data : []);
      setDirty({});
      setOk("Biomarker field visibility saved.");
    } catch (e) { setErr(e.response?.data?.detail || "Save failed."); }
    setSaving(false);
  };

  const isDirtyAny = Object.keys(dirty).length > 0;

  const VisSelect = ({ fieldName, role, currentVis }) => {
    const v = dirty[`${fieldName}.${role}`] !== undefined ? dirty[`${fieldName}.${role}`] : currentVis;
    return (
      <select value={v} onChange={(e) => markDirty(fieldName, role, e.target.value)}
        style={{ border: `2px solid ${VIS_COLORS[v] || "#CBD5E1"}`, borderRadius: 8, padding: "4px 8px", fontSize: 12, fontWeight: 600, color: VIS_COLORS[v] || "#64748B", background: `${VIS_COLORS[v] || "#CBD5E1"}12`, fontFamily: "Inter, sans-serif", cursor: "pointer", outline: "none", minWidth: 130 }}>
        {VIS_OPTIONS.map((opt) => <option key={opt} value={opt}>{VIS_LABELS[opt]}</option>)}
      </select>
    );
  };

  if (loading) return <Spinner label="Loading biomarker field settings…" />;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <p style={{ margin: "0 0 2px", fontSize: 15, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Biomarker Field Visibility</p>
          <p style={{ margin: 0, fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
            L1 = always visible &nbsp;·&nbsp; L2 = behind "Show More" &nbsp;·&nbsp; — = hidden
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {fields.length === 0 && <Btn label="Seed Defaults" onClick={seed} />}
          {isDirtyAny && (
            <Btn label={saving ? "Saving…" : "Save Changes"} disabled={saving} onClick={saveAll} />
          )}
        </div>
      </div>
      {err && <ErrBanner msg={err} onDismiss={() => setErr("")} />}
      {ok  && <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", color: "#166534", fontSize: 13, fontFamily: "Inter, sans-serif" }}>{ok}</div>}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 13, fontFamily: "Inter, sans-serif" }}>
          <thead>
            <tr style={{ background: "rgba(90,144,168,0.06)" }}>
              <th style={{ textAlign: "left", padding: "10px 16px", fontWeight: 600, color: "#4D7A8E", borderBottom: "2px solid rgba(90,144,168,0.15)", whiteSpace: "nowrap" }}>Field</th>
              {["doctor", "admin", "patient"].map((role) => (
                <th key={role} style={{ textAlign: "center", padding: "10px 16px", fontWeight: 600, color: "#4D7A8E", borderBottom: "2px solid rgba(90,144,168,0.15)", textTransform: "capitalize", whiteSpace: "nowrap" }}>{role}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fields.map((f, idx) => (
              <tr key={f.field_name} style={{ background: idx % 2 === 0 ? "#FFFFFF" : "rgba(90,144,168,0.02)" }}>
                <td style={{ padding: "10px 16px", borderBottom: "1px solid rgba(90,144,168,0.08)" }}>
                  <p style={{ margin: 0, fontWeight: 500, color: "#1C3545" }}>{f.field_label}</p>
                  <p style={{ margin: "1px 0 0", fontSize: 10, color: "#A8C8D8", fontFamily: "monospace" }}>{f.field_name}</p>
                </td>
                {["doctor", "admin", "patient"].map((role) => (
                  <td key={role} style={{ padding: "8px 12px", textAlign: "center", borderBottom: "1px solid rgba(90,144,168,0.08)" }}>
                    <VisSelect fieldName={f.field_name} role={role} currentVis={f[role] || "hidden"} />
                  </td>
                ))}
              </tr>
            ))}
            {fields.length === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: "32px", textAlign: "center", color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontSize: 13 }}>
                  No settings yet — click "Seed Defaults" to populate
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlatformSettingsEditor() {
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [edited, setEdited] = useState({});
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings");
      setGrouped(data);
    } catch { setErr("Failed to load settings."); }
    setLoading(false);
  };

  const seed = async (silent = false) => {
    try {
      const { data } = await api.post("/admin/settings/seed");
      await load();
      if (!silent && data.inserted > 0) setErr(`Synced ${data.inserted} new default settings.`);
    } catch { if (!silent) setErr("Sync failed."); }
  };

  useEffect(() => { seed(true); }, []);

  const save = async (key, value) => {
    setSaving((p) => ({ ...p, [key]: true }));
    try {
      await api.put(`/admin/settings/${key}`, { value: String(value) });
      setGrouped((prev) => {
        const next = { ...prev };
        for (const cat of Object.keys(next)) {
          next[cat] = next[cat].map((s) => s.key === key ? { ...s, value: String(value) } : s);
        }
        return next;
      });
      setEdited((p) => { const n = { ...p }; delete n[key]; return n; });
    } catch (e) { setErr(e.response?.data?.detail || "Save failed."); }
    setSaving((p) => ({ ...p, [key]: false }));
  };

  const getVal = (s) => edited[s.key] !== undefined ? edited[s.key] : s.value;

  if (loading) return <Spinner />;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <Btn small label="Sync Default Settings" onClick={() => seed(false)} />
      </div>
      <ErrBanner msg={err} onDismiss={() => setErr("")} />
      {Object.entries(grouped).map(([cat, settings]) => (
        <div key={cat} style={{ marginBottom: 28 }}>
          <p style={{ ...sectionHeading, display: "flex", alignItems: "center", gap: 8 }}>
            <span>{CATEGORY_ICONS[cat]}</span>{CATEGORY_LABELS[cat] || cat}
          </p>
          <div style={{ ...card, overflow: "hidden" }}>
            {settings.map((s, idx) => {
              const val = getVal(s);
              const isDirty = edited[s.key] !== undefined;
              return (
                <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 20px", borderBottom: idx < settings.length - 1 ? "1px solid rgba(90,144,168,0.1)" : "none", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <p style={{ margin: "0 0 3px", fontSize: 14, fontWeight: 500, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{s.label}</p>
                    {s.description && <p style={{ margin: 0, fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{s.description}</p>}
                    <p style={{ margin: "3px 0 0", fontSize: 10, color: "#A8C8D8", fontFamily: "monospace" }}>{s.key}</p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                    {s.setting_type === "boolean" ? (
                      <button
                        disabled={!s.is_editable}
                        onClick={() => {
                          const newVal = val === "true" ? "false" : "true";
                          setEdited((p) => ({ ...p, [s.key]: newVal }));
                          save(s.key, newVal);
                        }}
                        style={{ position: "relative", width: 44, height: 24, borderRadius: 999, border: "none", background: val === "true" ? "#5A90A8" : "rgba(90,144,168,0.15)", cursor: s.is_editable ? "pointer" : "not-allowed", transition: "background 200ms", flexShrink: 0 }}
                      >
                        <span style={{ position: "absolute", top: 3, left: val === "true" ? 22 : 3, width: 18, height: 18, borderRadius: "50%", background: "#FFFFFF", transition: "left 200ms" }} />
                      </button>
                    ) : s.setting_type === "integer" ? (
                      <input type="number" value={val} disabled={!s.is_editable}
                        onChange={(e) => setEdited((p) => ({ ...p, [s.key]: e.target.value }))}
                        style={{ ...inputBase, width: 90, textAlign: "center" }} />
                    ) : (
                      <input type="text" value={val} disabled={!s.is_editable}
                        onChange={(e) => setEdited((p) => ({ ...p, [s.key]: e.target.value }))}
                        style={{ ...inputBase, width: 240 }} />
                    )}
                    {isDirty && s.setting_type !== "boolean" && (
                      <Btn small label={saving[s.key] ? "…" : "Save"} disabled={!!saving[s.key]} onClick={() => save(s.key, val)} />
                    )}
                    {!s.is_editable && <span style={{ fontSize: 11, color: "#A8C8D8", fontFamily: "Inter, sans-serif" }}>read-only</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const SETTINGS_TABS = [
  { key: "platform",          label: "Platform Settings",          icon: "⚙" },
  { key: "cluster-fields",    label: "Cluster Field Visibility",   icon: "🧬" },
  { key: "biomarker-fields",  label: "Biomarker Field Visibility", icon: "🔬" },
];

function SettingsSection() {
  const [tab, setTab] = useState("platform");

  return (
    <div style={{ padding: "32px 28px 60px" }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: "0 0 2px", fontSize: 20, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Settings</h2>
        <p style={{ margin: "0 0 18px", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Control platform features and cluster field display rules</p>
        {/* Tab bar */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {SETTINGS_TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1px solid", fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 150ms",
                borderColor: tab === t.key ? "#5A90A8" : "rgba(90,144,168,0.2)",
                background: tab === t.key ? "#5A90A8" : "transparent",
                color: tab === t.key ? "#FFFFFF" : "#4D7A8E",
              }}>
              <span>{t.icon}</span> {t.label}
            </button>
          ))}
        </div>
      </div>
      {tab === "platform"          && <PlatformSettingsEditor />}
      {tab === "cluster-fields"   && <ClusterFieldVisibilityEditor />}
      {tab === "biomarker-fields" && <BiomarkerFieldVisibilityEditor />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN AdminDashboard
═══════════════════════════════════════════════════════════════════════════ */
const NAV = [
  { key: "overview",      label: "Overview",      icon: "◉" },
  { key: "applications",  label: "Applications",  icon: "📋" },
  { key: "users",         label: "Users",         icon: "👤" },
  { key: "doctors",       label: "Doctors",       icon: "🩺" },
  { key: "clusters",      label: "Clusters",      icon: "🧬" },
  { key: "biomarkers",    label: "Biomarkers",    icon: "🔬" },
  { key: "biomarker_alias", label: "Biomarker Alias", icon: "🏷" },
  { key: "biomarker_alt_units", label: "Biomarker Alternate Units", icon: "🔁" },
  { key: "settings",      label: "Settings",      icon: "⚙" },
];

export default function AdminDashboard({ onLogout }) {
  const { logout } = useAuth();
  const [section, setSection] = useState("overview");
  const [adminUser, setAdminUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clustersReset, setClustersReset] = useState(0);

  // ── Admin notification inbox ──
  const [adminNotifOpen,    setAdminNotifOpen]    = useState(false);
  const [adminNotifs,       setAdminNotifs]       = useState([]);
  const [adminUnread,       setAdminUnread]       = useState(0);
  const [adminNotifsLoaded, setAdminNotifsLoaded] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setAdminUser(data)).catch(() => {});
    // Poll unread count every 45 s
    const pollUnread = () => api.get("/admin/notifications/unread-count").then(({ data }) => setAdminUnread(data.count || 0)).catch(() => {});
    pollUnread();
    const t = setInterval(pollUnread, 45000);
    return () => clearInterval(t);
  }, []);

  const loadAdminNotifs = async () => {
    try {
      const { data } = await api.get("/admin/notifications?limit=50");
      setAdminNotifs(Array.isArray(data) ? data : []);
      setAdminNotifsLoaded(true);
    } catch (_) { setAdminNotifsLoaded(true); }
  };

  const openAdminNotifPanel = () => {
    setAdminNotifOpen((o) => !o);
    if (!adminNotifsLoaded) loadAdminNotifs();
  };

  const markAdminAllRead = async () => {
    try {
      await api.put("/admin/notifications/read-all");
      setAdminNotifs((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setAdminUnread(0);
    } catch (_) {}
  };

  const markAdminOneRead = async (id) => {
    try {
      await api.put(`/admin/notifications/${id}/read`);
      setAdminNotifs((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
      setAdminUnread((c) => Math.max(0, c - 1));
    } catch (_) {}
  };

  const ADMIN_NOTIF_ICONS = {
    doctor_registered:  "🩺",
    document_uploaded:  "📄",
    program_submitted:  "📋",
    question_proposed:  "💬",
    admin_message:      "📨",
  };

  const handleLogout = async () => {
    try { await logout(); } catch { /* silent */ }
    onLogout();
  };

  return (
    <div className="admin-dash" style={{ minHeight: "100vh", background: "#EDF4F7", display: "flex", flexDirection: "column", position: "relative" }} onClick={() => adminNotifOpen && setAdminNotifOpen(false)}>
      <style>{ADMIN_SELECT_STYLE}</style>

      {/* Top header */}
      <header style={{ position: "sticky", top: 0, zIndex: 50, background: "#8AAEBB", borderBottom: "1px solid rgba(90,144,168,0.35)", padding: "0 16px 0 20px", height: 54, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Hamburger button — visible on mobile only */}
          <button
            className="admin-hamburger"
            onClick={() => setSidebarOpen((o) => !o)}
            style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: "4px 6px", color: "#FFFFFF" }}
            aria-label="Toggle menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#FFFFFF", fontFamily: "Inter, sans-serif", letterSpacing: "-0.03em" }}>Supercore</span>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: "rgba(255,255,255,0.2)", color: "#FFFFFF", fontFamily: "Inter, sans-serif", letterSpacing: "0.08em" }}>ADMIN</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative" }}>
          {adminUser && <span className="admin-user-name" style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontFamily: "Inter, sans-serif" }}>{adminUser.name}</span>}

          {/* Notification bell */}
          <button onClick={openAdminNotifPanel} style={{ position: "relative", padding: "5px 8px", borderRadius: 7, border: "1px solid rgba(255,255,255,0.35)", background: adminNotifOpen ? "rgba(255,255,255,0.2)" : "transparent", color: "rgba(255,255,255,0.88)", fontSize: 17, cursor: "pointer", display: "flex", alignItems: "center" }}>
            🔔
            {adminUnread > 0 && (
              <span style={{ position: "absolute", top: -3, right: -3, minWidth: 16, height: 16, borderRadius: 8, background: "#EF4444", color: "#fff", fontSize: 10, fontWeight: 700, fontFamily: "Inter, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px", lineHeight: 1 }}>{adminUnread > 99 ? "99+" : adminUnread}</span>
            )}
          </button>

          {/* Notification dropdown */}
          {adminNotifOpen && (
            <div style={{ position: "absolute", top: 44, right: 0, width: "min(380px, calc(100vw - 24px))", maxHeight: 480, background: "#fff", borderRadius: 14, boxShadow: "0 12px 48px rgba(0,0,0,0.18)", border: "1px solid rgba(90,144,168,0.2)", zIndex: 200, display: "flex", flexDirection: "column", overflow: "hidden" }}
              onClick={(e) => e.stopPropagation()}>
              {/* Panel header */}
              <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid rgba(90,144,168,0.12)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Notifications {adminUnread > 0 && <span style={{ fontSize: 11, color: "#5A90A8", fontWeight: 400 }}>({adminUnread} unread)</span>}</span>
                <div style={{ display: "flex", gap: 8 }}>
                  {adminUnread > 0 && <button onClick={markAdminAllRead} style={{ fontSize: 11, color: "#5A90A8", background: "none", border: "none", cursor: "pointer", fontFamily: "Inter, sans-serif", padding: 0 }}>Mark all read</button>}
                  <button onClick={() => setAdminNotifOpen(false)} style={{ fontSize: 16, color: "#9BBCCC", background: "none", border: "none", cursor: "pointer", lineHeight: 1 }}>×</button>
                </div>
              </div>
              {/* List */}
              <div style={{ overflowY: "auto", flex: 1 }}>
                {!adminNotifsLoaded
                  ? <div style={{ padding: 24, textAlign: "center", color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontSize: 13 }}>Loading…</div>
                  : adminNotifs.length === 0
                    ? <div style={{ padding: 32, textAlign: "center", color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontSize: 13 }}>No notifications yet.</div>
                    : adminNotifs.map((n) => (
                        <div key={n.id} onClick={() => !n.is_read && markAdminOneRead(n.id)}
                          style={{ padding: "12px 16px", borderBottom: "1px solid rgba(90,144,168,0.08)", background: n.is_read ? "transparent" : "rgba(90,144,168,0.04)", cursor: n.is_read ? "default" : "pointer", display: "flex", gap: 10, alignItems: "flex-start" }}>
                          <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{ADMIN_NOTIF_ICONS[n.event_type] || "🔔"}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                              <span style={{ fontSize: 13, fontWeight: n.is_read ? 400 : 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{n.title}</span>
                              {!n.is_read && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#5A90A8", flexShrink: 0 }} />}
                            </div>
                            {n.body && <p style={{ margin: "0 0 3px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.4 }}>{n.body}</p>}
                            <span style={{ fontSize: 10, color: "#B0CDD8", fontFamily: "Inter, sans-serif" }}>{new Date(n.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      ))
                }
              </div>
            </div>
          )}

          <button onClick={handleLogout} style={{ padding: "5px 14px", borderRadius: 7, border: "1px solid rgba(255,255,255,0.45)", background: "transparent", color: "rgba(255,255,255,0.88)", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms" }} onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; e.currentTarget.style.color = "#FFF"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.88)"; }}>Log out</button>
        </div>
      </header>

      {/* Mobile sidebar overlay — click to close */}
      {sidebarOpen && (
        <div className="admin-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Body: sidebar + content */}
      <div style={{ display: "flex", flex: 1, position: "relative", zIndex: 1 }}>

        {/* Sidebar */}
        <nav className={`admin-sidebar${sidebarOpen ? " admin-sidebar--open" : ""}`} style={{ width: 210, flexShrink: 0, background: "#DAEAF2", borderRight: "1px solid rgba(90,144,168,0.2)", padding: "20px 0", display: "flex", flexDirection: "column", gap: 2 }}>
          {NAV.map((n) => {
            const active = section === n.key;
            return (
              <button key={n.key} onClick={() => { if (n.key === "clusters") setClustersReset((k) => k + 1); setSection(n.key); setSidebarOpen(false); }}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 20px", margin: "0 8px", borderRadius: 9, border: "none", background: active ? "rgba(90,144,168,0.2)" : "transparent", color: active ? "#1C3545" : "#4D7A8E", fontSize: 14, fontWeight: active ? 600 : 400, fontFamily: "Inter, sans-serif", cursor: "pointer", textAlign: "left", transition: "all 150ms" }}
                onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = "rgba(90,144,168,0.12)"; e.currentTarget.style.color = "#1C3545"; } }}
                onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#4D7A8E"; } }}
              >
                <span style={{ fontSize: 16 }}>{n.icon}</span>
                {n.label}
              </button>
            );
          })}
        </nav>

        {/* Main content */}
        <main style={{ flex: 1, overflowY: "auto", maxHeight: "calc(100vh - 54px)" }}>
          {section === "overview"      && <OverviewSection onGoToDoctors={() => setSection("doctors")} onGoToUsers={() => setSection("users")} />}
          {section === "applications"  && <PendingApplicationsSection />}
          {section === "users"         && <UsersSection />}
          {section === "doctors"       && <DoctorsSection />}
          {section === "clusters"      && <ClusterErrorBoundary><ClustersSection resetSignal={clustersReset} /></ClusterErrorBoundary>}
          {section === "biomarkers"    && <BiomarkersSection />}
          {section === "biomarker_alias" && <BiomarkerAliasesSection />}
          {section === "biomarker_alt_units" && <BiomarkerAlternateUnitsSection />}
          {section === "settings"      && <SettingsSection />}
        </main>
      </div>
    </div>
  );
}
